
import type { DokumenteArchivEntscheidung } from './components/dokumente/types';

export type AppView = 'dashboard' | 'projects' | 'admin' | 'admin-projects' | 'admin-settings' | 'users' | 'project-management' | 'tasks' | 'global-map' | 'project-hub' | 'module-vorank' | 'module-unterlage' | 'module-sigeko-inspection' | 'module-sige-plan' | 'module-sige-einweisung' | 'module-dokumente' | 'module-planung' | 'module-handbuch' | 'module-koordinierung' | 'module-asplan' | 'module-bauordnung' | 'module-sifa-unterweisung' | 'module-sifa-mitarbeiter' | 'module-sifa-fristen' | 'module-sifa-taetigkeiten' | 'module-sifa-gbu' | 'module-archive' | 'module-sent-reports' | 'editor' | 'settings' | 'sent-reports' | 'crm' | 'crm-map' | 'leads' | 'freelancers' | 'quotes' | 'invoices' | 'erp-cockpit' | 'lernzentrum' | 'sifa-vorlagen' | 'admin-vorlagen' | 'admin-open-positions' | 'admin-substitutions' | 'material' | 'talentpool';

export type ReportLanguage = 'de' | 'en' | 'de/en';

/**
 * Projektstatus.
 *
 * 'completed' = BEENDET: die Arbeit vor Ort ist vorbei, jeder Mitarbeiter darf
 * das setzen. Das Projekt bleibt sichtbar und erscheint in der Buchhaltung
 * als faellig fuer die Schlussrechnung.
 * 'finished'  = ARCHIV: nur Administratoren, und erst wenn nichts mehr offen
 * ist und die Schlussrechnung gestellt UND bezahlt wurde
 * (services/archiveGuardLogic.ts). Der Wert bleibt 'finished', weil Datenbank,
 * Trigger (Nr. 69), Sichten und Waechter ihn so kennen.
 */
export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'finished';

export interface EmailLog {
    id: string;
    project_id: string;
    project_name: string;
    recipients: string;
    subject: string;
    status: 'success' | 'error';
    error_message?: string;
    sent_at: string;
    sender_id: string;
    body_html?: string; // NEU: HTML Inhalt der Mail
    pdf_url?: string;   // NEU: Link zum PDF in der Cloud
    /**
     * Idempotenz-Schlüssel EINES Sendevorgangs (siehe newSendKey). Er wird beim
     * Auslösen erzeugt und über alle Wege mitgeführt, die denselben Vorgang zu
     * Ende bringen können (Direkt-Insert, Pending-Marker-Replay, Wiederhol-Klick
     * auf der Fehlerseite). Ein partieller Unique-Index auf dieser Spalte macht
     * die zweite Zeile physisch unmöglich.
     *
     * Optional, weil der Altbestand keinen Schlüssel hat und Clients ohne diese
     * Version weiterhin ohne Schlüssel schreiben.
     */
    send_key?: string;
}

export interface BugReport {
    id: string;
    created_at: string;
    last_seen_at: string;
    fingerprint: string;
    message: string;
    stack?: string;
    component_stack?: string;
    source: 'boundary' | 'window' | 'rejection';
    app_context?: string;
    user_agent?: string;
    user_id?: string;
    occurrence_count: number;
    status: 'open' | 'resolved';
    ai_solution?: string;
    ai_solution_status: 'pending' | 'done' | 'failed';
    profiles?: { first_name?: string; last_name?: string; email?: string } | null; // joined client-side
}

export interface Authority {
    id: string;
    name: string;
    address: string;
    email?: string;
    phone?: string;
    fax?: string;
    catchment_area?: string;
}

export interface ConstructionType {
    id: string;
    title: string;
    description: string;
}

/** Kategorie mit ihren Unterkategorien — Katalog in services/projectCategories.ts. */
export interface ProjectCategoryDef {
    title: string;
    subs: string[];
}

export interface ServiceTemplate {
    id: string;
    name: string;
    description?: string;
    items: ServiceItem[];
}

export interface UserProfile {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  job_title?: string; // NEU
  company_name?: string;
  company_id?: string | null;
  company_address?: string;
  address?: string; 
  company_logo?: string;
  phone?: string;
  // 'freelancer' ist die einzige EXTERNE Rolle (ERP/CRM-Plan 3.4, Phase D3):
  // ein selbstaendiger SiGeKo mit Login im selben Supabase-Projekt. Sie sieht
  // ausschliesslich das Freelancer-Portal (src/App.tsx) und ist in der
  // Datenbank ueber RESTRICTIVE-Deckel ueberall ausgesperrt, wo
  // supabase/freelancer_portal.sql (MIGRATIONS.md Nr. 20) nicht ausdruecklich
  // oeffnet. Nie in einer Rollenpruefung mit 'user' gleichsetzen.
  role: 'user' | 'admin' | 'super_admin' | 'arbeitsvorbereitung' | 'freelancer';
  app_mode?: 'sigeko' | 'hse';
  is_approved?: boolean;
  /**
   * Wann dieser Nutzer die Willkommens-Tour („Ersteinweisung in die App")
   * beendet oder abgebrochen hat (Migration Nr. 102,
   * `supabase/profiles_ersteinweisung.sql`).
   *
   * Drei Werte, drei Bedeutungen — die Unterscheidung ist wichtig:
   *  · `null`      — der Nutzer hat die Ersteinweisung noch vor sich. Die Tour
   *                  startet beim naechsten Login von selbst.
   *  · Zeitstempel — erledigt. Die Tour startet nie wieder von selbst, auf
   *                  keinem Geraet und in keinem Browser.
   *  · `undefined` — die Spalte gibt es in dieser Datenbank noch nicht
   *                  (Migration Nr. 102 offen). Dann entscheidet wie bisher
   *                  allein der Browser-Merker `lernzentrum.willkommen`.
   */
  ersteinweisung_am?: string | null;
  vacation_plan?: VacationRange[];
  settings?: {
      mapColor?: string;
      alertConfig?: AlertConfig;
      projectNumberConfig?: ProjectNumberConfig;
      moduleSettings?: any;
      projectView?: ProjectViewSettings;
      customRoles?: string[];
      constructionTypes?: ConstructionType[];
      /** Eigener Katalog Kategorie → Unterkategorien; fehlt er, gilt DEFAULT_PROJECT_CATEGORIES (services/projectCategories.ts). */
      projectCategories?: ProjectCategoryDef[];
      footerText?: string;
      emailSignatureTemplate?: string;
      /**
       * Eigene Vorlagen fuer die Belegueberschrift ("Teilschlussrechnung
       * Bauabschnitt 2"). Eine schlichte Wortliste neben invoiceConfig und
       * NICHT darin: der Rechnungsblock gehoert dem Einstellungsdialog, der
       * ihn vollstaendig verwaltet — siehe invoiceHeaderTemplates.ts.
       */
      invoiceHeaderTemplates?: string[];
      /**
       * Firmen-Overlay des Mängelkatalogs (Typ `KatalogOverlay` aus
       * services/mangelKatalog/typen.ts): ausgeblendete, geänderte und
       * eigene Einträge, Reihenfolge. Bewusst `unknown` — gelesen wird es
       * nur über `overlayNormalisieren` (mangelKatalog/ueberlagerung.ts),
       * das nie wirft und Unbekanntes verwirft.
       */
      mangelKatalog?: unknown;
  };
}

export interface UserInvite {
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
    company_name?: string;
    created_at: string;
    invited_by?: string;
}

export interface VacationRange {
    id: string;
    startDate: string;
    endDate: string;
}

export interface CustomDateRange {
    id: string;
    label: string;
    startDate: string;
    endDate: string;
}

export interface AlertConfig {
    enableOverrun: boolean;
    enableQuota: boolean;
    quotaThreshold: number;
    enableStartDocs: boolean;
    enableLatency: boolean;
    latencyDays: number;
}

export interface ProjectNumberConfig {
    enabled: boolean;
    dateFormat: 'YYMM' | 'YYYYMM';
    sequenceLength: number;
    prefixMap: { sigeko: string; sifa: string; hse: string; digital: string; other: string };
}

export interface ProjectViewSettings {
    viewFilters: { showOwn: boolean; showSubstitute: boolean; showDeputy?: boolean; };
    viewLayout: 'grid' | 'list';
    /** 'calendar' = Wochenkalender mit Verschieben per Drag-and-drop statt Gruppierung. */
    viewGrouping: 'none' | 'weekday' | 'interval' | 'calendar';
    /** 'active_all' = alles ausser den abgeschlossenen Projekten (Archiv ausgeblendet, Vorgabe). */
    statusFilter: 'all' | 'active' | 'planning' | 'on_hold' | 'completed' | 'finished' | 'active_all';
    sortConfig: { key: string; direction: 'asc' | 'desc' };
}

export interface AppSettings {
  companyName: string;
  companyAddress: string;
  companyLogo: string;
  /**
   * Zentrierter PDF-Fusszeilentext des Pruefberichts.
   *
   * Drei Zustaende, nicht zwei: undefined = nie gepflegt (Rueckfall auf
   * Firmenname | Anschrift), '' = bewusst geleert (Fusszeile bleibt leer).
   * Siehe services/profileSettings.resolveFooterText.
   */
  footerText?: string;
  emailSignatureTemplate?: string;
  appMode?: 'sigeko' | 'hse';
  filenameTemplate?: string;
  emailSubjectTemplate?: string;
  emailBodyTemplate?: string;
  customRoles?: string[];
  constructionTypes?: ConstructionType[];
  /** Vom Profil durchgereicht (App.withProfileSettings), damit die Stammdaten den gepflegten Katalog sehen. */
  projectCategories?: ProjectCategoryDef[];
  // signature (Webseite/USt-ID/Geschaeftsfuehrung/Registergericht) entfernt:
  // das Feld hatte ausser der Eingabemaske nie einen Leser. Diese Angaben
  // leben in InvoiceConfig und stehen von dort aus in Fusszeile und
  // E-Rechnung.
  moduleSettings?: {
      ufsa?: {
          defaultPreamble?: string;
      }
  }
}

export interface Contact {
  id: string;
  name: string;
  company: string;
  role: string;
  email: string;
  phone?: string;
  tradeType?: string;
  present: boolean;
  emailDistribution: boolean;
  inAdvanceNotice?: boolean;
  sectionIds?: string[];
  address?: string;
  sortOrder?: number;
}

export type ContactEntry = Contact;

/**
 * Gast einer EINZELNEN Begehung — jemand, der an dem Tag mitgelaufen ist, aber
 * nicht in die Projektbeteiligten gehoert (Bauherrenvertreter auf Stippvisite,
 * Poliere fremder Nachunternehmer, Praktikanten).
 *
 * Bewusst KEIN `Contact`: ein Kontakt landet in `project_contacts`, im
 * E-Mail-Verteiler, in der Vorankuendigung, im Organigramm des Handbuchs und im
 * SiGe-Plan — und stuende dort ab dann dauerhaft. Gaeste haengen deshalb allein
 * an `ReportMetadata.guests`, leben nur im `projects.data`-JSON und im
 * Archiv-Snapshot der Begehung, und werden beim Folgebericht geleert.
 */
export interface GuestParticipant {
  id: string;
  name: string;
  company?: string;
  role?: string;
}

export interface ProjectPlan {
  id: string;
  name: string;
  image: string;
  sectionId?: string;
}

export interface PlanCoordinates {
    x: number;
    y: number;
    /**
     * Plan dieser Nadel (ProjectPlan.id). Fehlt = Plan des Eintrags
     * (`InspectionItem.planId`, Altstand vor 28.09.2026). Lesen nur ueber
     * services/planPins.ts (pinsVon/pinsAufPlan).
     */
    planId?: string;
    /**
     * Nadel aus der Sensor-Verortung (Live-Standort, Auto-Ort, Ortsvorschlag);
     * hoechstens eine je Eintrag, der Blickpfeil aus `verortung` haengt an ihr.
     * Fehlt = von Hand gesetzt.
     */
    sensor?: boolean;
}

// ─── Sensor-Verortung (docs/maengel-verortung-plan.md Abschnitt 9/10) ────────
// Liegt im Blob unter `metadata.verortung` (metadata wird vollstaendig
// gespeichert; `metadata.plans` dagegen geht in die Tabelle project_plans mit
// festen Spalten — Planfelder dort gingen verloren). Alles optional: alte
// Projekte laufen unveraendert, die Verortung ist abschaltbar (Projekt: `aktiv`).

/** Geschoss mit Hoehe relativ ±0,00 (OKFF EG) in Metern. */
export interface VerortungGeschoss {
    id: string;
    name: string;
    hoeheM: number;
}

/** Geruest: `lagen` Lagen ab `ersteLageM` (relativ ±0,00) im Abstand `abstandM`. */
export interface VerortungGeruest {
    ersteLageM: number;
    abstandM: number;
    lagen: number;
}

/** Passpunkt eines Plans: Planposition (Prozent wie PlanCoordinates) ↔ UTM (Zone am Plan). */
export interface VerortungPasspunkt {
    id: string;
    name: string;
    plan: PlanCoordinates;
    ost: number;
    nord: number;
    quelle: 'gps' | 'utm' | 'achse';
    genauigkeitM?: number | null;
}

/** Benannter Punkt im Plan (Achskreuz „A/1") zum Uebertragen der Georeferenz zwischen Plaenen. */
export interface VerortungAchse {
    name: string;
    plan: PlanCoordinates;
}

/** Kalibrierung eines Plans (Schluessel in ProjektVerortung.plaene = ProjectPlan.id). */
export interface VerortungPlan {
    /** 'geschoss' = Grundriss eines Geschosses, 'lageplan' = Lage-/BE-Plan (Gelaende, Geruest aussen). */
    art?: 'geschoss' | 'lageplan';
    geschossId?: string | null;
    /** UTM-Zone der Passpunkte (Deutschland 32/33). */
    zone?: number;
    /** Breite/Hoehe des Planbilds in Pixeln — noetig fuer die Georeferenz. */
    seitenverhaeltnis?: number;
    passpunkte?: VerortungPasspunkt[];
    achsen?: VerortungAchse[];
}

/** Sensor-Verortung eines Projekts. `aktiv: false` schaltet Einblendung und Vorschlag im Projekt ab. */
export interface ProjektVerortung {
    aktiv: boolean;
    geschosse: VerortungGeschoss[];
    geruest?: VerortungGeruest | null;
    plaene?: Record<string, VerortungPlan>;
}

/**
 * Ergebnis der Sensor-Verortung am Eintrag (Geschoss, Hoehe, Blick). Der
 * Ortstext steht zusaetzlich in `location`. Seit 28.09.2026 mit den Nadeln in
 * inspection_items (SQL Nr. 130); kann auch OHNE Nadel stehen (nur Geschoss) —
 * welche Nadel zum Blick gehoert, sagt `PlanCoordinates.sensor`.
 */
export interface EintragVerortung {
    quelle: 'sensor';
    ebene?: string | null;
    hoeheM?: number | null;
    genauigkeitM?: number | null;
    /** Blickrichtung auf dem Plan, Grad im Uhrzeigersinn ab Plan-oben. */
    blickGradPlan?: number | null;
    sicherheit?: number | null;
}

export interface InspectionItem {
  id: string;
  ticketNumber?: number;
  title: string;
  location: string;
  observation: string;
  regulation: string;
  responsiblePerson: string;
  deadline: string;
  /**
   * Sofortmaßnahme: die Verantwortlichen wurden vor Ort informiert, der
   * Mangel bekommt KEINE Frist (deadline bleibt leer). Spalte
   * `inspection_items.immediate_action` (Nr. 87).
   */
  immediateAction?: boolean;
  dateFound?: string;
  status: 'open' | 'closed' | 'info' | 'good' | 'general';
  photos: string[];
  sectionId?: string;
  /** Plan der ERSTEN Nadel (die Sensor-Nadel steht immer vorn). Weitere Nadeln tragen ihren Plan selbst. */
  planId?: string;
  /** Nadeln (mehrere, auch auf mehreren Plaenen). Lesen/Aendern nur ueber services/planPins.ts. */
  planCoordinates?: PlanCoordinates | PlanCoordinates[];
  /** Sensor-Verortung (optional, siehe EintragVerortung). */
  verortung?: EintragVerortung;
  closedDate?: string;
  statusComment?: string;
  closedImage?: string;
  /**
   * Mängelkatalog („Standardmangel per Torte“, SQL Nr. 131
   * `inspection_items.katalog_key`): Knoten-ID der Auswahl (Torte oder
   * `eigen_<uuid>`); null bei einem Treffer direkt aus der Suche im
   * Gesamtkatalog. undefined = nicht bekannt (wird nicht geschrieben),
   * null = bewusst gelöscht. Schreiben/Lesen: services/mangelKatalog/spalten.ts.
   * Eine Katalognummer wird nirgends gespeichert (Entscheid 28.09.).
   */
  katalogKey?: string | null;
  /**
   * Kategorie-ID des Katalogs zum Zeitpunkt der Erfassung, z. B. 'absturz'
   * (Nr. 131 `inspection_items.katalog_kategorie`). null = bewusst gelöscht.
   */
  katalogKategorie?: string | null;
}

export interface ChecklistEntry {
  id: string;
  text: string;
  category: ChecklistCategory;
  isRelevant: boolean;
  lastChecked: string | null;
  status: ChecklistStatus;
  comment?: string;
  isCustom?: boolean;
  sectionId?: string;
}

export type ChecklistCategory = 'Allgemein' | 'Dokumente' | 'Baustelleneinrichtung' | 'Hochbau' | 'Tiefbau' | 'Brückenbau' | 'Industrie' | 'Abbruch' | 'Benutzerdefiniert' | 'SiGe-Plan';
export type ChecklistStatus = 'ok' | 'nok' | null;

export interface SafetyPoint {
  id: string;
  planId: string;
  x: number;
  y: number;
  label: string;
  number?: number;
  color?: string;
}

export interface MutualHazard {
  id: string;
  tradeSource: string;
  tradeAffected: string;
  description: string;
  measure: string;
  severity: 'low' | 'medium' | 'high';
}

export interface Note {
  id: string;
  text: string;
  date: string;
  author: string;
  mentions?: string[];
  readBy?: string[];
}

/**
 * Zuordnung einer LV-Position zu einer SiGeKo-Leistungsart.
 *
 * `asplan` = Arbeits- und Sicherheitsplan nach DGUV Regel 101-004 / TRGS 524.
 * Er steht NEBEN `sigeplan` und nicht darin: der A+S-Plan ist ein eigener
 * Plan mit eigener Freigabe, der nur bei Arbeiten in kontaminierten Bereichen
 * anfaellt ("bei Bedarf"). Auf dieselbe Zuordnung gelegt liessen sich die
 * beiden Leistungen auf der Rechnung nicht mehr auseinanderhalten, und die
 * Kachelzaehler beider Module zeigten dieselbe Position.
 *
 * `flatrate` = MONATSPAUSCHALE, eine eigene LV-Position (Einheit Monat,
 * Einheitspreis = Monatsbetrag). Sie ist keine Leistungsart wie die uebrigen
 * Werte, sondern ein Abrechnungsmodus: die Position traegt in `flatRate`,
 * WELCHE anderen Positionen sie geldlich deckt. Eine eigene Zuordnung statt
 * eines Flags auf einer bestehenden Position, weil die Pauschale selbst
 * gebucht und abgerechnet wird (ein Protokolleintrag je Monat) und dafuer
 * eine echte Position mit Menge, Preis und Protokoll braucht.
 *
 * ACHTUNG, zweiter Schluesselraum: `DokumentModul` in
 * `components/dokumente/types.ts` benennt die ABLAGE-Module von
 * `project_documents.module` und traegt andere Kennungen
 * (`vorankuendigung`, `begehung`, `baustellenordnung`). Nie das eine ueber
 * das andere abbilden.
 */
/**
 * `unterweisung` = Unterweisung des SiFa-Moduls (services/sifa): der Kunde
 * fuehrt sie per Link durch, gebucht wird je Teilnahme auf eine Stueckposition
 * (unterweisungBooking.ts). Eigener Wert, damit die Position nicht mit der
 * SiGe-Plan-EINWEISUNG (`einweisung`) verwechselt wird — zwei Leistungen,
 * zwei Kacheln, zwei Zuordnungen.
 *
 * `gbu` = Gefaehrdungsbeurteilung des SiFa-Moduls (docs/sifa-gbu-plan.md §10):
 * eine Stundenposition, gebucht ueber „Zeit buchen" in der GBU
 * (gbuBooking.ts). Optional (Entscheid O3): ohne Zuordnung findet der
 * Positionsfilter die Position auch ueber Stichworte im Titel.
 */
export type ServiceAssignment = 'none' | 'inspection' | 'einweisung' | 'vorank' | 'unterlage' | 'sigeplan' | 'asplan' | 'site_rules' | 'planning_general' | 'additional' | 'flatrate' | 'unterweisung' | 'gbu';

/**
 * Deckungsumfang einer Monatspauschale — steht AUF der Pauschalposition.
 *
 *  - `inspection` — die Pauschale deckt die Begehungspositionen des Projekts.
 *  - `all`        — die Pauschale deckt alle uebrigen Positionen.
 *  - `explicit`   — die Pauschale deckt genau die in `serviceIds` genannten
 *                   Positionen. Spaeter beauftragte Zusatzleistungen werden
 *                   NIE stillschweigend mitgedeckt: der Nachtragsdialog fragt
 *                   jedes Mal ausdruecklich "in der Monatspauschale
 *                   enthalten: ja/nein" (Betreiber-Entscheidung 3).
 *
 * Aufgeloest wird der Umfang ausschliesslich in
 * services/flatRateLogic.resolveFlatCoverage — nirgendwo sonst eine eigene
 * Regel, sonst deckt dieselbe Pauschale je nach Ansicht andere Positionen.
 */
export interface FlatRateCoverage {
  scope: 'inspection' | 'all' | 'explicit';
  /** Nur bei scope 'explicit': die gedeckten Positions-Ids. */
  serviceIds?: string[];
}

/**
 * Woher ein Bestandteil einer Paketposition seine Erledigung bekommt.
 *
 * Die Modulwerte sind DIESELBEN Kennungen wie
 * `projekt_unterlagen_ausgaben.unterlage` (SQL Nr. 163) — der Trigger der
 * Bestandteile vergleicht sie zeichengleich. Das ist ein DRITTER
 * Schluesselraum neben `ServiceAssignment` und `DokumentModul`; die einzige
 * Abbildung zwischen ihnen steht in services/bestandteile/bestandteilLogic.ts.
 * `hand` = nur von Hand erledigbar (z. B. „Abstimmung Bauherr").
 */
export type BestandteilQuelle =
  | 'vorankuendigung'
  | 'sigeplan'
  | 'asplan'
  | 'unterlage'
  | 'baustellenordnung'
  | 'handbuch'
  | 'hand';

/**
 * Ein Bestandteil einer PAKETPOSITION (Plan docs/positionsbestandteile-plan.md).
 *
 * Eine Paketposition (z. B. „Planungsphase", Menge 1) wird erst abrechenbar,
 * wenn jeder Bestandteil erledigt oder entfallen ist. Die Definition steht an
 * der Position (`project_services.bestandteile`, SQL Nr. 165) und wird beim
 * Anlegen aus Katalog/Angebot KOPIERT, nie verwiesen — eine spaetere
 * Katalogaenderung aendert laufende Projekte nicht.
 *
 * Die Erledigung steht NICHT hier, sondern in
 * `position_bestandteil_eintraege` (mit Zeitpunkt und Person, vom Server
 * gesetzt); die Zahl der offenen Bestandteile pflegt die Datenbank selbst in
 * `project_services.bestandteile_offen` (ServiceItem.bestandteileOffen).
 *
 * Rein intern: nie auf Rechnung, Leistungsnachweis, E-Rechnung, Angebots-PDF.
 */
export interface PositionsBestandteil {
  /** Stabil, nie neu vergeben und nie umbenannt — Eintraege verweisen darauf. */
  key: string;
  /** Anzeige, z. B. „Vorankündigung". */
  label: string;
  quelle: BestandteilQuelle;
  /**
   * Nur bei Modulquelle: ab welcher Ausgabe der Bestandteil erledigt ist.
   * Ein Versand erledigt auch `freigabe`, eine Freigabe erledigt `versand`
   * nicht. Fehlt der Wert, gilt die Vorgabe je Quelle
   * (bestandteilLogic.BESTANDTEIL_AB_VORGABE: Vorankuendigung = versand, sonst
   * freigabe — Betreiberentscheid E3 vom 19.10.2026).
   */
  ab?: 'freigabe' | 'versand';
}

/**
 * HERKUNFT einer Position, die beim Verknuepfen von einer Tochter an die
 * Mutter gewandert ist (services/projectLv.planLinkTransfer,
 * cloudService.applyLinkTransfer).
 *
 * Der Fall, fuer den es das Feld gibt: eine Mutter kann MEHRERE Toechter
 * haben, und jede Kollision mit Historie laesst eine eigene Zeile derselben
 * LV-Nummer zurueck. Nach zwei Toechtern fuehrt die Mutter drei Zeilen
 * "2.7.50 DGUV-Begehung", die im Buchungsdialog nicht zu unterscheiden sind —
 * eine Buchung landet dann bei der Schwester, und das faellt erst bei der
 * Rechnung auf.
 *
 * Warum ein eigenes Feld und KEIN Stempel im `title`: der Titel ist die
 * Leistungsbeschreibung, die auf der Rechnung des Kunden steht
 * (invoiceLogic: `title: service.title`). Eine interne Herkunftsangabe waere
 * damit ein Text im Kundenbeleg — und beim erneuten Verknuepfen ein zweites
 * Mal darin.
 *
 * Name und Nummer werden zum Zeitpunkt des Umzugs KOPIERT und danach nie
 * abgeglichen: die Angabe soll sagen, woher die Zeile kam, und dafuer braucht
 * die Anzeige keinen zweiten Ladeweg auf ein fremdes Projekt. Benennt sich das
 * Herkunftsprojekt spaeter um, bleibt `projectId` der belastbare Wert.
 */
export interface ServiceTransferOrigin {
  /** Projekt, aus dem die Position kam (projects.id). Der belastbare Wert. */
  projectId: string;
  /** Projektnummer zum Zeitpunkt des Umzugs — nur fuer die Anzeige. */
  projectNumber?: string;
  /** Projektname zum Zeitpunkt des Umzugs — nur fuer die Anzeige. */
  projectName?: string;
  /** Wann umgezogen wurde (ISO-Zeitstempel). */
  at: string;
}

export interface ServiceItem {
  id: string;
  lvPosition: string;
  clientLvPosition?: string;
  title: string;
  unit: string;
  quantityContracted: number;
  quantityUsed: number;
  quantityBilled?: number;
  unitPrice: number;
  status: 'open' | 'done' | 'billed';
  category?: string;
  isDefault?: boolean;
  assignment?: ServiceAssignment;
  /**
   * NEP — Eventualposition („Nur Einheitspreis"): steht mit Preis im Angebot,
   * ist aber NICHT beauftragt. Kommt aus QuoteItem.nep bei der
   * Auftragsannahme (quoteLogic.mapQuoteItemsToServiceItems) und wird in
   * TasksView von Hand umgeschaltet. Persistiert in
   * project_services.is_optional — der Name bleibt hier bewusst der der
   * Spalte, weil rohe Zeilen (DashboardView, TasksView) und ServiceItems
   * dasselbe Feld lesen.
   *
   * Wirkung: Kennzeichnung und Begruendungspflicht, keine Geldregel. Die
   * Position zaehlt in Leistung & Budget wie jede andere; wer darauf bucht,
   * muss im Buchungsdialog sagen, warum (nepPosition.ts) — etwa weil der
   * Kunde nachtraeglich beauftragt hat. Beim Versand wird sie nicht als
   * Zusatzleistung angeboten (additionalServiceCandidates), Aufgabenliste
   * und Dashboard fordern sie nicht als offenes Dokument ein.
   */
  is_optional?: boolean;
  dueDate?: string;
  /**
   * Minimum hours to charge per booking for an hourly position (per project,
   * proposed when the project is created). Only meaningful when the unit is
   * hourly; undefined keeps the legacy default of 1. Soft rule: booking a
   * lower value stays possible, the dialog only warns.
   */
  minHours?: number;
  /**
   * VERZICHT — erbrachte Menge, die ausdruecklich NICHT mehr abgerechnet wird.
   *
   * Der Fall, fuer den es das Feld gibt: das Projekt ist schlussgerechnet, eine
   * Begehung steht aber noch als offen (erbracht, nie auf einem Beleg). Sie
   * bliebe sonst dauerhaft in der Buchhaltungsliste, im Rechnungs-Picker, im
   * Projektcheck und im Rechnungslauf stehen — bisher war der einzige Ausweg
   * ein SQL-Eingriff von Hand (supabase/fix_5983_…, fix_6211_…).
   *
   * ABGRENZUNG, und sie ist der ganze Punkt: der Verzicht laesst
   * `quantityUsed` UNANGETASTET. Die Leistung wurde erbracht, sie steht weiter
   * im Leistungsnachweis und zaehlt weiter fuer den Begehungsrhythmus — sie
   * wird nur nicht mehr gefordert. Eine Buchung, die es gar nicht haette geben
   * duerfen (Bericht ausgefallen, doppelt gebucht), ist der ANDERE Fall und
   * gehoert zurueckgenommen, nicht verzichtet: dort muss `quantityUsed` sinken.
   * Beides zu vermischen faelscht entweder den Leistungsstand oder den
   * Nachweis.
   *
   * Wirksam wird das Feld an genau zwei Stellen, `openQuantity` und
   * `getProjectFinancials` (beide in services/projectLv.ts). Alles weitere —
   * Rechnungsauswahl, Projektcheck, Rechnungslauf, die Buchhaltungsliste —
   * haengt daran und braucht keine eigene Regel.
   */
  quantityWaived?: number;
  /**
   * Warum verzichtet wurde. PFLICHT beim Setzen.
   *
   * Ohne Grund ist der Verzicht im Streitfall wertlos, und er waere sonst der
   * bequemere Weg als die richtige Klaerung — dieselbe Ueberlegung wie bei
   * „nicht abrechenbar" fuer versendete Berichte (unbookedReportsLogic).
   */
  waivedReason?: string;
  waivedAt?: string;
  waivedBy?: string;
  /**
   * WELCHE Leistungen der Verzicht meint — die ids der Protokolleintraege.
   *
   * `quantityWaived` allein ist eine blanke Zahl: bei acht protokollierten
   * Begehungen sagt "auf 1 verzichtet" nur, dass es irgendeine war. Im
   * Streitfall ist das wertlos, und beim naechsten Blick auf das Projekt weiss
   * niemand mehr, welcher Termin gemeint war.
   *
   * Die Menge bleibt die fuehrende Groesse — jede Rechnung, jeder Projektcheck
   * und jeder Rechnungslauf rechnet weiter ueber `quantityWaived`
   * (projectLv.waivedQuantity). Diese Liste ist die BEGRUENDUNG dazu und darf
   * leer sein: bei Stunden oder einer Teilmenge ohne eigenen Eintrag gibt es
   * keinen Termin zu benennen, und der Altbestand kennt sie ohnehin nicht.
   */
  waivedEntryIds?: string[];
  /**
   * MONATSPAUSCHALE — macht diese Position zur Pauschalposition und benennt,
   * welche anderen Positionen sie geldlich deckt (Form: FlatRateCoverage).
   *
   * Der Fall, fuer den es das Feld gibt: das Projekt zahlt z. B. 1.200 EUR im
   * Monat fuer die Begehungen, egal ob 4 oder 5 stattfanden. Die Begehungen
   * muessen trotzdem als NACHWEIS auf dem Leistungsnachweis erscheinen — sie
   * kosten nur nichts mehr extra.
   *
   * KERNREGEL: eine GEDECKTE Position laesst `quantityUsed` weiterlaufen, ihr
   * GELDSTAND steht still. Die Deckung fasst `quantityUsed` NIE an —
   * Begehungszaehler, Begehungsrhythmus, Projektcheck und "versendet ohne
   * Buchung" duerfen sich nicht aendern. Im kumulativen Stand bleibt eine
   * gedeckte Position mit Menge = `quantityBilled` STEHEN statt wegzufallen:
   * fiele sie weg, schrumpfte der Gesamtstand unter die bereits ausgestellten
   * Vorbelege, und der naechste Beleg wuerde negativ.
   *
   * ABGRENZUNG zum VERZICHT (`quantityWaived`): der Verzicht ist einmalig,
   * begruendet und beziffert — eine konkrete erbrachte Menge wird nicht mehr
   * gefordert. Die Deckung ist dauerhaft und strukturell: alles, was auf einer
   * gedeckten Position aufgelaufen ist und auflaufen wird, ist mit der
   * Pauschale abgegolten. Beide Mechanismen bleiben getrennt; keiner ersetzt
   * den anderen.
   *
   * Gebucht wird die Pauschale selbst als Protokolleintrag je Monat mit
   * `bookingReference` 'FLAT-YYYY-MM' — der Schluessel ist der Riegel gegen
   * Doppelbuchung, nicht die Menge (ein angebrochener Monat darf 0,5 tragen).
   * Es wird NIE still automatisch gebucht, nur per Vorschlag mit einem Klick.
   * Die Ableitungen dazu liegen in services/flatRateLogic.ts.
   */
  flatRate?: FlatRateCoverage;
  /**
   * ABGELEITET, NIE PERSISTIEREN: Id der Pauschalposition, die diese Position
   * geldlich deckt — der Stempel, an dem die Geldpfade die Deckung erkennen.
   *
   * Gestempelt wird ausschliesslich in projectLv.stampFlatCoverage (das
   * getNormalizedServices auf jede Liste anwendet), aufgeloest ausschliesslich
   * ueber flatRateLogic.resolveFlatCoverage. Ein Stempel statt einer erneuten
   * Aufloesung an jeder Stelle, weil openQuantity nur die EINZELNE Position
   * sieht und die Rechnungslogik auch mit GEFILTERTEN Listen arbeitet — eine
   * Aufloesung gegen eine Liste ohne die Pauschalposition saehe die Deckung
   * nicht mehr, und die gedeckte Position wuerde erneut gefordert.
   *
   * NIE in die Datenbank: project_services wird mit expliziter Spaltenliste
   * geschrieben (cloudService/saveProjectToCloud), und der JSON-Blob leert
   * `globalData.services` vor dem Speichern. Beides muss so bleiben — ein
   * persistierter Stempel ueberlebte das Entfernen der Pauschale und deckte
   * weiter, obwohl niemand mehr zahlt.
   */
  flatCoveredBy?: string;
  /**
   * Herkunft nach einem Umzug von einer Tochter an die Mutter — siehe
   * ServiceTransferOrigin. Undefiniert bei jeder Position, die immer schon
   * dort lag; das ist der Normalfall und bleibt es.
   *
   * Persistiert in `project_services.transferred_from` (jsonb, SQL Nr. 68).
   * Geschrieben wird das Feld an GENAU EINER Stelle
   * (cloudService.applyLinkTransfer, im selben `update`, das `project_id`
   * umhaengt) — und wie beim Verzicht sendet saveProjectToCloud den Schluessel
   * nur mit, wenn der Aufrufer etwas dazu zu sagen hat. Sonst loeschte das
   * erste Speichern aus einer Ansicht, die das Feld nicht kennt, die Herkunft
   * still weg.
   */
  transferredFrom?: ServiceTransferOrigin;
  /**
   * PAKETPOSITION — die Bestandteile, die erledigt sein muessen, bevor die
   * Position abgerechnet werden darf (siehe PositionsBestandteil).
   *
   * Persistiert in `project_services.bestandteile` (jsonb, SQL Nr. 165). Wie
   * `flatRate` sendet saveProjectToCloud den Schluessel nur mit, wenn er
   * nicht `undefined` ist — sonst loeschte das erste Speichern aus einer
   * Ansicht, die das Feld nicht kennt, die Definition still weg. Entfernen
   * heisst ausdruecklich `null` senden; `[]` gilt wie `null` als „keine
   * Bestandteile".
   *
   * Nur bei Positionen mit beauftragter Menge 1 (Betreiberentscheid E5).
   */
  bestandteile?: PositionsBestandteil[] | null;
  /**
   * NUR LESEN, NIE SENDEN: Zahl der noch offenen Bestandteile — gepflegt von
   * der DATENBANK (Trigger in SQL Nr. 166 auf project_services und
   * position_bestandteil_eintraege), nicht vom Client.
   *
   * Steht hier, damit jeder Geldpfad, der die Positionen ohnehin laedt, das
   * Gate synchron kennt (openQuantity sieht nur die einzelne Position).
   * saveProjectToCloud schickt den Wert NIE mit; der Trigger rechnet ihn bei
   * jedem Schreiben ohnehin neu.
   *
   * Lesart (bestandteilLogic.bestandteilGate):
   *  - keine Bestandteile           → kein Gate
   *  - Bestandteile, Wert 0         → frei
   *  - Bestandteile, Wert > 0       → gesperrt
   *  - Bestandteile, Wert fehlt/null → UNBEKANNT = gesperrt (fail closed)
   */
  bestandteileOffen?: number | null;
  /**
   * ABGELEITET, NIE in eine Spalte geschrieben: der GELADENE Stand der
   * Definition — die Basis, gegen die saveProjectToCloud entscheidet, ob
   * `bestandteile` mitgeht. Gesetzt beim Laden (projectLv.bestandteilFelderAusZeile).
   *
   * Warum: eine veraltete Sitzung (alter Tab, Warteschlange, Bergung nach
   * Ankerfehler) traegt den alten Wert und schickte ihn sonst bei jedem
   * Speichern zurueck — ein altes `null` setzte die inzwischen festgelegte
   * Definition eines Admins zurueck, und die Paketposition waere ohne Gate
   * abrechenbar. Gesendet wird deshalb nur, was sich gegenueber DIESER Basis
   * geaendert hat, oder eine NEUE Position (keine Basis, oder eine Basis mit
   * fremder `id` — die Kopie einer Vorlage).
   *
   * Wer eine Position aendert, behaelt das Feld einfach per Spread
   * (`{ ...s, bestandteile: neu }`). Wer eine BESTEHENDE Position von Grund auf
   * neu baut, muss es uebernehmen — ohne Basis gilt sie als neu und ihre
   * Definition geht ungeprueft mit.
   *
   *  - `id`: die Position, fuer die die Basis gilt.
   *  - `json`: die normalisierte Definition beim Laden (`'null'` = keine).
   *  - `herkunft`: Kennung dieses einen Ladevorgangs (die Abstammung — nach
   *    einem eigenen Speichern gilt der gesendete Wert als neue Basis, aber nur
   *    fuer Staende derselben Herkunft).
   */
  bestandteileBasis?: { id: string; json: string; herkunft: string };
  /**
   * PAKETPOSITION — internes Stundenkontingent (Kalkulationsgrundlage, z. B.
   * „Planungsphase = 100 h"). Plan docs/stundenkontingent-vertrag.md.
   *
   * Persistiert in `project_services.stunden_kontingent` (numeric, SQL Nr. 171).
   * Wirksam wird der gesendete Wert NUR beim Anlegen der Position (Kopie aus
   * Katalog/Angebot). Bei einer bestehenden Position behaelt die Datenbank den
   * alten Wert (Trigger Nr. 172) — aendern darf ihn nur ein Admin ueber die RPC
   * `position_stundenkontingent_setzen`. So setzt kein alter Tab den Wert zurueck.
   *
   * Rein intern: nie auf Rechnung, Leistungsnachweis, E-Rechnung, Angebots-PDF.
   */
  stundenKontingent?: number | null;
  /**
   * NUR LESEN, NIE SENDEN: Summe der Stunden aller aktiven Bestandteil-Eintraege
   * dieser Position — gepflegt von der DATENBANK (Nr. 172), wie
   * `bestandteileOffen`. null/fehlt = unbekannt.
   */
  stundenErfasst?: number | null;
}

/**
 * Buchungsart aus dem Journal (Spalte service_journal.kind). Wird NUR von
 * journalToServiceTransactions gesetzt; JSON-Eintraege tragen sie nicht. Nie
 * aus einem bestehenden Eintrag in einen neuen kopieren (Spread) — ein
 * abgeleiteter Eintrag mit neuem Beleg truege sonst eine falsche Art.
 */
export type ServiceJournalKind =
  | 'work'
  | 'invoice_posting'
  | 'manual_posting'
  | 'waiver'
  | 'reversal'
  | 'legacy_backfill'
  | 'flat_rate';

export interface ServiceTransaction {
  id: string;
  date: string;
  serviceId: string;
  quantity: number;
  performerId: string;
  performerName: string;
  isSubstitution: boolean;
  /**
   * Buchungsart aus dem Journal (Spalte service_journal.kind).
   *
   * Wird NUR von journalToServiceTransactions gesetzt; JSON-Eintraege tragen
   * sie nicht — deshalb optional, und deshalb darf keine Auswertung ihr
   * Vorhandensein voraussetzen (serviceLogUtils.isPostingEntry faellt ohne sie
   * auf den Praefix- und Textrueckfall zurueck).
   *
   * NIE aus einem bestehenden Eintrag in einen neuen kopieren (Spread): ein
   * abgeleiteter Eintrag mit neuem Beleg truege sonst eine falsche Art — eine
   * aus einer Buchung abgeleitete Leistung bliebe `invoice_posting` und fiele
   * aus jeder Abrechnung heraus. Beim Schreiben ins Journal wird die Art
   * ohnehin aus dem INHALT neu abgeleitet (serviceJournal.journalKind).
   */
  kind?: ServiceJournalKind;
  description?: string;
  /**
   * WARUM auf eine NEP-Position (ServiceItem.is_optional) gebucht wurde —
   * Pflicht im Buchungsdialog, z. B. „Kunde hat am 12.09. nachträglich
   * beauftragt". Nur auf Buchungen aus dem Dialog auf eine NEP-Position.
   *
   * Eigenes Feld und NICHT in `description` eingemischt, weil die
   * Beschreibung an vier Stellen als Regel gelesen wird (serviceLogUtils,
   * serviceJournal, generatorPerformance: Hauptbuchung, Tochter-Wrapper,
   * „Manuelle Verbuchung", Berichtsnummer im Text). Eine Begruendung wie
   * „Freigabe nach Bericht Nr. 3" haette dort die echte Berichtsnummer
   * verdraengt, „manuelle Verbuchung" die Leistung zum Abrechnungsvorgang
   * gemacht. Angezeigt wird das Feld ueber nepPosition.withNepReason —
   * im Buchungsdialog und im Leistungsnachweis; das Buchungsjournal
   * (Zweitschrift) traegt es nicht.
   */
  nepReason?: string;
  // New booking fields
  bookedAt?: string;       // ISO date when this was booked/billed
  bookedBy?: string;       // Name of the admin who booked it
  bookedById?: string;     // ID of the admin who booked it
  bookingReference?: string; // Optional: Invoice number or reference
  reportRef?: string;        // Stable source-report key (RPT-<section>-<inspectionNo>) for idempotent booking
  /**
   * NUR auf einer Ruecknahmespur ('RN-…'): die id des Protokolleintrags, den
   * diese Ruecknahme entfernt hat, und sein `reportRef`.
   *
   * Warum: eine Spur mit Menge 0 und einem Text sagt nicht, WELCHE Buchung
   * weg ist. Bei Zwillingen (derselbe Bericht zwei- oder dreimal gebucht,
   * 6110/6185/6051 am 02.09.2026) sehen die verbleibenden Eintraege genauso
   * aus wie der entfernte — wer den Dialog erneut oeffnet, waehlt sonst den
   * letzten, RICHTIGEN Eintrag noch einmal. Ueber `reversedReportRef` kann der
   * Dialog sagen: „zu diesem Bericht wurde schon einmal zurueckgenommen".
   * Fehlen beide Felder, war es eine Ruecknahme ohne Eintrag (Luecke) oder
   * eine Spur aus der Zeit vor diesem Feld.
   */
  reversedEntryId?: string;
  reversedReportRef?: string;
  /**
   * NUR auf der Spur einer TEILWEISEN Ruecknahme: die Menge, um die der
   * Eintrag `reversedEntryId` gekuerzt wurde (4 Fahrten gebucht, 3 davon
   * zurueckgenommen → 3). Der Eintrag selbst bleibt unter seiner id im
   * Protokoll und traegt nur noch den Rest — die id darf nicht wechseln, weil
   * `allocations.entryId` ausgestellter Rechnungen auf sie zeigt.
   *
   * Fehlt das Feld auf einer Spur mit `reversedEntryId`, wurde der Eintrag
   * GANZ entfernt; seine Menge steht dann im Text der Spur.
   */
  reversedQuantity?: number;
  /**
   * HERKUNFT einer Zeile, die beim Verknuepfen von der Tochter an die Mutter
   * gewandert ist — mit dem, was sie VORHER trug.
   *
   * Warum es das gibt: an der Mutter darf eine Tochter-Begehung nicht wie eine
   * eigene aussehen. Der Transfer schreibt sie deshalb in die Kind-Form um
   * (`RPT-CHILD-<Tochter>-<Nr>`, `Tochter-Projekt "…": Bericht Nr. …`) — genau
   * die Form, die der Kind-zu-Mutter-Buchungspfad seit je schreibt und die
   * Leistungsnachweis, Journal und Tagesregel als Tochter erkennen. Ohne die
   * Umschreibung ordnete der Nachweis „Bericht #8" der Tochter dem Bericht #8
   * der MUTTER zu — Berichtsnummern sind nur innerhalb eines Projekts
   * eindeutig (6290-1, 02.09.2026: zehn statt acht Begehungen im Juli/August,
   * sechs fehlten im Mai/Juni).
   *
   * `reportRef` und `description` halten das ORIGINAL fest, damit der Rueckweg
   * (Trennen) die Zeile zeichengleich wiederherstellen kann: an der Tochter
   * ist dieselbe Begehung wieder eine eigene, und der Abschnitt im
   * urspruenglichen Ref (`RPT-<section>-<Nr>`) ist in der Kind-Form nicht
   * enthalten. Fehlt das Feld, ist die Zeile nie gewandert oder stammt aus
   * der Zeit vor diesem Umbau.
   */
  transferredFrom?: ServiceTransferOrigin & {
    /** `reportRef` vor der Umschreibung; `null`, wenn die Zeile keinen trug. */
    reportRef: string | null;
    /** `description` vor der Umschreibung. */
    description: string;
  };
  /**
   * Auftraggeber, dem diese Menge berechnet wurde.
   *
   * Nur auf ABRECHNUNGSzeilen ('BU-…' aus dem Ausstellen, 'ALT-…' aus einer
   * eingelesenen Alt-Rechnung) — eine erbrachte Leistung gehoert dem Projekt,
   * nicht einem Kunden.
   *
   * Traegt den Aufteilungsschluessel: teilen sich mehrere Auftraggeber ein
   * Projekt, ist der Abrechnungsstand JE AUFTRAGGEBER die Groesse, gegen die
   * die naechste kumulative Rechnung ihren Zuwachs rechnet. quantityBilled der
   * Position bleibt die Summe ueber alle — ein zweiter Zaehler daneben liefe
   * frueher oder spaeter auseinander, deshalb wird der Stand je Kunde aus
   * genau diesen Eintraegen ABGELEITET (projectSplit.billedByParty).
   *
   * Fehlt das Feld, ist die Menge Altbestand ohne Zuordnung. Sie wird keinem
   * Teilnehmer zugeschlagen, sondern sperrt die Aufteilung, bis jemand
   * entscheidet — geraten waere hier eine falsche Geldforderung.
   */
  customerId?: string;
  /**
   * ABSCHNITTE: der Teilnehmer des Aufteilungsschluessels (project_splits.id),
   * dem diese Menge berechnet wurde.
   *
   * Feiner als `customerId` und deshalb daneben und nicht statt ihrer: ein
   * Gesamtprojekt mit mehreren Haeusern hat EINEN Auftraggeber und trotzdem je
   * Haus eine eigene Rechnung. Der Abrechnungsstand ist dann je ABSCHNITT zu
   * fuehren; ueber den Auftraggeber gerechnet zoege die Rechnung von Haus B den
   * Stand von Haus A ab, und die Leistung fiele lautlos aus der Abrechnung.
   *
   * Fehlt das Feld an einer Abrechnungszeile (Altbestand vor SQL Nr. 54), loest
   * `projectSplit.billedByParty` sie ueber `customerId` auf — aber nur, wenn
   * GENAU EIN Teilnehmer diesen Auftraggeber hat. Sonst zaehlt sie als nicht
   * zugeordnet und sperrt, statt zu raten.
   */
  splitPartyId?: string;
  /** Anteil in Basispunkten, mit dem gebucht wurde (5000 = 50,00 %). */
  splitShareBp?: number;
  /**
   * Nur auf ALT-Zeilen (eingelesene Alt-Rechnung): was das Einlesen an der
   * Zielposition TATSAECHLICH veraendert hat.
   *
   * `quantity` bleibt die Belegmenge — die Aussage "diese Rechnung hat N
   * abgegolten". Angewandt wird davon oft weniger: erbrachte Leistung, die
   * bereits protokolliert ist, wird nicht ein zweites Mal hochgezaehlt.
   * Ohne diese beiden Werte waere die Ruecknahme nicht mehr die Umkehrung des
   * Einlesens — sie zoege die volle Belegmenge ab und risse ein Loch in den
   * Leistungsstand.
   *
   * Fehlen sie (Zeilen von vor dieser Aenderung), gilt die Belegmenge fuer
   * beide — das alte Verhalten, und fuer diese Zeilen auch das richtige.
   */
  appliedUsed?: number;
  appliedBilled?: number;
}

export interface ProjectSection {
    id: string;
    name: string;
    currentInspectionNumber?: number;
    projectNumberSuffix?: string;
    lastDate?: string;
}

export type BillingMode = 'project_daily' | 'section_individual';

export interface LinkedProjectConfig {
    role: 'parent' | 'child';
    linkedProjectId: string;
    linkedProjectName: string;
    billingMode?: 'daily_flat' | 'individual';
    /**
     * Die Umstellungen der Abrechnungsart, aelteste zuerst — `from` ist der
     * Kalendertag, ab dem die Art gilt.
     *
     * Gebraucht, weil die Abrechnungsart RUECKWIRKEND darueber entscheidet, ob
     * eine alte Begehung als abgerechnet gilt: bei Tagespauschale deckt die
     * Buchung der Mutter am selben Tag die Tochter mit. Ohne die Historie
     * machte ein Umschalten auf „einzeln" jede frueher gedeckte Begehung
     * schlagartig wieder zu einer offenen Forderung, obwohl sich an den
     * Rechnungen von damals nichts geaendert hat.
     *
     * Fehlt das Feld (Altbestand, nie umgestellt), gilt `billingMode` fuer alle
     * Tage — unveraendertes Verhalten. Siehe services/linkedBillingMode.ts.
     */
    billingModeHistory?: Array<{ mode: 'daily_flat' | 'individual'; from: string }>;
}

export interface ProjectScope {
    baustellenordnung?: any;
    vorankuendigung?: any;
    sigeplanPlanung?: any;
    unterlage?: any;
    initialMeeting?: any;
    totalInspections?: number;
    pricePerInspection?: number;
    billedInspections?: number;
}

export interface ProjectGlobalData {
  projectId?: string;
  status: ProjectStatus;
  startDate?: string;
  endDate?: string;
  planningStartDate?: string;
  planningEndDate?: string;
  executionStartDate?: string;
  executionEndDate?: string;
  customDateRanges?: CustomDateRange[];
  budget?: number;
  budgetUsed?: number;
  notes?: Note[];
  services?: ServiceItem[];
  serviceLog?: ServiceTransaction[];
  scope?: ProjectScope;
  sections?: ProjectSection[];
  enableSections?: boolean;
  linkedProjectConfig?: LinkedProjectConfig;
  billingMode?: BillingMode;
  /**
   * Vertraglich vereinbarter Sicherheitseinbehalt (5 = 5 %, § 17 VOB/B oder
   * freie Vereinbarung).
   *
   * NUR Vorbelegung fuer neue Belege des Projekts — wie ancillaryPercent aus
   * der Konfiguration. Kein stiller Durchgriff auf bestehende Entwuerfe:
   * Betraege duerfen sich nicht aendern, ohne dass es jemand am Beleg
   * eintraegt (dieselbe Begruendung wie beim Nachlass).
   */
  retention?: { percent: number };
  onHoldUntil?: string; 
  deliveryConfig?: {
      method: 'email' | 'poolarserver' | 'both';
      poolarserverUrl?: string;
  };
  locationCoordinates?: {
      lat: number;
      lng: number;
  };
  reportExempt?: boolean;
  /**
   * Die Antwort auf die Rueckfrage beim Archivieren: Ist die
   * Dokumentenablage (Modul „Dokumente", Tabelle `project_documents`)
   * behalten oder geraeumt worden?
   *
   * Der Vermerk ist kein Selbstzweck. Ohne ihn liesse sich Jahre spaeter
   * nicht mehr unterscheiden, ob ein Projekt nie eine Ablage hatte oder ob
   * sie beim Abschliessen bewusst geloescht wurde — und genau diese Frage
   * kommt, wenn jemand ein altes Projekt oeffnet und die Plaene sucht.
   *
   * Betrifft AUSSCHLIESSLICH die Handablage. Begehungsberichte, SiGe-Plaene,
   * Unterlage fuer spaetere Arbeiten, Vorankuendigungen und Rechnungen sind
   * davon nie beruehrt.
   */
  documentsArchiveDecision?: DokumenteArchivEntscheidung;
  /**
   * Antwort auf die Datenpflege-Rueckfrage im Projekt-Hub (projectDataHygiene):
   * „Das Projekt endet in Kuerze / ist ueberschritten — stimmt das Ende noch?"
   * Haelt fest, WELCHES Enddatum bestaetigt wurde. Aendert sich das Datum,
   * gilt die Bestaetigung nicht mehr; nach BESTAETIGUNG_GUELTIG_TAGE wird
   * erneut gefragt, damit ein ueberschrittenes Ende nicht ewig stehen bleibt.
   */
  endDateConfirmation?: {
    endDate: string;
    confirmedAt: string;
    confirmedBy?: string;
  };
}

export type IntervalType = 'daily' | 'weekly' | 'biweekly' | 'twice_monthly' | 'monthly' | 'custom' | 'manual';

export interface IntervalConfig {
  type: IntervalType;
  customDays?: number;
  lastInspectionDate?: string;
  nextScheduledDate?: string;
  preferredWeekdays?: number[];
}

export interface SubstitutionConfig {
    active: boolean;
    substituteId?: string;
    substituteName?: string;
    substituteEmail?: string;
    startDate: string;
    endDate: string;
}

export interface PrintOptions {
  includeDistribution: boolean;
  includeChecklists: boolean;
  includeActiveItems: boolean;
  includeHistory: boolean;
  includeReportHistory?: boolean;
  includePlans: boolean;
  includeHazards: boolean;
  includeStatistics: boolean;
  historyFilterDays?: number;
  plansLandscape?: boolean;
  includedPlanIds?: string[];
  hideClosedImagesAfterNextReport?: boolean;
  /**
   * Die Kästchen mit QR-Code und Link „Mangel online als behoben melden" vor
   * den Feststellungen (docs/freimeldung-online-plan.md). Fehlt der Wert, wird
   * gedruckt — abgewählt wird bewusst, nicht durch Vergessen.
   *
   * Der QR steht NUR im PDF, nie im Mailtext: ein Link im Fließtext kostet die
   * Versandmail Spam-Punkte, und der Mailtext bleibt deshalb unberührt.
   */
  showFreimeldungQr?: boolean;
}

/**
 * Eine natürliche Person mit Erreichbarkeit.
 *
 * Punkt 2 der Vorankündigung verlangt sie neben der Firma des Bauherrn,
 * Punkt 4 und 5 dürfen mehrere davon führen (Vertreter, 2. SiGeKo …).
 */
export interface VorankuendigungPerson {
    id: string;
    /** Freitext, z. B. "Vertreter", "2. SiGeKo", "Ansprechpartner". */
    role?: string;
    name: string;
    phone?: string;
    email?: string;
}

/** Punkt 4: verantwortlicher Dritter — Person ODER Firma, deshalb mit Anschrift. */
export interface VorankuendigungParty {
    id: string;
    role?: string;
    name: string;
    /** Mehrzeilige Anschrift. */
    address?: string;
    phone?: string;
    email?: string;
}

/** Punkt 5 a/b: ein Koordinator mit eigener Firmenanschrift. */
export interface VorankuendigungCoordinator {
    id: string;
    role?: string;
    /** Mehrzeilige Firmenanschrift. */
    company: string;
    name: string;
    phone?: string;
    email?: string;
    /**
     * Gesetzt, wenn der Eintrag aus den Projekt-Stammdaten stammt. Nur solche
     * Einträge zieht der Abgleich noch nach — von Hand ergänzte Personen
     * (Vertreter, zweiter SiGeKo) bleiben unangetastet.
     */
    coordinatorId?: string;
}

/**
 * Felder der Punkte 7–10, die der Anwender selbst gesetzt hat.
 *
 * Sie sind damit gegen den Abgleich mit den Projekt-Stammdaten gesperrt. Der
 * Weg zurück existiert ohnehin nicht: die Vorankündigung liest die Projektdaten
 * nur, sie schreibt nie hinein.
 */
export type VorankuendigungManualField = 'maxWorkers' | 'numEmployers' | 'numContractors' | 'employersList';

export interface VorankuendigungData {
    sender: string;
    recipient: string;
    siteAddress: string;
    clientName: string;
    clientAddress: string;
    clientPhone: string;
    clientEmail: string;
    /** Punkt 2: natürliche Personen des Bauherrn mit Telefon und E-Mail. */
    clientPersons?: VorankuendigungPerson[];
    projectDescription: string;
    /** @deprecated Freitext-Vorgänger von `responsibleThirdParties` (nur noch für Altstände). */
    responsibleThirdParty: string;
    /** Punkt 4: mehrere verantwortliche Dritte. */
    responsibleThirdParties?: VorankuendigungParty[];

    /** @deprecated Vorgänger von `planningCoordinators` (nur noch für Altstände). */
    coordinatorPlanningCompany: string;
    /** @deprecated Vorgänger von `planningCoordinators` (nur noch für Altstände). */
    coordinatorPlanningPerson: string;
    /** @deprecated Vorgänger von `planningCoordinators[].coordinatorId`. */
    planningCoordinatorId?: string;
    useOwnCompanyForPlanning?: boolean;
    /** Punkt 5 a: Koordinatoren für die Planung. */
    planningCoordinators?: VorankuendigungCoordinator[];

    /** @deprecated Vorgänger von `executionCoordinators` (nur noch für Altstände). */
    coordinatorExecutionCompany: string;
    /** @deprecated Vorgänger von `executionCoordinators` (nur noch für Altstände). */
    coordinatorExecutionPerson: string;
    /** @deprecated Vorgänger von `executionCoordinators[].coordinatorId`. */
    executionCoordinatorId?: string;
    /** Punkt 5 b: Koordinatoren für die Ausführung. */
    executionCoordinators?: VorankuendigungCoordinator[];

    startDate: string;
    endDate: string;
    maxWorkers: string;
    numEmployers: string;
    numContractors: string;
    employersList: string[];
    /** Punkte 7–10, die von Hand gesetzt und damit gegen den Abgleich gesperrt sind. */
    manualFields?: VorankuendigungManualField[];
    hasDangerousWork: boolean;
    selectedDangerousWorks?: string[];
    dangerousWorkDescription: string;
    hasSiteManager: boolean;
    siteManager: string;
    locationDate: string;
    signerName: string;
    history?: { date: string; action: 'created' | 'revised' }[];
    archive?: VorankuendigungArchiveEntry[];
}

export interface VorankuendigungArchiveEntry {
    id: string;
    date: string;
    /** Vom Anwender vergebener Versionsname. Fehlt bei Einträgen, die automatisch beim Export entstanden sind. */
    label?: string;
    snapshot: VorankuendigungData;
}

export interface RabEntry {
  id: string;
  component: string;
  category: string;
  activity: string;
  hazards: string;
  measures: string;
  frequency: string;
  regulations: string;
  sectionId?: string;
}

export interface CatalogSuggestion extends Omit<RabEntry, 'id'> {
    reason?: string;
}

export interface HazardFinding {
    id: string;
    title: string;
    description: string;
    box: { xmin: number; xmax: number; ymin: number; ymax: number; };
}

export interface AppendixImage {
    id: string;
    imageData: string;
    findings: HazardFinding[];
    suggestions?: CatalogSuggestion[];
    size?: 'small' | 'medium' | 'large';
}

export interface ProjectData {
  projectName: string;
  projectNumber: string;
  location: string;
  builder: string;
  builderAddress?: string;
  
  coordinator: string;
  coordinatorId?: string; 
  
  date: string;
  contacts: Contact[];
  preamble: string;
  projectImage: string;
  companyLogo: string;
  companyName: string;
  companyAddress: string;
  version: string;
  isDraft: boolean;
  themeColor: string;
  designId: string;
  coverImageSize?: 'small' | 'medium' | 'large';
  appendixImages?: AppendixImage[];
  /**
   * Reihenfolge der Kategorien im Maßnahmenkatalog (Liste UND PDF lesen sie
   * über `groupEntriesByCategory`). Fehlt sie, gilt DEFAULT_CATEGORY_ORDER.
   * Liegt bewusst im Projektdaten-JSON: eine eingefrorene Version nimmt ihre
   * Reihenfolge so mit.
   */
  categoryOrder?: string[];
  /**
   * Mitgeltende Unterlagen (Statik, Revisionspläne, Prüfbücher …) mit ihrem
   * Ablageort beim Betreiber. Fehlt das Feld, gilt eine leere Liste. Liegt im
   * Projektdaten-JSON und geht damit in jede eingefrorene Version mit.
   */
  mitgeltendeUnterlagen?: UfsaMitgeltendeUnterlage[];
}

/** Eine mitgeltende Unterlage der Unterlage für spätere Arbeiten. */
export interface UfsaMitgeltendeUnterlage {
  id: string;
  name: string;
  /** Id im Katalog der mitgeltenden Dokumente; fehlt/null bei freiem Eintrag. */
  katalogId?: string | null;
  /** Wo die Unterlage beim Betreiber liegt, z. B. „Hausmeisterbüro, Ordner Statik". */
  ablageort: string;
  bemerkung: string;
}

/**
 * Eingefrorener Stand der Unterlage für spätere Arbeiten.
 * Wird nur angehängt, bestehende Einträge werden nie verändert.
 */
export interface UnterlageVersion {
  id: string;
  label: string;
  createdAt: string;
  projectData: ProjectData;
  rabEntries: RabEntry[];
}

/**
 * Ein Verantwortlicher des Projekts. Die Reihenfolge in
 * `ReportMetadata.representatives` ist die Priorität: Index 0 ist der
 * Hauptverantwortliche (Koordinator), alle weiteren sind Stellvertreter.
 * Die ersten drei Einträge werden zusätzlich in die Legacy-Felder
 * `coordinator*`/`coordinatorDeputy*`/`coordinatorDeputy2*` gespiegelt,
 * damit PDFs, Vorankündigung und alte Clients unverändert weiterlaufen.
 * Wer in der Liste steht, ist zuständig — einen separaten Aktiv-Haken je
 * Eintrag gibt es nicht mehr.
 */
export interface ProjectRepresentative {
  /** profiles.id des Kollegen (fehlt bei Alt-Daten aus Freitext-Eingabe). */
  id?: string;
  name: string;
  email?: string;
  phone?: string;
  /** Wochentage der Vertretung (0 = So … 6 = Sa) — nur für Positionen > 0 ausgewertet. */
  weekdays?: number[];
  /**
   * Zuständig nur für einen Bereich (docs/projekt-kickoff-plan.md §2):
   * 'planung' = Vorankündigung, Unterlage, SiGe-Plan, A+S-Plan, Planung allg.;
   * 'ausfuehrung' = Begehungen, Einweisung, Baustellenordnung. Fehlt das Feld,
   * gilt der Eintrag für beide Bereiche (Altverhalten).
   */
  bereich?: 'planung' | 'ausfuehrung';
}

/**
 * Betreuungsgruppe nach DGUV Vorschrift 2, Anlage 2 (Grundbetreuung je
 * Beschaeftigtem und Jahr): I = 2,5 h, II = 1,5 h, III = 0,5 h. Die Summe
 * gilt fuer SiFa UND Betriebsarzt zusammen; der SiFa-Anteil ist Vereinbarung
 * (mindestens 20 % je Seite). Deshalb bleibt `einsatzzeitSollStunden` ein
 * frei editierbarer Wert — die Gruppe liefert nur den Vorschlag.
 */
export type SifaBetreuungsgruppe = 'I' | 'II' | 'III';

/** Betriebsdaten der SiFa-Betreuung — liegen im Projekt-JSON (metadata.sifa). */
export interface SifaBetriebsdaten {
  /** Branche/Taetigkeit des Betriebs (Freitext, z. B. „Metallbau", „Pflegeheim"). */
  branche?: string;
  /** Zustaendige Berufsgenossenschaft / Unfallkasse (z. B. „BG BAU", „BGHM"). */
  berufsgenossenschaft?: string;
  /** Mitgliedsnummer bei der BG. */
  bgMitgliedsnummer?: string;
  /** Beschaeftigte (Kopfzahl) — Grundlage fuer Betreuungsgruppe und ASA-Pflicht (> 20). */
  beschaeftigte?: number;
  betreuungsgruppe?: SifaBetreuungsgruppe;
  /** Vereinbarte SiFa-Einsatzzeit je Jahr in Stunden (Soll). */
  einsatzzeitSollStunden?: number;
  /** Bestellung der SiFa (Datum der schriftlichen Bestellung nach § 5 ASiG). */
  bestelltAm?: string;
  /** Betriebsarzt (Name/Praxis) — Partner in ASA und Vorsorge. */
  betriebsarzt?: string;
  /** Sicherheitsbeauftragte im Betrieb (Freitext, mehrere durch Komma). */
  sicherheitsbeauftragte?: string;
  /** Betriebsrat vorhanden (Beteiligung nach § 9 ASiG). */
  betriebsrat?: boolean;
  /** Ersthelfer-Anzahl (Soll ergibt sich aus Beschaeftigten: 5 % bzw. 10 %). */
  ersthelfer?: number;
}

export interface ReportMetadata {
  projectNumber: string;
  projectName: string;
  projectAddress: string;
  inspectionNumber: number;
  date: string;
  time: string;
  conductedBy: string;
  /**
   * Wer die Begehung TATSÄCHLICH durchgeführt hat — der Mensch, der den
   * Bericht schreibt und versendet, unabhängig davon, ob er fester Vertreter
   * ist oder nur in der Urlaubszeit übernimmt. Wird beim Archivieren aus
   * `conductor_name` gestempelt; weicht er vom Hauptverantwortlichen
   * (`coordinator`) ab, steht er im Protokoll als "Begehung durchgeführt durch".
   */
  conductedByName?: string;

  coordinator: string;
  coordinatorId?: string;
  coordinatorEmail?: string;
  coordinatorPhone?: string;
  
  coordinatorDeputy?: string;
  coordinatorDeputyId?: string;
  coordinatorDeputyEmail?: string;
  coordinatorDeputyPhone?: string;
  /** Spiegel — seit Wegfall des Aktiv-Hakens `true`, wenn die Position besetzt ist; nur noch für Alt-Clients/Legacy-RLS geschrieben. */
  coordinatorDeputyActive?: boolean;
  coordinatorDeputyWeekdays?: number[];

  coordinatorDeputy2?: string;
  coordinatorDeputy2Id?: string;
  coordinatorDeputy2Email?: string;
  coordinatorDeputy2Phone?: string;
  /** Spiegel — seit Wegfall des Aktiv-Hakens `true`, wenn die Position besetzt ist; nur noch für Alt-Clients/Legacy-RLS geschrieben. */
  coordinatorDeputy2Active?: boolean;
  coordinatorDeputy2Weekdays?: number[];

  /**
   * Quelle der Wahrheit für die Verantwortlichen (beliebig viele, sortiert).
   * Fehlt bei Alt-Projekten — `getRepresentatives` leitet die Liste dann aus
   * den Legacy-Feldern oben ab (lazy migration, kein Backfill).
   */
  representatives?: ProjectRepresentative[];


  clientName: string;
  clientAddress?: string;
  clientContactPerson?: string;
  clientPhone?: string;
  clientEmail?: string;
  
  customerName: string;
  customerAddress?: string;
  customerContactPerson?: string;
  customerPhone?: string;
  customerEmail?: string;
  
  siteContactCompany?: string;
  siteContactAddress?: string;
  siteContactPerson?: string;
  siteContactPhone?: string;
  siteContactEmail?: string;
  
  contractorName?: string; 
  contractorCompany?: string; 
  
  distributionList: Contact[];
  /** Teilnehmer NUR dieser Begehung, ohne Eintrag in den Projektbeteiligten. Siehe GuestParticipant. */
  guests?: GuestParticipant[];
  plans: ProjectPlan[];
  /** Sensor-Verortung: Geschosse, Geruest, Plan-Kalibrierung, Projektschalter (siehe ProjektVerortung). */
  verortung?: ProjektVerortung;

  intervalConfig?: IntervalConfig;
  globalData?: ProjectGlobalData;
  /** Legacy: einzelne Vertretung. Neue Clients schreiben `substitutions[]` und spiegeln den aktuellen Eintrag hierher. */
  substitutionConfig?: SubstitutionConfig;
  /** Quelle der Wahrheit für Urlaubsvertretungen (mehrere Zeiträume/Vertreter pro Projekt). */
  substitutions?: SubstitutionConfig[];
  
  /** `sifa` = Fachkraft fuer Arbeitssicherheit, unbefristete Betreuung (docs/sifa-unterweisung-plan.md). */
  projectType?: 'sigeko' | 'hse' | 'sifa';
  /**
   * Betriebsdaten einer SiFa-Dauerbetreuung (docs/sifa-betreuung-plan.md).
   * Nur bei `projectType === 'sifa'` gepflegt; ersetzt dort Bau-Kategorie,
   * Bauart, Bauherr und Projektvolumen der Stammdaten.
   */
  sifa?: SifaBetriebsdaten;
  constructionType?: string; 
  projectCategory?: string;
  projectSubcategory?: string;
  category?: string;
  subcategory?: string;
  totalProjectVolume?: number;
  reportLanguage?: ReportLanguage;
  isExported?: boolean;
  sectionId?: string;
  printOptions?: PrintOptions;
  /**
   * Einstellungen der externen Freimeldung (Empfänger des PDFs melden einen
   * Mangel über `#/freimeldung/<token>` als behoben).
   *
   * Bewusst KEINE eigene Spalte: der Wert wird in den Stammdaten gepflegt und
   * von der öffentlichen RPC aus `projects.data->'metadata'` gelesen.
   *
   * `aktiv` = die Online-Freimeldung ist für DIESES Projekt freigeschaltet.
   * Vorgabe (fehlt der Wert) ist AN — Betreiber 28.10.2026: lieber abwählen
   * als vergessen dazuzuwählen (`istFreimeldungAktiv`). Aus ist ein Projekt
   * nur mit dem ausdrücklichen `false` des abgewählten Hakens. Ohne Häkchen
   * entsteht kein Link, es wird kein QR-Code gedruckt, und ein bereits
   * gedruckter Link antwortet ab sofort als ungültig (die RPCs ab Nr. 183
   * prüfen das Flag selbst, mit derselben Vorgabe). Ein Projekt lässt
   * sich damit nachträglich stilllegen, ohne dass irgendwo ein Link
   * weiterlebt.
   *
   * `fotoPflicht` = das Beweisfoto ist Pflicht; Vorgabe (fehlt der Wert) ist
   * optional. Nur relevant, wenn `aktiv` gesetzt ist — ohne Freischaltung
   * gibt es keine Meldung, an der eine Foto-Pflicht hängen könnte.
   */
  freimeldung?: { aktiv?: boolean; fotoPflicht?: boolean };

  currentBillingServiceId?: string;
  /**
   * Aufteilung EINER Begehung auf mehrere LV-Positionen (Lose): eine Begehung
   * deckt mehrere Lose ab, geschrieben wird nur ein Bericht, die erbrachte
   * Leistung muss aber je Los abgerechnet werden.
   *
   * Optional und nur gesetzt, wenn tatsaechlich aufgeteilt wurde. Berichte von
   * vor dieser Aenderung (und jeder nicht aufgeteilte Vorgang) tragen weiterhin
   * nur `currentBillingServiceId` — jeder Leser muss darauf zurueckfallen.
   */
  currentBillingSplit?: BookingSplitRow[];
  isLegacyCorrected?: boolean;

  /**
   * Kurzbeschreibung des Projekts in ganzen Saetzen (Betreiber 12.09.2026:
   * Werbung in sozialen Medien). Erscheint auf dem Referenzblatt und in der
   * Admin-Uebersicht „Projektbilder". Frei formuliert, kein Pflichtfeld.
   * Die BILDER dazu liegen NICHT hier, sondern in der Tabelle
   * `project_media` (Nr. 99, src/services/projectMediaService.ts).
   */
  projectDescription?: string;
}

/**
 * Eine Zeile einer aufgeteilten Begehungsbuchung: LV-Position + die dort zu
 * buchende Menge. Die Mengen werden frei erfasst (1,5 Std. auf Los 1, 2,0 Std.
 * auf Los 2) — es gibt keine erzwungene Gleichverteilung.
 */
export interface BookingSplitRow {
    serviceId: string;
    quantity: number;
}

export interface Report {
    metadata: ReportMetadata;
    settings: AppSettings;
    items: InspectionItem[];
    checklists: ChecklistEntry[];
    mutualHazards?: MutualHazard[];
    safetyPoints?: SafetyPoint[];
    vorankuendigung?: VorankuendigungData;
    unterlage?: {
        projectData: ProjectData;
        rabEntries: RabEntry[];
        entries?: any[];
    };
    /** Gespeicherte Versionsstände der Unterlage (neueste zuerst). */
    unterlageVersions?: UnterlageVersion[];
}

export interface ReportHistoryEntry {
    id: string;
    projectNumber: string;
    projectName: string;
    inspectionNumber: number;
    sectionId: string | null;
    date: string;
    time: string;
    conductedByName: string;
    /**
     * Wer den Bericht VERSENDET hat (`report_history.sent_by`, Migration Nr. 67).
     * Der Name, der im Leistungsnachweis neben der Begehung steht.
     * `conductedByName` daneben bleibt, wer archiviert hat.
     */
    sentByName?: string;
    reportData: Report;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXTERNE FREIMELDUNG (docs/freimeldung-online-plan.md)
//
// Der Empfänger des Begehungsprotokolls meldet einen Mangel ohne Login als
// behoben. Die Meldung SCHLIESST den Mangel NICHT — sie legt eine offene
// Freimeldung an, die der Koordinator bestätigt oder ablehnt.
//
// Diese Daten stehen in EIGENEN Tabellen (`report_freimeldung_links`,
// `inspection_item_freimeldungen`), nie im Projekt-Blob: Autosave und
// `save_project_children_tx` (upsert + prune) würden eine RPC-Schreibung von
// außen beim nächsten Speichern wieder wegräumen.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Eine Meldung von außen zu genau einem Mangel.
 *
 * `itemId` ist bewusst nur Text und trägt KEINEN Fremdschlüssel auf
 * `inspection_items` — deshalb stehen `ticketNumber` und `itemTitle` als
 * Momentaufnahme daneben: Wird der Mangel gelöscht, bleibt die Meldung lesbar
 * und der Client erkennt sie als verwaist.
 */
export interface ExterneFreimeldung {
  id: string;
  linkId: string;
  projectId: string;
  /** Text, kein FK — die Id des Mangels aus `inspection_items`. */
  itemId: string;
  /** Momentaufnahme beim Melden; der Mangel selbst kann später verschwinden. */
  ticketNumber: number | null;
  itemTitle: string | null;
  firma: string;
  name: string;
  email: string | null;
  phone: string | null;
  /** Freitext der Firma; bei einer Zurückweisung die (Pflicht-)Begründung. */
  kommentar: string | null;
  /**
   * Was die Firma meldet (Spalte `art`, Nr. 184): `behoben` = Freimeldung,
   * `zurueckgewiesen` = die Firma weist den Mangel mit Begründung zurück.
   * Eine Zurückweisung ist nur ein Hinweis an den Koordinator — er nimmt sie
   * zur Kenntnis (Mangel bleibt offen) oder schließt den Mangel selbst.
   * Fehlt der Wert (Zeile vor Nr. 184), ist es eine Freimeldung.
   */
  art?: 'behoben' | 'zurueckgewiesen';
  /**
   * Objektpfad des Beweisfotos im Eimer `project-assets`
   * (`<projekt>/freimeldungen/<token12>/<uuid>.jpg`), sonst null. Das ist das,
   * was in der Datenbank steht (Spalte `foto_pfad`).
   */
  fotoPfad: string | null;
  /**
   * Öffentliche Storage-URL des Beweisfotos (`https://…`), sonst null.
   * ABGELEITET aus `fotoPfad` — sie steht nirgends gespeichert und hängt
   * deshalb an keinem Hostnamen, den ein Melder mitgeschickt hat.
   */
  fotoUrl: string | null;
  gemeldetAt: string;
  status: 'offen' | 'bestaetigt' | 'abgelehnt';
  bearbeitetVon: string | null;
  bearbeitetAt: string | null;
  bearbeitungsKommentar: string | null;
}

/**
 * Der Link EINES versendeten Berichts. Schlüssel ist
 * `(projectId, inspectionNumber, sectionId)`; ein Wiederholungsversand liefert
 * denselben Token, damit ein bereits gedruckter QR-Code gültig bleibt.
 *
 * `itemIds` ist die Momentaufnahme der Mängel, die im PDF offen standen — nur
 * sie zeigt die öffentliche Seite.
 */
export interface FreimeldungLink {
  id: string;
  projectId: string;
  inspectionNumber: number;
  sectionId: string | null;
  reportDate: string | null;
  itemIds: string[];
  token: string;
  /** null = unbefristet. */
  aktivBis: string | null;
  createdAt: string;
}

/** Der fertige QR-Code für das PDF — Muster `EinweisungQrQuelle`. */
export interface FreimeldungQrQuelle {
  token: string;
  url: string;
  /** PNG-DataURL des QR-Codes. */
  dataUrl: string;
  inspectionNumber: number;
}

export interface ImportSelection {
    meta_basics: boolean;
    meta_people: boolean;
    contacts: boolean;
    items: boolean;
    checklists: boolean;
    plans: boolean;
    hazards: boolean;
    modules: boolean;
}

export const INITIAL_IMPORT_SELECTION: ImportSelection = {
    meta_basics: true,
    meta_people: true,
    contacts: true,
    items: false,
    checklists: false,
    plans: true,
    hazards: true,
    modules: true
};

export const SERVICE_CATALOG: ServiceItem[] = [
    { id: '1', lvPosition: '1.2.10', title: 'SiGe-Plan erstellen', unit: 'Pauschal', quantityContracted: 1, quantityUsed: 0, unitPrice: 450, status: 'open', category: 'Entwurf', isDefault: true, assignment: 'sigeplan' },
    { id: '2', lvPosition: '1.4.10', title: 'Unterlage erstellen (RAB 32)', unit: 'Pauschal', quantityContracted: 1, quantityUsed: 0, unitPrice: 250, status: 'open', category: 'Entwurf', isDefault: true, assignment: 'unterlage' },
    { id: '3', lvPosition: '2.1.10', title: 'Vorankündigung erstellen', unit: 'Pauschal', quantityContracted: 1, quantityUsed: 0, unitPrice: 95, status: 'open', category: 'Ausführung', isDefault: true, assignment: 'vorank' },
    { id: '4', lvPosition: '2.7.10', title: 'Baustellenbegehung', unit: 'Stück', quantityContracted: 10, quantityUsed: 0, unitPrice: 350, status: 'open', category: 'Ausführung', isDefault: true, assignment: 'inspection' },
    { id: '5', lvPosition: '2.11.10', title: 'Baustellenordnung erstellen', unit: 'Pauschal', quantityContracted: 1, quantityUsed: 0, unitPrice: 150, status: 'open', category: 'Ausführung', isDefault: true, assignment: 'site_rules' },
    // Einweisung in den SiGe-Plan: abgerechnet wie eine Begehung — Einheit
    // "Stück" mit Kontingent, gebucht GENAU EINMAL je Einweisung.
    { id: '7', lvPosition: '2.12.10', title: 'Einweisung SiGe-Plan', unit: 'Stück', quantityContracted: 3, quantityUsed: 0, unitPrice: 180, status: 'open', category: 'Ausführung', isDefault: true, assignment: 'einweisung' },
    { id: '6', lvPosition: '3.1.10', title: 'Fahrtkosten', unit: 'Stück', quantityContracted: 10, quantityUsed: 0, unitPrice: 45, status: 'open', category: 'Sonstiges', isDefault: true, assignment: 'additional' }
];

// --- CRM + Angebotsmodul (Phase 1) ---

/**
 * `partial` = TEILBEAUFTRAGT: ein Teil der Positionen/Mengen ist beauftragt,
 * der Rest steht noch zur Entscheidung (Rest beauftragen / Rest entfällt).
 * Welche Mengen beauftragt sind, sagt allein `Quote.orderTranches`
 * (services/quoteOrderTranches.ts). Der Wert braucht Migration Nr. 140
 * (CHECK auf quotes.status).
 */
export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'partial' | 'rejected' | 'expired';

/** Eine Zeile eines Auftragsteils: welche Angebotsposition, welche Menge. */
export interface QuoteOrderLine {
  /**
   * QuoteItem.id — beim gewöhnlichen Angebot zugleich die id der
   * Leistungsposition im Projekt-LV. Beim NACHTRAGSANGEBOT steht die
   * Projektposition in QuoteOrderTranche.serviceIdByItemId.
   */
  itemId: string;
  quantity: number;
}

/**
 * Ein Auftragsteil (Teilbeauftragung) — `quotes.order_tranches`.
 *
 * Nur ANGEHÄNGT, nie umgeschrieben (einzige Ausnahme: `appliedAt` wird beim
 * Eintragen ins Projekt-LV einmal gesetzt). Beauftragte, entfallene und offene
 * Mengen werden daraus abgeleitet, nirgends sonst gespeichert.
 *
 *  - `order`   — der Kunde hat diese Mengen beauftragt; sie stehen im Projekt-LV.
 *  - `decline` — diese Mengen kommen nicht mehr („Rest entfällt"); kein LV-Bezug.
 *
 * Eigene Spalte statt `details`: der Editor schreibt `details` als Ganzes, ein
 * Autosave aus einem älteren Stand nähme sonst einen Auftragsteil zurück.
 */
export interface QuoteOrderTranche {
  /** uuid — zugleich die id der Projektnotiz, die den Teil im Projekt festhält. */
  id: string;
  kind: 'order' | 'decline';
  /** Laufende Nummer am Angebot (1, 2, …) — Anzeige „Teil 2". */
  seq: number;
  /** Zeitpunkt der Erfassung (ISO). */
  at: string;
  /** Datum der Beauftragung bzw. Absage durch den Kunden (yyyy-mm-dd). */
  orderDate: string;
  by?: string;
  /** Name zum Zeitpunkt der Erfassung (Profil kann später umbenannt werden). */
  byName: string;
  /** Bestellnummer / Zeichen des Kunden für diesen Teil. */
  customerRef?: string;
  lines: QuoteOrderLine[];
  /** Eingefrorener Nettobetrag des Teils (EUR, nach Positions- und Gesamtrabatt). */
  netAmount: number;
  /** Vermerk; bei `decline` Pflicht (Begründung). */
  note?: string;
  /**
   * Nur `order`: wann der Teil im Projekt-LV angekommen ist. Fehlt = steht
   * noch aus (Eintragen gescheitert) — „Erneut eintragen" holt es nach.
   */
  appliedAt?: string;
  /**
   * Nur NACHTRAGSANGEBOT: QuoteItem.id → ServiceItem.id der Projektposition,
   * auf der die Position landet — für JEDE Zeile dieses Teils (erhöhte
   * Position, sonst die neue, auch bei gleicher id; NEP nicht). Der erste
   * Teil trägt zusätzlich die im Annahmedialog gewählte Zuordnung der noch
   * nicht beauftragten Positionen (Erhöhung → deren id, neue Position → die
   * eigene id).
   * VERBINDLICH: spätere Teile und „Erneut eintragen" lesen die Zuordnung von
   * hier (ein späterer Teil gewinnt) und prüfen nur noch diese Position und
   * die gleiche id — keinen Vorschlag. Sonst verteilte sich eine
   * Angebotsposition auf zwei Projektpositionen. Ohne Eintrag: gleiche id,
   * sonst der Vorschlag (quoteSupplementLogic.proposeSupplementMapping).
   */
  serviceIdByItemId?: Record<string, string>;
}

export interface CustomerContact {
  id: string;
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

// Rahmenvertrag eines Kunden – im Kundendetail (Konditionen) gepflegt und im
// Angebot per Auswahl in details.frameworkRef uebernommen.
export interface FrameworkContract {
  id: string;
  number: string;
  title?: string;
  /** Verknüpfte LV-Vorlage aus dem Katalog (service_templates.id) – der Rahmenvertrag IST eine Katalog-Vorlage. */
  templateId?: string;
  validFrom?: string;
  validUntil?: string;
  note?: string;
  // Standard-Rahmenvertrag des Kunden: wird beim Anlegen eines neuen Angebots
  // automatisch als details.frameworkRef vorbelegt (nur einer je Kunde).
  isDefault?: boolean;
  /**
   * Vereinbarte Einheitspreise — gesetzt, wenn der Rahmenvertrag aus einem
   * angenommenen Angebot ohne Endsumme (totalsMode 'unitPrices') entstand.
   * Eingefroren wie das Angebot: Preisaenderungen laufen ueber ein neues
   * Angebot, nicht ueber das Bearbeiten dieser Liste.
   */
  items?: FrameworkContractItem[];
  /** Gesamtrabatt des Angebots in % — wirkt zusaetzlich zum Positionsrabatt. */
  discountPercent?: number;
  /** Herkunft (Angebot/Auftrag) — gesetzt nur bei Uebernahme aus einem Angebot. */
  source?: FrameworkContractSource;
}

/** Eine vereinbarte Preisposition eines Rahmenvertrags (aus dem Angebot). */
export interface FrameworkContractItem {
  /** id der Angebotsposition — stabil, damit eine Wiederholung nichts doppelt anlegt. */
  id: string;
  catalogItemId?: string;
  /** Positionsnummer im Angebot (z. B. "1.1.10"). */
  posNumber?: string;
  /** LV-Nummer (Katalog bzw. Kunden-LV), wie sie ins Projekt-LV ginge. */
  lvPosition: string;
  title: string;
  unit: string;
  /** Listen-Einheitspreis netto, wie im Angebot gedruckt. */
  unitPrice: number;
  /** Positionsrabatt in %. */
  discountPercent?: number;
  /** Eventualposition: Ausfuehrung nur auf gesonderte Anforderung. */
  nep?: boolean;
  /** Ueberschrift der Angebots-Sektion, zur Gliederung in der Anzeige. */
  sectionTitle?: string;
  category?: string;
  assignment?: ServiceAssignment;
  bemerkung?: string;
}

/** Woher ein Rahmenvertrag stammt (Angebotsannahme). */
export interface FrameworkContractSource {
  quoteId: string;
  quoteNumber: string;
  /** Auftragsnummer (AB) der Annahme. */
  orderNumber?: string;
  /** Zeitpunkt der Annahme (ISO). */
  acceptedAt: string;
  /** Beauftragt am (yyyy-mm-dd), falls im Dialog angegeben. */
  orderDate?: string;
  /** Bestellnummer des Kunden. */
  customerRef?: string;
  /** Name dessen, der angenommen hat. */
  acceptedByName?: string;
}

/** Eine historisierte Feldaenderung der Kunden-Stammdaten. */
export interface CustomerChange {
  /** Deutsches Feld-Label ("Name", "Adresse", …) — direkt anzeigbar. */
  field: string;
  from?: string;
  to?: string;
}

export interface CustomerChangeEntry {
  /** ISO-Zeitpunkt der Aenderung. */
  at: string;
  /** Anzeigename des Bearbeiters, falls bekannt. */
  by?: string;
  changes: CustomerChange[];
}

export interface Customer {
  id: string;
  // 5-stellige Kundennummer, buchstabenbasierter Debitoren-Nummernkreis
  // 30000-39999 (Sch/St eigene Bereiche). Vergabe ueber die next_customer_number-RPC,
  // Klassifikation/Vorschau in customerNumberRanges.ts.
  customerNumber?: string;
  /**
   * Unternehmen oder Privatperson. Fehlend = 'company': der gesamte Altbestand
   * ist als Firma angelegt, ein Nachtragen waere reines Rauschen.
   */
  customerType?: 'company' | 'person';
  /** Anrede ("Herr"/"Frau") — nur bei customerType 'person' relevant. */
  salutation?: string;
  /** Titel (z. B. "Dr.") — nur bei customerType 'person' relevant. */
  title?: string;
  /**
   * Vorname — nur bei customerType 'person' gepflegt (DATEV verlangt die
   * Trennung, die Briefanrede braucht den Nachnamen allein).
   */
  firstName?: string;
  /** Nachname — nur bei customerType 'person' gepflegt. */
  lastName?: string;
  /**
   * Anzeige-/Vollname. Bleibt die EINE Wahrheit fuer PDF, Angebot, Rechnung,
   * Suche und Dublettenpruefung; bei Privatpersonen aus firstName + lastName
   * komponiert (services/personName.ts), der Titel steht NICHT darin.
   */
  name: string;
  /**
   * Zweite und dritte Namenszeile des Anschriftfelds (z. B. Geschaeftsbereich,
   * Abteilung, z. Hd.). NUR fuer den Andruck der Adresse — Suche, DATEV,
   * Dublettenpruefung und Anzeige arbeiten weiter allein mit `name`.
   */
  name2?: string;
  name3?: string;
  address?: string;
  street?: string;
  zip?: string;
  city?: string;
  email?: string;
  phone?: string;
  website?: string;
  contacts: CustomerContact[];
  frameworkContracts?: FrameworkContract[];
  /**
   * Zahlungsziel in Tagen — nur die ABWEICHUNG vom Standard.
   *
   * null = globale Grundeinstellung des Rechnungsmoduls
   * (InvoiceConfig.defaultPaymentTermsDays). Frueher war das Feld eine
   * Pflichtangabe mit Vorbelegung; dadurch trug jeder Kunde einen Wert und
   * die globale Einstellung erreichte keine einzige Rechnung.
   *
   * undefined bedeutet in einem Patch "nicht anfassen", null dagegen
   * "auf die Grundeinstellung zuruecksetzen" — beides muss unterscheidbar
   * bleiben, sonst faellt ein geleertes Feld beim Speichern unter den Tisch.
   */
  paymentTermsDays?: number | null;
  defaultDiscountPercent: number;
  /**
   * Skonto-Kondition des Kunden: Abzug in Prozent bei Zahlung innerhalb der
   * Frist in Tagen. Belegt neue Rechnungsentwuerfe vor (details.skonto samt
   * Zahlungsbedingungen-Text); fehlend = kein Skonto.
   */
  skonto?: { percent: number; days: number };
  ratingZahlungsmoral?: number;
  ratingZusammenarbeit?: number;
  ratingWirtschaftlichkeit?: number;
  ratingOverall?: number;
  ratingNotes?: string;
  notes?: string;
  /**
   * Branche aus dem kontrollierten Vokabular (services/branchen.ts, CHECK in
   * supabase/geo_foundation.sql). Fehlend = ungepflegt; kein Backfill.
   */
  branche?: string;
  /**
   * Koordinaten des Firmensitzes. Gesetzt vom Batch-Job
   * (services/geocodeBackfill.ts) oder von Hand — nie beim Rendern der Karte,
   * dafuer ist das Nominatim-Limit zu eng.
   */
  lat?: number;
  lng?: number;
  /** Guete der Verortung: 'exact' | 'interpolated' | 'city' | 'failed'. */
  geoQuality?: string;
  /** Von Hand verortet — der Batch-Job fasst diesen Kunden nicht an. */
  geoManual?: boolean;
  /** Historie der Stammdaten-Aenderungen (services/customerChangeLog.ts). */
  changeLog?: CustomerChangeEntry[];
  /** Archiviert statt geloescht — Rechnungen referenzieren den Kunden (GoBD). */
  archivedAt?: string;
  archivedBy?: string;
  /** Ziel des Zusammenfuehrens. Kundennummer bleibt HIER, wird nie uebertragen. */
  mergedIntoId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Abweichende Kundenangaben NUR fuer EINEN Beleg (Rechnung oder Angebot).
 *
 * Ein Diff gegen den Kundenstamm, kein zweiter Kundensatz: gespeichert werden
 * ausschliesslich die Felder, die auf diesem Beleg anders lauten sollen. Alles
 * Uebrige folgt weiter dem Stamm — aendert sich dort die Strasse, druckt ein
 * Beleg, der nur "z. Hd. Frau Muster" abweichend fuehrt, die neue Strasse.
 * Ein voller Zweitsatz haette dieselbe Anschrift an zwei Orten gefuehrt und
 * waere beim ersten Umzug des Kunden stillschweigend falsch geworden.
 *
 * Gedacht fuer den Fall, dass EIN Auftraggeber fuer EIN Projekt eine andere
 * Rechnungsanschrift verlangt (eigene Vergabestelle, abweichende
 * Konzerngesellschaft, andere Rechnungs-E-Mail), ohne dass der Kundenstamm und
 * damit jeder andere Beleg desselben Kunden mitwandert.
 *
 * Bewusst NICHT enthalten: `customerNumber` (Debitorennummer, DATEV-Konto —
 * sie identifiziert den Kunden in der Buchhaltung und darf je Beleg nicht
 * abweichen) und alles Kaufmaennische (Zahlungsziel, Skonto, Preise); das sind
 * Konditionen des Kunden, keine Andruckangaben.
 *
 * Liegt in `details` und friert damit beim Ausstellen mit ein — ab da gilt
 * ohnehin allein der `customerSnapshot`, in den die Abweichung eingerechnet
 * wurde.
 */
export interface CustomerOverride {
  customerType?: 'company' | 'person';
  salutation?: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  name2?: string;
  name3?: string;
  /** Alt-Anschrift als Freitext — wird mitgefuehrt, wenn Strasse/PLZ/Ort abweichen. */
  address?: string;
  street?: string;
  zip?: string;
  city?: string;
  email?: string;
  phone?: string;
}

export interface CustomerPrice {
  id: string;
  customerId: string;
  catalogItemId: string;
  unitPrice?: number;
  discountPercent?: number;
  note?: string;
}

export interface QuoteItem {
  id: string;
  catalogItemId?: string;
  lvPosition: string;
  title: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  discountPercent?: number;
  category?: string;
  assignment?: ServiceAssignment;
  // Phase 2: frei definierbare Sektionen, Eventualpositionen (NEP), Bemerkung
  // und berechnete Positionsnummer (z.B. "1.1.10").
  sectionId?: string;
  nep?: boolean;
  bemerkung?: string;
  posNumber?: string;
  // Aus einer Ausschreibung uebernommene LV-Position (Auftraggeber-Vorgabe):
  // LV-Nummer und Menge sind vorgegeben und nur nach Rueckfrage aenderbar
  // (Schluessel bzw. Pruefwert fuer den Preisstempel im Original-LV);
  // Einheit, Beschreibung, Einheitspreis, NEP und Sektion bleiben editierbar.
  // Siehe "LV aus Ausschreibung".
  locked?: boolean;
  /** Struck-through LV position: kept for reference, excluded from totals and delivery. */
  excluded?: boolean;
  /**
   * Formatted service description (bold, italic, bullets, colour) for the
   * quote PDF. `title` stays the plain-text version and is what every other
   * consumer reads; this is only honoured while its plain text still equals
   * `title` (see quoteRichText.effectiveQuoteRich).
   */
  titleRich?: QuoteRichBlock[];
  /**
   * Bestandteile einer Paketposition (PositionsBestandteil) — INTERN, nie im
   * Angebots-PDF (anders als `bemerkung`). Wird bei der Auftragsannahme und
   * der Teilbeauftragung in ServiceItem.bestandteile kopiert.
   */
  bestandteile?: PositionsBestandteil[];
  /**
   * Internes Stundenkontingent der Paketposition (ServiceItem.stundenKontingent)
   * — INTERN, nie im Angebots-PDF. Geht nur zusammen mit `bestandteile` in die
   * Projektposition.
   */
  stundenKontingent?: number;
  /**
   * NACHTRAGSANGEBOT: id der Projektposition (ServiceItem.id), deren
   * Vertragsmenge diese Position bei der Annahme erhöhen soll. Gesetzt, wenn
   * die Position im Editor aus dem Projekt übernommen wurde — nur Vorschlag
   * für die Zuordnung im Annahmedialog, die dort noch geändert werden kann.
   */
  targetServiceId?: string;
}

/** How the quote PDF presents amounts — see QuoteDetails.totalsMode. */
export type QuoteTotalsMode = 'total' | 'unitPrices' | 'monthly';

/** One run of uniformly formatted text inside a formatted description line. */
export interface QuoteRichRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  /** '#rrggbb'; absent = the document's normal text colour. */
  color?: string;
}

/** One paragraph (or, with `list`, one bullet point) of a formatted description. */
export interface QuoteRichBlock {
  list?: boolean;
  /** With `list`: further line of the previous bullet point (line break, no new bullet). */
  cont?: boolean;
  runs: QuoteRichRun[];
}

// Frei definierbare Angebots-Sektion (Firmen-Schema als Default, siehe
// DEFAULT_QUOTE_SECTIONS). Reihenfolge = Anzeigereihenfolge im PDF.
export interface QuoteSection {
  id: string;
  title: string;
}

// Begehungs-Intervall einer Projektphase. 'individuell' erlaubt eine freie
// Anzahl Begehungen pro Woche (perWeek).
export type IntervalKind = 'taeglich' | 'woechentlich' | '14taeglich' | '2xmonat' | '1xmonat' | 'individuell';

// Eine Phase mit eigenem Zeitraum und Intervall. Ein Projekt kann mehrere
// Phasen mit unterschiedlichen Intervallen haben (z.B. erst 14-taeglich, dann
// woechentlich). Die Begehungsanzahl wird je Phase automatisch berechnet.
export interface IntervalPhase {
  id: string;
  from: string;
  to: string;
  interval: IntervalKind;
  perWeek?: number; // nur fuer 'individuell' (z.B. 3 = 3x pro Woche)
  // Verknuepfte Angebotsposition: deren Menge wird aus den Begehungen dieser
  // Phase (und weiterer auf dieselbe Position verlinkter Phasen) berechnet.
  linkedItemId?: string;
  // Menge je Begehung (z.B. 2.5 Std pro Begehung). Default 1.
  factor?: number;
}

// Strukturierte Detail-/Metadaten eines Angebots (in quotes.details als jsonb).
/**
 * Bezug eines Nachtragsangebots auf ein bestehendes Projekt
 * (QuoteDetails.nachtragZu). Die Anzeigefelder werden beim Anlegen bzw. bei
 * der Annahme eingefroren — massgeblich ist `projectId`.
 */
export interface QuoteSupplementRef {
  projectId: string;
  projectNumber?: string;
  projectName?: string;
  /** Angebot, aus dem das Projekt entstand (falls bekannt). */
  mainQuoteId?: string;
  mainQuoteNumber?: string;
  /** Auftragsnummer des Hauptangebots (AB …). */
  mainOrderNumber?: string;
  /** Datum des Hauptauftrags (yyyy-mm-dd). */
  mainOrderDate?: string;
  /** Laufende Nachtragsnummer am Projekt (1, 2, …) — LV-Präfix „N<seq>." neuer Positionen. */
  seq?: number;
}

export interface QuoteDetails {
  sections?: QuoteSection[];
  siteStreet?: string;
  siteZip?: string;
  siteCity?: string;
  projectStart?: string;
  projectEnd?: string;
  // Mehrphasige Intervalle. Wenn gesetzt und nicht leer, werden intervalLabel
  // und inspectionCount daraus abgeleitet (Fallback-Felder fuer Altbestand).
  intervalPhases?: IntervalPhase[];
  intervalLabel?: string;
  inspectionCount?: number;
  frameworkRef?: string;
  // Angebotsart: Ueberschriftzeilen auf Seite 1 des PDF unter dem Betreff
  // (mehrzeilig via \n). Default, wenn leer:
  // "SiGeKo-Leistungen nach Baustellenverordnung\nHonorarangebot".
  offerType?: string;
  // Manueller Hoehenversatz des Anschreiben-Blocks auf Seite 1 des PDF in mm:
  // verschiebt Datumszeile, Betreff ("Angebot Nr. ...") und alles darunter
  // gemeinsam nach unten (positiv) oder oben (negativ). Ueber den Regler im
  // Angebotseditor gepflegt; der Generator klemmt den Wert, damit der Block
  // weder ins Anschriftfeld rutscht noch die Seite sprengt. Fehlt das Feld
  // (Altbestand), gilt 0 = bisheriges Layout.
  bodyOffsetMm?: number;
  // Freie Zusatzzeilen der "Ihr Angebot auf einen Blick"-Box auf Seite 1 des
  // PDF. Die Box zeigt datengetragene Zeilen (Zeitraum, Begehungen) plus diese
  // Liste — NIE fest einkompilierte Leistungszusagen: was hier steht, muss der
  // Ersteller ausdruecklich hinterlegt haben, sonst verspricht das PDF
  // Leistungen, die nicht angeboten sind.
  glanceLines?: string[];
  // Optionaler Inklusiv-Hinweis direkt unter dem Endsummen-Block (z. B.
  // "Fahrt- und Dokumentationskosten sind im Angebotspreis enthalten.").
  // Nur ausgegeben, wenn gesetzt — aus demselben Grund wie glanceLines.
  inclusiveNote?: string;
  // Allgemeine Geschaeftsbedingungen (Freitext, im PDF auf eigener Seite).
  agbText?: string;
  // Datenschutzinformation (Freitext, im PDF auf eigener Seite nach den AGB).
  privacyText?: string;
  // Steuert die Darstellung der Endsummen-Bloecke im Angebots-PDF:
  //  - 'auto' (Default, wenn undefined): beide Bloecke (ohne/inkl. NEP) + Fussnote
  //    NUR wenn das Angebot NEP-Positionen enthaelt; sonst ein einzelner
  //    Summenblock ohne "(ohne NEP)"-Zusaetze und ohne Fussnote.
  //  - 'both': immer beide Bloecke + Fussnote (auch ohne NEP-Positionen).
  //  - 'simple': immer nur ein schlichter Summenblock (Werte = ohne NEP).
  nepDisplay?: 'auto' | 'both' | 'simple';
  // Summen im Angebots-PDF (undefined = 'total'):
  //  - 'total': Gesamtbetrag je Position, Zwischen- und Endsummen (bisher).
  //  - 'unitPrices': nur Einheitspreise — keine Menge, keine Summen; fuer
  //    laufende Leistungen ohne Enddatum (z. B. SiFa), abgerechnet nach Aufwand.
  //  - 'monthly': Mengen und Betraege gelten je Monat, Summen "je Monat".
  // Gespeicherte netTotal/grossTotal rechnen unveraendert Menge x Preis.
  totalsMode?: QuoteTotalsMode;
  // Aus einer Ausschreibung ausgelesene Anforderungen (Funktion "Anforderungen
  // auslesen"): Freitext zum Abgleich (Leistungsumfang, Fristen, Auflagen), der
  // NICHT in konkrete Positionen/Preise uebersetzt wird. Projekt-Meta
  // (Bauvorhaben->title, Adresse, Start/Ende, Intervalle) landet in den
  // bestehenden Feldern oben bzw. in quote.title.
  tenderRequirements?: string;
  // Haengt das gespeicherte Original-Ausschreibungs-PDF (tender_documents,
  // kind 'lv') hinten an das Angebots-PDF an — Angebot vorne, Original ab der
  // Folgeseite unveraendert. Nur sinnvoll, wenn zum Angebot ein LV-Dokument
  // hochgeladen wurde. Legacy-Flag: von lvOriginalMode abgeloest (true -> 'append').
  attachOriginalLv?: boolean;
  // Einbindung des Original-LV ins Angebots-PDF:
  //  - 'none'   (Default): nicht einbinden.
  //  - 'append': Original unveraendert hinten anhaengen.
  //  - 'priced': Preise (EP/GB) werden per Text-Matching in das Original
  //    gestempelt; das bepreiste Original ersetzt die eigene Kalkulation
  //    (PDF = Anschreiben + bepreistes LV + Zahlungsbed./AGB/Datenschutz).
  lvOriginalMode?: 'none' | 'append' | 'priced';
  // tender_documents.id des Original-LV, dessen Positionen UEBERNOMMEN wurden.
  // Jeder Import laedt sein PDF sofort hoch; ohne diesen Verweis gewann das
  // juengste Dokument — auch aus einem abgebrochenen oder falschen Import.
  // Fehlt das Feld (Altbestand), gilt weiter das juengste 'lv'-Dokument.
  activeTenderDocumentId?: string;
  // Herkunft eines EINGELESENEN Alt-/Fremdangebots (PDF-Import). Gesetzt heisst:
  // dieses Angebot wurde nicht in der App geschrieben, sondern aus einem
  // fertigen Beleg uebernommen. Auswertungen zaehlen es mit, kennzeichnen es
  // aber — sonst sieht ein nachgetragener Altbestand aus wie frische Akquise.
  importedQuote?: QuoteImportInfo;
  /**
   * Abweichende Kundenangaben NUR fuer dieses Angebot (siehe CustomerOverride).
   *
   * Gleiche Regel wie auf der Rechnung: der Bearbeiten-Dialog schreibt sonst in
   * den Kundenstamm und damit auf jeden anderen Beleg desselben Kunden. Wirkt
   * auf Vorschau, PDF, Versand-Empfaenger und den beim Versand eingefrorenen
   * customerSnapshot.
   */
  customerOverride?: CustomerOverride;
  /**
   * Verlauf der Nachfaesse zu diesem Angebot (Reiter "Nachfassen").
   *
   * Bewusst im JSON und nicht in einer eigenen Tabelle: der Verlauf wird immer
   * zusammen mit dem Angebot gelesen und gehoert zu ihm wie der Positionssatz.
   * Aeltester Eintrag zuerst — angehaengt wird nur ans Ende, bestehende
   * Eintraege werden NIE umgeschrieben (sonst verschwindet der Stand, auf den
   * sich ein spaeteres Gespraech beruft).
   */
  followUpLog?: QuoteFollowUpEntry[];
  /**
   * Gesetzt, wenn das Angebot als Rahmenvertrag angenommen wurde: id des
   * Rahmenvertrags am Kunden (Customer.frameworkContracts). Dann gibt es
   * KEIN Projekt — die Preise liegen beim Kunden.
   */
  frameworkContractId?: string;
  /**
   * NACHTRAGSANGEBOT zu einem bestehenden Projekt. Bei der Annahme entsteht
   * kein neues Projekt: die Positionen erhöhen die Vertragsmenge vorhandener
   * Projektpositionen oder werden mit LV-Präfix „N<seq>." angehängt
   * (quoteSupplementLogic.ts / quoteSupplementService.ts).
   */
  nachtragZu?: QuoteSupplementRef;
}

/**
 * Ergebnis eines Nachfassens. Steuert nur die Beschriftung/Farbe — welche
 * Angebote faellig sind, entscheidet allein follow_up_date/follow_up_done_at.
 */
export type QuoteFollowUpOutcome =
  | 'reached'          // Kunde erreicht, Stand aufgenommen
  | 'no_answer'        // niemand erreicht / keine Rueckmeldung
  | 'decision_pending' // Entscheidung laeuft noch, spaeter erneut fragen
  | 'postponed'        // Kunde hat vertagt
  | 'closed';          // kein weiteres Nachfassen

/** Ein einzelnes, erledigtes Nachfassen (QuoteDetails.followUpLog). */
export interface QuoteFollowUpEntry {
  /** Zeitpunkt der Erfassung (ISO). */
  at: string;
  /** Nachfassdatum, das mit diesem Eintrag erledigt wurde (yyyy-mm-dd). */
  dueDate?: string;
  /** Nutzer-Id des Nachfassenden. */
  by?: string;
  /**
   * Name zum Zeitpunkt der Erfassung. Wird mitgeschrieben, weil ein Profil
   * spaeter umbenannt oder geloescht werden kann — der Verlauf muss dann noch
   * sagen koennen, wer telefoniert hat.
   */
  byName: string;
  outcome: QuoteFollowUpOutcome;
  /** Stand in Worten (Pflichtfeld im Dialog). */
  note: string;
  /** Naechstes Nachfassdatum (yyyy-mm-dd). Fehlt = Nachfassen abgeschlossen. */
  nextDate?: string;
  /** Gewaehlter Abstand in Tagen, falls ueber das Intervall gesetzt. */
  nextIntervalDays?: number;
}

/** Belegherkunft eines eingelesenen Angebots (QuoteDetails.importedQuote). */
export interface QuoteImportInfo {
  source: 'pdf';
  fileName?: string;
  /** Zeitpunkt des Imports (ISO). */
  importedAt: string;
  /**
   * Belegnummer wie im Original. Die interne Angebotsnummer wird beim Anlegen
   * neu gezogen — zwei Nummernkreise duerfen sich nicht vermischen.
   */
  originalNumber?: string;
  /**
   * Vom Beleg ABGELESENE Endsummen. Sie werden bewusst nicht als Summen des
   * Angebots verwendet (die entstehen aus den Positionen), sondern
   * aufbewahrt, damit eine Abweichung nachvollziehbar bleibt.
   */
  statedNet?: number;
  statedGross?: number;
}

// Wiederverwendbarer Textbaustein fuer eines der vier Angebots-Textfelder.
export type TextTemplateCategory = 'intro' | 'agb' | 'outro' | 'payment' | 'offer_type' | 'privacy';
export interface TextTemplate {
  id: string;
  category: TextTemplateCategory;
  name: string;
  content: string;
}

export interface Quote {
  id: string;
  quoteNumber: string;
  orderNumber?: string;
  customerId: string;
  customerName?: string;
  projectId?: string;
  status: QuoteStatus;
  title: string;
  quoteDate: string;
  validUntil?: string;
  items: QuoteItem[];
  discountPercent: number;
  taxRate: number;
  netTotal: number;
  grossTotal: number;
  introText?: string;
  outroText?: string;
  paymentTerms?: string;
  pdfUrl?: string;
  sentAt?: string;
  sentTo?: string;
  customerSnapshot?: Customer;
  acceptedAt?: string;
  rejectedAt?: string;
  followUpDate?: string;
  followUpDoneAt?: string;
  followUpNotifiedAt?: string;
  details?: QuoteDetails;
  /**
   * Auftragsteile (Spalte `order_tranches`, Migration Nr. 140). Leer bei
   * Angeboten, die vor der Teilbeauftragung angenommen wurden — die gelten
   * als voll beauftragt (quoteOrderTranches.effectiveTranches).
   */
  orderTranches?: QuoteOrderTranche[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// An ein Angebot gebundenes Original-Ausschreibungs-PDF (Tabelle
// tender_documents, privater Bucket 'tender-docs'). 'lv' = Leistungsverzeichnis
// (Positionen uebernommen), 'requirements' = Anforderungs-/Meta-Extraktion.
export type TenderDocumentKind = 'lv' | 'requirements';
export interface TenderDocument {
  id: string;
  quoteId: string;
  kind: TenderDocumentKind;
  filePath: string;
  fileName?: string;
  mimeType?: string;
  uploadedBy?: string;
  createdAt: string;
}

// Frei definierbare Angebotsvorlage (Firmen-Schema als Default).
export interface QuoteTemplate {
  id: string;
  name: string;
  payload: {
    title?: string;
    // Angebotsart-Ueberschrift (details.offerType) der Vorlage.
    offerType?: string;
    introText?: string;
    outroText?: string;
    paymentTerms?: string;
    sections?: QuoteSection[];
    items?: QuoteItem[];
    discountPercent?: number;
    taxRate?: number;
  };
  createdBy?: string;
  createdAt?: string;
}

// Firmen-Standard-Sektionen (Default, wenn ein Angebot keine eigenen Sektionen
// definiert). Reihenfolge = Kalkulationsblatt der Firma.
export const DEFAULT_QUOTE_SECTIONS: QuoteSection[] = [
  { id: 'planung', title: 'SiGeKo-Leistungen während der Planungsphase' },
  { id: 'bau', title: 'SiGeKo-Leistungen während der Bauphase' },
  { id: 'stunden', title: 'Stundensatz SiGeKo / Fachkraft für Arbeitssicherheit' },
];

/**
 * Globaler Briefkopf + Briefpapier (profiles.settings.letterheadConfig).
 *
 * Gilt fuer Angebote UND Rechnungen; Rechnungen koennen weiterhin per
 * InvoiceConfig.ownLetterhead einen eigenen Block setzen. Existiert das
 * Objekt, gilt es VOLLSTAENDIG — auch seine leeren Felder. Kein Rueckfall
 * Feld fuer Feld auf die Altfelder in QuoteConfig: ein geleertes Feld muss
 * leer bleiben duerfen (dasselbe Muster wie ownLetterhead).
 * Fehlt das Objekt ganz (Altbestand), gelten die Felder aus QuoteConfig.
 */
export interface LetterheadConfig {
  /** Rechte Briefkopf-Spalte auf Seite 1 des PDF, eine Angabe pro Zeile. */
  letterheadText?: string;
  /** Schriftgroesse der PDF-Fusszeile in pt (Default 7). */
  footerFontSize?: number;
  /** Kleine linke Kopfzeile auf Seite 1 (z. B. Verbandsmitgliedschaft), unter der oberen Trennlinie. */
  letterheadLeftLine?: string;
  /** Schriftgroesse der linken Kopfzeile in pt (Default 6.5). */
  letterheadLeftLineFontSize?: number;
  // ── Briefpapier (Stationery) ──────────────────────────────────────────────
  // Firmen-Briefpapier als Ganzseiten-Hintergrund. Ist stationeryPage1
  // gesetzt, ersetzt das Briefpapier den selbst gezeichneten Briefkopf/
  // Fusszeile des Generators.
  /** dataURL (JPEG/PNG) — Hintergrund Seite 1 (Anschreiben). */
  stationeryPage1?: string;
  /**
   * dataURL — Hintergrund der Folgeseiten. Fallback: ist dieses Feld nicht
   * gesetzt, wird stationeryPage1 fuer ALLE Seiten verwendet.
   */
  stationeryFollowing?: string;
  /** mm, Inhaltsbeginn Seite 1 (default 50). */
  stationeryTopMarginPage1?: number;
  /** mm, Inhaltsbeginn Folgeseiten (default 25). */
  stationeryTopMarginFollowing?: number;
  /** mm, unterer Rand auf allen Seiten (default 25). */
  stationeryBottomMargin?: number;
}

// Die geerbten Briefkopf-Felder sind der ALTBESTAND: neu gepflegt wird der
// Briefkopf in settings.letterheadConfig; services/letterheadConfig.ts
// mischt ihn beim Lesen hierher. Direkt aus settings.quoteConfig gelesen
// tragen die Felder nur noch Werte von vor der Umstellung.
export interface QuoteConfig extends LetterheadConfig {
  followUpDays: number;
  defaultValidityDays: number;
  defaultTaxRate: number;
  defaultPaymentTerms?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Rechnungsmodul
//
// Geldbetraege sind AUSNAHMSLOS ganzzahlige Cent (siehe services/money.ts).
// Kein Float, kein bigint: bigint ueberlebt JSON.stringify nicht, und
// Rechnungszeilen liegen als jsonb in der Datenbank.
//
// Steuersaetze sind Basispunkte (19 % = 1900), damit Steuergruppen ueber
// Ganzzahlen gruppiert werden und 19.00 !== 19 nicht zwei Gruppen erzeugt.
// ═══════════════════════════════════════════════════════════════════════════

/** Belegart. Bestimmt Titel, Nummernkreis, Steuerlogik und EN16931-Typcode. */
export type DocumentType =
    | 'INVOICE'                   // normale Rechnung
    | 'ADVANCE_INVOICE'           // Abschlagsrechnung
    | 'FINAL_INVOICE'             // Schlussrechnung (mit Abschlagsabzug)
    | 'REMAINING_INVOICE'         // Restrechnung
    | 'CANCELLATION_INVOICE'      // Stornorechnung (Vollstorno)
    | 'CORRECTION_INVOICE'        // berichtigte Rechnung
    | 'COMMERCIAL_CREDIT_INVOICE' // kaufmaennische Gutschrift / Minderungsbeleg
    | 'SELF_BILLING_CREDIT_NOTE'; // Gutschrift im umsatzsteuerlichen Sinn

export type InvoiceStatus =
    | 'DRAFT'
    | 'READY_FOR_REVIEW'
    | 'APPROVED'
    /**
     * Zwischenzustand waehrend der Ausstellung.
     *
     * Noetig, weil ein Claim, der APPROVED setzt UND auf APPROVED filtert, unter
     * READ COMMITTED von zwei parallelen Aufrufern gleichzeitig gewonnen wird:
     * die zweite Transaktion wertet ihre Bedingung nach dem Commit der ersten
     * neu aus und findet den Status unveraendert vor. APPROVED -> ISSUING ist
     * dagegen eine echte Aenderung und passt nur einmal.
     */
    | 'ISSUING'
    | 'ISSUED'
    | 'SENT'
    | 'PARTIALLY_PAID'
    | 'PAID'
    | 'PARTIALLY_CREDITED'
    | 'CREDITED'
    | 'REFUND_PENDING'
    | 'REFUNDED'
    | 'DISPUTED'
    | 'OVERDUE'
    | 'SUPERSEDED_BY_FINAL_INVOICE'
    | 'CANCELLED';

/** Nummernkreis-Kuerzel. Welches greift, entscheidet InvoiceNumberingConfig. */
export type InvoiceSeries = 'RE' | 'AR' | 'SR' | 'RR' | 'ST' | 'KR' | 'GS' | 'GA';

/** Zuordnung Belegart -> eigener Kreis. Gilt nur im Modus 'per_type'. */
export const SERIES_FOR_DOCUMENT_TYPE: Record<DocumentType, InvoiceSeries> = {
    INVOICE: 'RE',
    ADVANCE_INVOICE: 'AR',
    FINAL_INVOICE: 'SR',
    REMAINING_INVOICE: 'RR',
    CANCELLATION_INVOICE: 'ST',
    CORRECTION_INVOICE: 'KR',
    COMMERCIAL_CREDIT_INVOICE: 'GS',
    SELF_BILLING_CREDIT_NOTE: 'GA',
};

/**
 * EN16931 BT-3 Dokumententyp-Code fuer die E-Rechnung.
 * 380 Rechnung, 381 Gutschrift, 384 berichtigte Rechnung, 389 Selbstabrechnung.
 */
export const EN16931_TYPE_CODE: Record<DocumentType, '380' | '381' | '384' | '389'> = {
    INVOICE: '380',
    ADVANCE_INVOICE: '380',
    FINAL_INVOICE: '380',
    REMAINING_INVOICE: '380',
    CORRECTION_INVOICE: '384',
    CANCELLATION_INVOICE: '381',
    COMMERCIAL_CREDIT_INVOICE: '381',
    SELF_BILLING_CREDIT_NOTE: '389',
};

/** Deutsche Belegbezeichnung — Titel im PDF und in der Oberflaeche. */
export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
    INVOICE: 'Rechnung',
    ADVANCE_INVOICE: 'Abschlagsrechnung',
    FINAL_INVOICE: 'Schlussrechnung',
    REMAINING_INVOICE: 'Restrechnung',
    CANCELLATION_INVOICE: 'Stornorechnung',
    CORRECTION_INVOICE: 'Rechnungskorrektur',
    COMMERCIAL_CREDIT_INVOICE: 'Rechnungskorrektur',
    SELF_BILLING_CREDIT_NOTE: 'Gutschrift',
};

/**
 * EN16931 BT-151 Steuerkategorie.
 * S Regelsatz, Z Nullsatz, E steuerbefreit, AE Reverse Charge,
 * K innergemeinschaftlich, G Ausfuhr, O nicht steuerbar.
 */
export type TaxCategoryCode = 'S' | 'Z' | 'E' | 'AE' | 'K' | 'G' | 'O';

export type InvoiceLineKind =
    | 'SECTION'            // Zwischenueberschrift (z.B. Projekt in der Sammelrechnung)
    | 'SERVICE'            // abzurechnende Leistung
    | 'TEXT'               // reiner Erlaeuterungstext
    | 'ADVANCE_DEDUCTION'  // Abzug einer vereinnahmten Abschlagszahlung
    | 'DISCOUNT'
    | 'SURCHARGE';

export interface InvoiceLine {
    id: string;
    kind: InvoiceLineKind;
    posNumber?: string;
    /**
     * Positionsnummer und Text wurden im Beleg von Hand gesetzt.
     *
     * Beides gilt NUR fuer diesen Beleg — das Projekt-LV bleibt unberuehrt.
     * Die Merker sind noetig, weil beide Werte sonst automatisch neu gesetzt
     * wuerden: nextInvoicePosNumbers nummeriert bei jedem Speichern durch, und
     * der Positionsaufbau holt den Titel aus dem Projekt zurueck. Ohne sie
     * verschwaende eine Aenderung beim naechsten Autosave oder spaetestens
     * beim naechsten Neuaufbau (keepManualLineEdits).
     */
    posNumberManual?: boolean;
    titleManual?: boolean;

    // ── Herkunft im Projekt-LV ────────────────────────────────────────────
    // Traegt gleichzeitig die Rueckschreibung beim Ausstellen UND die
    // Reservierung (v_invoice_line_reservations), die einen zweiten
    // Rechnungslauf daran hindert, dieselbe Menge erneut zu ziehen.
    projectId?: string;
    projectNumber?: string;
    projectName?: string;
    serviceId?: string;
    lvPosition?: string;
    clientLvPosition?: string;

    title: string;
    description?: string;
    unit?: string;
    /** Menge mit bis zu 3 Nachkommastellen — keine Geldgroesse. */
    quantity?: number;
    /**
     * Anteil, mit dem diese Zeile aus einer geteilten Position hervorgegangen
     * ist (5000 = 50,00 %). Leer bei ungeteilten Belegen.
     */
    splitShareBp?: number;
    /**
     * Die ungeteilte Menge der Position — fuer den Andruck
     * ("4,00 h (Anteil 50 % von 8,00 h)").
     *
     * Gespeichert und nicht zurueckgerechnet: der Beleg ist eingefroren, der
     * Leistungsstand des Projekts laeuft weiter. Aus Menge und Anteil spaeter
     * zurueckzurechnen ergaebe den Stand von HEUTE, nicht den, den dieser Beleg
     * abgerechnet hat.
     */
    splitFullQuantity?: number;
    unitPriceCents?: number;
    /**
     * Nachlass auf DIESE Position in Prozent (10 = 10 %).
     *
     * Wirkt an genau fuenf Stellen, und nirgends sonst:
     *   - computeLineNet (invoiceLogic): netto = Menge x Einzelpreis x (1 - p/100),
     *     erst multiplizieren, dann EINMAL runden.
     *   - buildInvoiceLine (ublInvoice): BT-138 MultiplierFactorNumeric der
     *     cac:AllowanceCharge; der Betrag selbst ist die Differenz zum
     *     eingefrorenen Netto, nicht neu gerechnet.
     *   - buildLineItem (ciiInvoice): ram:CalculationPercent, ebenso.
     *   - generatorInvoice: der Nachlassvermerk unter der Leistungsbezeichnung,
     *     damit der Empfaenger Menge x EP abzueglich Nachlass nachrechnen kann.
     *   - uncoveredPriorBilling (invoiceLogic): bewertet die ungedeckte
     *     TEILmenge zu den Konditionen dieser Zeile.
     *
     * Der Einzelpreis bleibt der vereinbarte Preis und wird NICHT rabattiert
     * abgelegt: er ist die Groesse, an der Projekt-LV, Leistungsnachweis und
     * Deckungspruefung messen, und ein gerundeter rabattierter Einzelpreis mal
     * Menge ergibt je nach Menge einen anderen Betrag als das Zeilennetto.
     */
    discountPercent?: number;
    /**
     * Fester Nachlass auf DIESE Position in Cent, positiv als Minderung
     * (5000 = 50,00 EUR weniger).
     *
     * Eigenes Feld und nicht in discountPercent umgerechnet: der Prozentsatz
     * landet mit zwei Nachkommastellen in BT-138 bzw. ram:CalculationPercent.
     * Ein umgerechneter Satz (50,00 EUR von 1.035,00 EUR = 4,83091787 %) stuende
     * dort als "4.83" und erklaerte den ausgewiesenen Nachlassbetrag nicht mehr
     * — der Empfaenger bekaeme zwei Zahlen, die nicht zueinander passen. Zudem
     * ginge die vereinbarte Groesse ("50 EUR Nachlass") verloren.
     *
     * SCHLIESST discountPercent AUS: ist dieses Feld gesetzt und ungleich 0,
     * rechnet computeLineNet ausschliesslich damit und sieht den Prozentsatz
     * nicht an. Der Editor leert deshalb beim Setzen des einen immer das
     * andere. Beides zugleich zu verrechnen waere in der E-Rechnung nicht mehr
     * darstellbar: dort erklaert ENTWEDER ein Prozentsatz den Nachlassbetrag
     * oder gar keiner.
     *
     * Wirkt an drei Stellen: computeLineNet (netto = Menge x Einzelpreis
     * abzueglich Betrag), generatorInvoice (Nachlassvermerk) und
     * uncoveredPriorBilling (dort in seinen Prozentsatz umgerechnet, weil die
     * Bewertung eine TEILmenge betrifft — effectiveLineDiscountPercent).
     * UBL und CII brauchen es nicht: beide leiten den Nachlassbetrag als
     * Differenz zwischen Menge x Preis und dem eingefrorenen Netto ab und
     * geben den Prozentsatz nur mit, wenn es einen gibt.
     */
    discountAmountCents?: number;

    taxRateBp: number;
    taxCategoryCode?: TaxCategoryCode;
    /** Pflicht, sobald taxRateBp === 0 (EN16931 BT-120/BT-121). */
    taxExemptionReason?: string;

    /**
     * Zeilennetto, beim Ausstellen eingefroren. Wird NICHT zur Anzeigezeit neu
     * berechnet — sonst weicht ein spaeter geoeffneter Beleg vom archivierten
     * PDF ab.
     */
    netCents: number;
    /**
     * netCents ist massgeblich und darf NICHT aus Menge x Einzelpreis neu
     * gerechnet werden.
     *
     * Gesetzt bei Korrektur- und Gutschriftzeilen: dort ist der Betrag eine
     * DIFFERENZ, die der Nutzer eingibt, waehrend Menge und Einzelpreis die
     * urspruengliche Position benennen. Ohne diesen Schalter ueberschreibt
     * recalculateLines die Differenz mit Menge x Einzelpreis — aus einer
     * Minderung um 60,00 EUR wird dann eine Forderung ueber 360,00 EUR.
     */
    netCentsExplicit?: boolean;

    /**
     * Menge, Einheit und Einzelpreis dieser Zeile benennen nur die
     * urspruengliche Position — sie sind KEINE abzurechnende Menge.
     *
     * Gesetzt von buildCorrectionLines, wenn der Bearbeiter ausschliesslich den
     * BETRAG berichtigt (Menge unveraendert). Der Beleg muss die Position
     * weiterhin bezeichnen, damit der Empfaenger sie wiederfindet; buchen darf
     * er dabei nichts.
     *
     * Ohne diesen Schalter trug die Differenzzeile die VOLLE Positionsmenge
     * positiv: die Rueckschreibung buchte sie ein zweites Mal (20 abgerechnet
     * wurden 40), und wo nichts mehr offen war, scheiterte die Ausstellung an
     * der Ueberbuchungsschranke — eine reine Betragskorrektur liess sich gar
     * nicht ausstellen.
     *
     * Wirkt in genau drei Rechnungen: aggregateLinesByService (bucht nichts),
     * planAllocations (verbraucht keine Protokolleintraege) und
     * onlyReleasesProjectQuantity (zaehlt nicht als beanspruchte Menge). Anzeige
     * und E-Rechnung lesen die Menge unveraendert weiter — sie steht ja auf dem
     * Beleg.
     */
    quantityInformational?: boolean;

    /**
     * Vom System erzeugte Zeile, nicht vom Bearbeiter eingetragen.
     *
     * 'ANCILLARY' = prozentualer Nebenkostenzuschlag auf die Honorarsumme.
     * 'DISCOUNT'  = Nachlass auf den GANZEN Beleg (details.discountPercent bzw.
     *               details.discountAmountCents), je Steuersatz eine Zeile.
     *
     * Solche Zeilen werden bei jeder Aenderung der Positionen verworfen und neu
     * gebildet — sonst bliebe der Zuschlag auf dem Betrag stehen, der vor der
     * Aenderung galt, und niemand saehe es dem Beleg an.
     *
     * Die Reihenfolge ist tragend: erst der Nachlass, dann der Zuschlag. Der
     * Zuschlag rechnet auf die Honorarsumme ABZUEGLICH Nachlaesse; andersherum
     * rechnete er gegen einen veralteten Nachlass.
     *
     * Nach der Festschreibung wird nichts mehr neu gebildet: ab da sind die
     * Zeilen eingefroren.
     */
    /**
     * Abschnitt, dem diese Position auf DIESEM Beleg zugeordnet ist
     * (InvoiceSection.id in details.sections).
     *
     * Die Zuordnung ist die einzige gespeicherte Wahrheit ueber die Gliederung;
     * die Ueberschriftszeilen (kind 'SECTION') werden daraus abgeleitet
     * (invoiceLogic::applyInvoiceSections). Zwei Quellen — Reihenfolge im Array
     * UND Zuordnung — liefen sonst auseinander, sobald der Positionsaufbau die
     * LV-Zeilen neu erzeugt.
     *
     * Steht auch auf der abgeleiteten Ueberschriftszeile selbst: daran erkennt
     * der Neuaufbau seine eigenen Ueberschriften und laesst fremde in Ruhe (die
     * Sammelrechnung setzt je Projekt eine, billingRunLogic).
     *
     * Leer heisst "ohne Zuordnung" — diese Zeilen stehen vor dem ersten
     * Abschnitt, genau wie bisher.
     */
    sectionId?: string;

    generated?: 'ANCILLARY' | 'DISCOUNT';

    /**
     * Welche Eintraege des Leistungsprotokolls diese Zeile abrechnet.
     *
     * Ohne diese Angabe merkt sich das Leistungsverzeichnis nur eine Summe
     * (quantityBilled) und NICHT, welche Leistung damit bezahlt ist. Jede
     * datumsbezogene Aussage muesste dann raten — welcher Monat noch offen
     * ist, ob ein Zeitraumfilter eine bereits abgerechnete Leistung erneut
     * anbietet, was eine kumulative Rechnung zum Stichtag zeigt.
     *
     * Entschieden wird beim AUSSTELLEN, wo Zeitraum und Mengen endgueltig
     * feststehen, und danach nie wieder: die Spalte lines ist ab da vom
     * Trigger invoices_freeze unveraenderlich. Deshalb steht die Zuordnung
     * hier und nicht in einer eigenen Tabelle — die muesste dieselbe
     * Unveraenderlichkeit erst durch Constraints herstellen.
     */
    allocations?: InvoiceLineAllocation[];

    /**
     * Menge dieser Leistung, die zum Zeitpunkt des Aufbaus bereits abgerechnet
     * war — nur bei kumulativer Abrechnung gesetzt.
     *
     * Dort nennt die Zeile den GESAMTEN Leistungsstand, und wie viel davon neu
     * ist, sieht man ihr nicht an: "7 Stueck" koennen eine neue Begehung plus
     * sechs alte sein. Summe und Abzug des Belegs rechnen unabhaengig davon.
     *
     * Er ist damit aber die einzige Stelle, an der der fertige Beleg selbst
     * sagt, wieviel Vorleistung er voraussetzt — darauf bauen die
     * Mengenreservierung (invoiceWriteBackLogic) und die Sperre
     * `prior_billing_not_deducted` (invoiceLogic: uncoveredPriorBilling) auf.
     * Wer ihn nicht mehr setzt, nimmt beiden die Grundlage.
     *
     * Er sagt NICHT, dass es zu dieser Menge einen Beleg gibt — das sagt
     * previouslyInvoicedQty.
     */
    previouslyBilledQty?: number;

    /**
     * Menge dieser Leistung, fuer die es beim Aufbau des Belegs tatsaechlich
     * einen abzugsfaehigen Beleg gab — nur bei kumulativer Abrechnung gesetzt.
     *
     * Das GEGENSTUECK zu previouslyBilledQty, und der Grund, warum es sie
     * braucht: previouslyBilledQty sagt nur, was das Leistungsverzeichnis als
     * abgerechnet fuehrt. WOMIT es abgerechnet wurde, sagt es nicht. Bei
     * Bestandsprojekten stammt ein Teil davon aus dem Altsystem und existiert
     * im System als gar nichts — genau der Schaden von Vorfall 6172, wo 213
     * Tagespauschalen als abgerechnet gefuehrt wurden und nur 22 davon auf
     * einem Beleg standen.
     *
     * Zusammengezaehlt aus zwei Quellen, beide in MENGE (invoiceService,
     * buildProjectServiceLines):
     *   - was die abzugsfaehigen Vorbelege des Projekts beansprucht haben
     *     (advanceInvoiceLogic: invoicedQuantityByService — vorzeichenrichtig,
     *     eine stornierte Rechnung deckt nichts mehr)
     *   - was als Alt-Rechnung nachgetragen wurde (externalAdvances:
     *     altCoverageByService, die 'ALT-'-Protokollzeilen)
     *
     * Die Differenz previouslyBilledQty - previouslyInvoicedQty ist die Menge
     * ohne Beleg; uncoveredPriorBilling (invoiceLogic) macht daraus die Sperre
     * `prior_billing_not_deducted`.
     *
     * FEHLT das Feld, heisst das "nicht ermittelt", NICHT "null gedeckt": so
     * sehen Entwuerfe von vor dieser Pruefung aus, und ebenso ein Aufbau, dem
     * die Belegabfrage weggebrochen ist. Der Beleg wird dann nicht gesperrt,
     * sondern gewarnt (`prior_billing_coverage_unknown`). Ein fehlendes Feld
     * als 0 zu lesen sperrte jeden Altentwurf mit Vorrechnung.
     */
    previouslyInvoicedQty?: number;

    /**
     * Die Position ist durch eine Monatspauschale gedeckt (ServiceItem.flatCoveredBy
     * beim Aufbau der Zeile, siehe projectLv.stampFlatCoverage).
     *
     * Kumulativ steht so eine Zeile mit ihrem eingefrorenen Geldstand auf dem
     * Beleg — sie fordert nichts Neues, darf aber nicht fehlen (der Abzug der
     * Vorrechnungen rechnet je Beleg, nicht je Leistung). Der Merker erlaubt
     * dem Andruck, diese Zeilen zusammenzufassen (details.flatRateDisplay),
     * ohne den Beleg selbst anzufassen. Ein Storno spiegelt ihn mit.
     */
    flatCovered?: boolean;

    // ── nur bei kind === 'ADVANCE_DEDUCTION' ──────────────────────────────
    advanceInvoiceId?: string;
    advanceInvoiceNumber?: string;
    advanceInvoiceDate?: string;
}

/**
 * Ein abgerechneter Anteil eines einzelnen Protokolleintrags.
 *
 * Ein Eintrag kann auf mehrere Rechnungen aufgeteilt sein (drei Begehungen
 * erfasst, zwei abgerechnet), deshalb eine Teilmenge und kein blosses
 * Kennzeichen am Eintrag. Genau daran scheitert der naheliegende Weg, den
 * Arbeitseintrag selbst zu stempeln — und ein Stempel mit dem Praefix "BU-"
 * liesse ihn ausserdem aus Begehungszaehlern, Leistungsnachweis und
 * Projekthistorie verschwinden.
 */
export interface InvoiceLineAllocation {
    /** id des ServiceTransaction im Leistungsprotokoll des Projekts. */
    entryId: string;
    /** Abgerechnete Teilmenge. Negativ bei Storno und Gutschrift. */
    quantity: number;
    /**
     * Leistungsdatum des Eintrags, mitgeschrieben.
     *
     * Damit beantwortet der Beleg selbst, welchen Monat er abgerechnet hat —
     * ohne das Projekt zu laden, und unabhaengig davon, ob der Eintrag dort
     * spaeter noch existiert.
     */
    date?: string;
}

/** Einzelaufstellung oder Zusammenfassung der Abschlaege in der Schlussrechnung. */
export type AdvanceDeductionMode = 'INDIVIDUAL' | 'GROUPED';

/** Andruck gedeckter Positionen bei Monatspauschale — siehe InvoiceDetails.flatRateDisplay. */
export type FlatRateDisplayMode = 'ALL' | 'FLAT_ONLY';

/**
 * Vom Bearbeiter gesetzte Nummerierung der abgezogenen Abschlaege.
 *
 * Alle drei Felder sind freiwillig; fehlt alles, zaehlt der Beleg ab 1 bis zur
 * Zahl der abgezogenen Abschlagsrechnungen. Siehe InvoiceDetails.advanceNumbering.
 */
export interface AdvanceNumbering {
    from?: number;
    to?: number;
    hidden?: boolean;
}

/**
 * Ein abzuziehender Abschlag. Massgeblich ist die tatsaechlich VEREINNAHMTE
 * Zahlung, nicht die ausgestellte Abschlagsrechnung (Spezifikation §12.1).
 * Betraege positiv gespeichert, im Beleg negativ dargestellt.
 */
/**
 * Extern geschriebener Alt-Abschlag eines Bestandsprojekts
 * (public.external_advances).
 *
 * KEIN Beleg: kein Nummernkreis, kein Snapshot, keine offenen Posten. Nur
 * Abzugsposten der kumulativen Abrechnung — die Originale liegen im
 * Altsystem. In AdvanceDeduction erscheint er mit der Quell-ID
 * 'ext:<id>'; die Ausstellungs-Pfade filtern dieses Praefix vor jedem
 * Zugriff auf public.invoices (FK/Statuspruefung/Verknuepfung).
 */
export interface ExternalAdvance {
    id: string;
    projectId: string;
    /** Nummer des Altbelegs — Pflicht, sonst weist validateAdvanceChain ab. */
    invoiceNumber: string;
    /** yyyy-mm-dd; darf fehlen (Abzugsblock alter Rechnungen nennt oft keins). */
    invoiceDate?: string;
    /**
     * Auftraggeber des Altbelegs. Leer = Altbestand vor der Aufteilung.
     *
     * Tragend beim geteilten Projekt: der Abzug der Vorrechnungen ist je
     * Auftraggeber zu bilden. Zoege eine Teilrechnung auch die Abschlaege der
     * uebrigen Auftraggeber ab, forderte sie fast nichts mehr — und der Fehler
     * saehe auf dem Beleg richtig aus.
     *
     * Ein Altbeleg OHNE Kunden gilt weiterhin fuer alle: er stammt aus der
     * Zeit vor der Aufteilung, als es nur einen Auftraggeber gab.
     */
    customerId?: string;
    /**
     * ABSCHNITTE: der Teilnehmer, dessen Abschlag das war
     * (project_splits.id). Feiner als `customerId`.
     *
     * Gebraucht, sobald ein Auftraggeber mehrere Abschnitte hat: der Abzug ist
     * dann je ABSCHNITT zu bilden. Ueber den Auftraggeber gerechnet zoege die
     * Rechnung fuer Haus B auch die Abschlaege von Haus A ab.
     *
     * Leer heisst weiterhin "vor der Aufteilung" und wird ueber `customerId`
     * aufgeloest, solange das eindeutig ist. Ist es das nicht, sperrt das
     * Ausstellen — ein Abzugsposten, der sich nur raten liesse, ist ein
     * Geldbetrag.
     */
    splitPartyId?: string;
    grossCents: number;
    taxRateBp: number;
    note?: string;
}

export interface AdvanceDeduction {
    advanceInvoiceId: string;
    invoiceNumber: string;
    invoiceDate: string;
    /** Datum des Zahlungseingangs, falls bekannt — gehoert in die Einzelaufstellung. */
    paymentDate?: string;
    taxRateBp: number;
    netCents: number;
    taxCents: number;
    grossCents: number;
}

/**
 * Zusammengefasste Darstellung — gruppiert je STEUERSATZ, nicht in eine einzige
 * Zeile. Eine gesammelte "abzueglich Abschlaege"-Zeile waere bei mehreren
 * Saetzen nicht §14-UStG-konform.
 */
export interface AdvanceDeductionGroup {
    taxRateBp: number;
    netCents: number;
    taxCents: number;
    grossCents: number;
    sourceInvoiceIds: string[];
    sourceInvoiceNumbers: string[];
}

/**
 * Der auf einem ABGEZOGENEN Vorbeleg einbehaltene Sicherheitseinbehalt — je
 * Vorbeleg ein Eintrag, wie AdvanceDeduction fuer den Abschlagsabzug.
 *
 * Kumulatives Modell (VOB-Muster): der Einbehalt wird auf den Leistungsstand
 * gerechnet und um das bereits Einbehaltene der Vorbelege gekuerzt —
 *   delta = round(pct x gross) - sum(priorRetentions)
 * Positiv wird mit diesem Beleg neu einbehalten, negativ freigegeben. Auf der
 * Schlussrechnung (Satz 0) ist diese Liste damit die Freigabeliste.
 *
 * Ohne Steueraufteilung: der Einbehalt ist keine Entgeltminderung, sondern nur
 * ein zurueckgehaltener Teil des Zahlbetrags. Die Steuer ist auf dem Vorbeleg
 * bereits vollstaendig ausgewiesen und entstanden (§ 14 UStG); eine
 * Aufteilung in Netto und Steuer waere erfunden.
 */
export interface PriorRetention {
    /**
     * Die advanceInvoiceId des Abzugs: Beleg-Uuid oder `ext:<id>` fuer einen
     * externen Alt-Abschlag (externalAdvances). Fuer `ext:` gibt es keinen
     * Beleg im System — sein Einbehalt kommt nur aus dem Backfill.
     */
    sourceId: string;
    invoiceNumber: string;
    invoiceDate: string;
    /**
     * Netto auf dem Vorbeleg einbehalten (retention - release), positiv.
     * Negativ bei Storno/Korrektur (Spiegelung) und bei Belegen, die selbst
     * freigegeben haben.
     */
    retentionCents: number;
    /**
     * Der Betrag stammt ganz oder teilweise aus invoice_retention_backfill —
     * ein von Hand nachgetragener Alt-Einbehalt, den der Vorbeleg selbst nicht
     * traegt (Altbestand vor dem Feature, Satzwechsel).
     */
    backfilled?: boolean;
}

/**
 * Manuell erfasster Alt-Einbehalt (Tabelle invoice_retention_backfill, Nr. 107).
 *
 * Fuer Vorbelege, die vor dem Feature ausgestellt wurden oder aus anderen
 * Gruenden keinen Einbehalt tragen, obwohl der Auftraggeber einen abgezogen
 * hat. Ohne diesen Eintrag hielte das kumulative Modell auf dem naechsten Beleg
 * den vollen Satz auf den Leistungsstand ein — der Auftraggeber zahlte den
 * Einbehalt zum zweiten Mal.
 */
export interface RetentionBackfill {
    id: string;
    projectId: string;
    /** advanceInvoiceId des Abzugs, dem der Betrag zugeordnet ist (Beleg-Uuid oder `ext:<id>`). */
    sourceRef: string;
    invoiceNumber: string;
    invoiceDate?: string;
    /** Auf dem Vorbeleg einbehalten, positiv. */
    retentionCents: number;
    note?: string;
}

/** Steuerausweis je Satz. Die Steuer wird aus der Gruppensumme berechnet, nie je Position. */
export interface TaxGroup {
    taxRateBp: number;
    taxCategoryCode: TaxCategoryCode;
    taxableNetCents: number;
    taxCents: number;
    taxExemptionReason?: string;
}

export interface InvoiceTotals {
    /** Summe der Leistungszeilen vor Abschlagsabzug (EN16931 BT-109). */
    netTotalCents: number;
    /** Summe der Steuer ueber alle Gruppen (BT-110). */
    taxTotalCents: number;
    /** netTotal + taxTotal (BT-112). */
    grossTotalCents: number;
    /** Bereits vereinnahmt, also abgezogen (BT-113). */
    prepaidCents: number;
    /**
     * Sicherheitseinbehalt, kumulativ (VOB-Muster) — die eine Formel fuer
     * Abschlag, Schlussrechnung, Satzwechsel und Nachholung:
     *
     *   cumulative = round(pct x gross)             auf den LEISTUNGSSTAND
     *   prior      = sum(priorRetentions)           auf den Vorbelegen einbehalten
     *   delta      = cumulative - prior
     *   retention  = max(0, delta), release = max(0, -delta)
     *   payable    = gross - prepaid - retention + release
     *
     * Der Einbehalt mindert AUSSCHLIESSLICH den Zahlbetrag — Netto, Steuer und
     * Brutto bleiben vollstaendig ausgewiesen (§ 14 UStG; keine
     * Entgeltminderung, kein Nachlass).
     *
     * Bemessungsgrundlage ist der Leistungsstand (gross) und nicht die
     * Bruttoforderung dieses Belegs: so ist der Gesamteinbehalt eines Projekts
     * nach jedem Beleg exakt round(pct x Leistungsstand), auch wenn ein
     * Vorbeleg keinen Einbehalt trug (Altbestand, Satzwechsel) — das wird mit
     * dem naechsten Beleg nachgeholt statt fuer immer vergessen.
     *
     * Alle fuenf Felder optional, weil Altbestand in der Datenbank sie nicht
     * traegt; Leser rechnen `?? 0`.
     */
    /** round(pct x gross): was auf den Leistungsstand insgesamt einzubehalten ist. */
    cumulativeRetentionCents?: number;
    /** Summe der PriorRetention-Liste: netto bereits auf den abgezogenen Vorbelegen einbehalten. */
    priorRetentionCents?: number;
    /** Mit diesem Beleg neu einbehalten (max(0, cumulative - prior)), positiv als Minderung. */
    retentionCents?: number;
    /** Mit diesem Beleg freigegeben (max(0, prior - cumulative)), positiv — Schlussrechnung mit Satz 0. */
    retentionReleaseCents?: number;
    /**
     * prepaid - prior: die tatsaechlich erhaltenen Abschlagszahlungen. Die
     * Vorbelege forderten prepaid, gezahlt wurde davon nur der Teil ohne
     * Einbehalt — diese Zahl gehoert auf die Zeile "abzueglich erhaltener
     * Abschlagszahlungen", nicht prepaid.
     */
    paidPriorCents?: number;
    /** Tatsaechlicher Zahlbetrag: gross - prepaid - retention + retentionRelease. */
    payableCents: number;
    taxGroups: TaxGroup[];
}

/** Firmendaten zum Zeitpunkt der Ausstellung — historisiert, nie nachgefuehrt. */
export interface InvoiceCompanySnapshot {
    name: string;
    address: string;
    vatId?: string;
    taxNumber?: string;
    registerCourt?: string;
    registerNumber?: string;
    managingDirectors?: string[];
    iban?: string;
    bic?: string;
    bankName?: string;
    /** Kontoinhaber zum Ausstellungszeitpunkt — eingefroren wie die IBAN. */
    accountHolder?: string;
    email?: string;
    phone?: string;
    /** Fingerabdruck des verwendeten Briefpapiers (Hash, nicht das Bild). */
    stationeryHash?: string;
}

export type ProjectCheckTraffic = 'green' | 'yellow' | 'red';

/**
 * Projektbezug eines Belegs: Nummer und Name, wie sie in Belegliste,
 * Rechnungsmaske und PDF erscheinen.
 *
 * Beides zusammen, weil beides gebraucht wird — die Nummer ist der Schluessel,
 * unter dem im Betrieb gesprochen wird, der Name das Einzige, woran ein
 * Auftraggeber die Baustelle wiedererkennt.
 */
export interface InvoiceProjectRef {
    id?: string;
    number?: string;
    name?: string;
}

/**
 * Vorlage fuer die BEZEICHNUNG einer Kostenstellenzeile — der Text, der links
 * auf dem Beleg steht: "Kostenstelle", "PSP-Element", "Innenauftrag".
 *
 * Bewusst NUR die Bezeichnung. Der Wert dahinter ("4711-02") ist je Beleg
 * verschieden und waere als Vorlage wertlos; wiederkehrend ist ausschliesslich,
 * WIE der Auftraggeber diese Angabe nennt. Damit der Wert trotzdem nicht bei
 * jedem Beleg neu getippt werden muss, uebernimmt ein neuer Entwurf die zuletzt
 * verwendete Kombination aus Bezeichnung UND Wert (invoiceCostCenters.ts:
 * fetchLastUsedInvoiceDefault).
 *
 * Getrennt von TextTemplate, weil die dortige Tabelle einen Kategorie-CHECK mit
 * fester Liste traegt und am Angebotseditor haengt (Tabelle
 * invoice_cost_centers).
 */
export interface InvoiceCostCenter {
    id: string;
    name: string;
}

/**
 * Frei benannter Abschnitt eines Belegs ("Haus 3", "Bauabschnitt Nord").
 *
 * Reihenfolge in details.sections = Reihenfolge auf dem Beleg. Die Gliederung
 * gilt NUR fuer diesen Beleg und ruehrt das Projekt-LV nicht an — dieselbe
 * Regel wie fuer handgesetzte Positionsnummern und Bezeichnungen.
 */
export interface InvoiceSection {
    id: string;
    title: string;
}

export interface InvoiceDetails {
    introText?: string;
    outroText?: string;
    paymentTerms?: string;
    /**
     * Ueberschrift des Belegs, falls abweichend vom Standard (der Belegart).
     *
     * Wird von Storno, Korrektur und Gutschrift gesetzt (Bezug auf den
     * Ursprungsbeleg) und laesst sich in der Maske frei eingeben, auch
     * uebernommen aus dem Angebot. Verdraengt die Standardueberschrift
     * VOLLSTAENDIG: der Beleg traegt genau EINE Titelzeile, und es ist diese.
     * Die Belegart bleibt ueber die Nummernzeile im rechten Kopfblock ablesbar,
     * der Entwurfsvermerk wandert an die eigene Ueberschrift (invoiceHeading in
     * invoiceLogic.ts).
     */
    headerText?: string;

    /**
     * Abweichende UEBERSCHRIFT des Abschlagsabzugs — die fette Zeile ueber den
     * Abzugszeilen ("Abzüglich erhaltener Abschlagszahlungen (Abschläge 1–4)").
     *
     * Der Standardtext ist gerechnet: Rueckbelastung statt Abzug, wenn die
     * Abzuege negativ sind, und die Spanne der laufenden Nummern in der
     * zusammengefassten Ansicht (advanceDeductionHeadingLabel). Die Rechnung
     * kann aber Abschlaege abziehen, die NICHT bei 1 anfangen oder die aus
     * einem Altbestand stammen — dann ist die gerechnete Spanne zwar richtig
     * gezaehlt, aber nicht das, was der Auftraggeber lesen soll.
     *
     * Reine Anzeigeangabe: sie ersetzt die Ueberschriftszeile VOLLSTAENDIG und
     * ruehrt weder die Abzugszeilen noch die Betraege noch die E-Rechnung an —
     * dort taucht kein Ueberschriftstext auf. Der abschliessende Doppelpunkt
     * wird gesetzt, nicht eingegeben.
     *
     * Leer heisst: der gerechnete Standardtext. Liegt in details und friert
     * damit beim Ausstellen ein. Storno, Korrektur und Gutschrift erben sie
     * bewusst NICHT — dort dreht sich das Vorzeichen und der Standardtext ist
     * ein anderer ("Rückbelastung erhaltener Abschlagszahlungen").
     */
    advanceDeductionHeading?: string;

    /**
     * Vorgabe fuer die NUMMERIERUNG der abgezogenen Abschlaege.
     *
     * Die laufenden Nummern stehen an drei Stellen des Belegs, und sie muessen
     * zueinander stimmen: in der Ueberschrift ("(Abschläge 1–2)"), vor jeder
     * Einzelzeile ("1. Abschlag — AR-2026-0001 vom …") und in der Summenzeile
     * ("Abschläge 1–2 gesamt"). Gerechnet zaehlt die Anwendung die Belege, die
     * DIESE Rechnung abzieht, ab 1 durch.
     *
     * Das ist nicht immer die Zaehlung des Auftraggebers: Abschlaege aus einem
     * Altsystem, aus einem Vorprojekt oder aus einer bereits abgerechneten
     * Staffel fangen nicht bei 1 an, und die Rechnung zieht mitunter nur einen
     * Ausschnitt einer laengeren Kette ab.
     *
     * - `from` — Nummer des ERSTEN hier abgezogenen Abschlags (Vorgabe 1). Die
     *   Einzelzeilen zaehlen ab hier weiter.
     * - `to` — Nummer, mit der die Spanne ENDET. Frei, weil die Kette laenger
     *   sein kann als das, was dieser Beleg auffuehrt: "Abschläge 1–6" bei zwei
     *   aufgefuehrten Zeilen ist eine zulaessige Aussage ueber die Kette.
     *   Fehlt sie, endet die Spanne beim letzten aufgefuehrten Abschlag.
     * - `hidden` — gar keine Nummern: keine Spanne in der Ueberschrift, keine
     *   fuehrende Nummer an den Zeilen, und die Summenzeile heisst schlicht
     *   "Abschlagszahlungen gesamt". Belegnummer und Datum tragen die
     *   Zuordnung dann allein.
     *
     * Reine Anzeigeangabe: welche Belege abgezogen werden und mit welchem
     * Betrag, aendert sich dadurch NIE — das entscheiden die Abzuege selbst.
     * In der E-Rechnung taucht keine Nummerierung auf. Liegt in details und
     * friert beim Ausstellen ein; Storno, Korrektur und Gutschrift erben sie
     * bewusst nicht (siehe advanceDeductionHeading).
     */
    advanceNumbering?: AdvanceNumbering;

    /**
     * Abweichender Anzeigename des Projekts — nur auf DIESEM Beleg.
     *
     * Auf dem Papier steht die Zeile "Projekt: <Name>". Was der Auftraggeber
     * dort lesen will, deckt sich nicht immer mit dem Namen, unter dem die
     * Baustelle bei uns gefuehrt wird: eine Rechnung ueber zwei Lose heisst
     * beim Empfaenger "SuedLink Los 5 und 6", waehrend unser Projekt "SuedLink
     * Los 6" bleibt. Das Projekt deshalb umzubenennen waere der falsche Hebel —
     * es traegt denselben Namen in Projektliste, Pruefberichten, CRM und
     * Auswertungen.
     *
     * Wirkt ausschliesslich auf die Projektzeile des Belegs (invoiceHeading).
     * "Unser Zeichen" — die Projektnummer im rechten Kopfblock — bleibt
     * unberuehrt: sie ist der Schluessel des Vorgangs und darf nicht abweichen.
     * Ebenso unberuehrt bleiben Listen, Masken und der details.projects-Vermerk;
     * die echte Zuordnung des Belegs aendert sich nicht.
     *
     * Leer heisst: der echte Projektname. Der naechste Entwurf DESSELBEN
     * Projekts uebernimmt ihn (invoiceLogic: pickLastUsedProjectNames), Storno,
     * Korrektur und Gutschrift erben ihn vom Ursprungsbeleg — genau wie die
     * Kostenstelle. Liegt in details und friert damit beim Ausstellen ein.
     */
    projectNameOverride?: string;
    /**
     * Zusatzzeilen DIREKT UNTER der Empfaengeradresse im Anschriftfeld —
     * Angaben, die der Auftraggeber dort verlangt (z. B. Vergabestelle,
     * Referenz, Abteilung), die aber weder in den Kundenstamm gehoeren noch
     * in den Betreff-Block.
     *
     * Drei getrennte Felder statt eines Mehrzeilers, weil sie als drei Zeilen
     * gedruckt werden und einzeln vorbelegt/geleert werden. Der naechste
     * Entwurf DESSELBEN Projekts uebernimmt sie (invoiceLogic:
     * pickLastUsedAddressExtras) — dieselbe Mechanik wie Kostenstelle und
     * abweichender Projektname. Liegen in details und frieren beim Ausstellen
     * ein.
     */
    addressExtra1?: string;
    addressExtra2?: string;
    addressExtra3?: string;
    /**
     * Der Schlussblock dieses Belegs bleibt weg: Grussformel, Unterschrift,
     * Name des Bearbeiters UND die Firmenzeile darunter.
     *
     * Die SEITENFUSSZEILE bleibt davon unberuehrt — Anschrift, Registergericht,
     * Steuernummer und Bankverbindung sind Pflichtangaben und gehoeren nicht
     * zur Zeichnung des Belegs. Wer den Beleg ausgestellt hat, steht dort
     * weiterhin.
     *
     * Warum am Beleg und nicht in den Einstellungen: die Grussformel dort gilt
     * fuer Angebot, Rechnung UND Gutschrift gemeinsam, und der Name liess sich
     * ueberhaupt nicht abschalten. Wer eine einzelne Rechnung ohne
     * persoenliche Zeichnung verschicken will (Sammel- und Turnusrechnungen,
     * Belege an eine Buchhaltungsadresse), musste bisher die Einstellung fuer
     * alles andere mit aendern.
     *
     * FEHLT das Feld, wird gedruckt — der Bestand bleibt unveraendert. Friert
     * beim Ausstellen mit den uebrigen details ein.
     */
    hideClosing?: boolean;
    /**
     * Abweichende Kundenangaben NUR fuer diesen Beleg (siehe CustomerOverride).
     *
     * Der Bearbeiten-Dialog im Beleg schreibt sonst in den Kundenstamm und
     * damit auf jeden anderen Beleg desselben Kunden. Verlangt EIN Auftraggeber
     * fuer EIN Projekt eine andere Rechnungsanschrift, waere das der falsche
     * Hebel — deshalb die zweite Ablage hier, genau wie beim abweichenden
     * Projektnamen.
     *
     * Wirkt auf ALLES, was dieser Beleg vom Kunden ausgibt: Andruck, Vorschau,
     * Kopfzeile, E-Rechnung und den beim Ausstellen eingefrorenen
     * customerSnapshot. Die Zuordnung des Belegs (customerId, Kundennummer,
     * DATEV-Konto) aendert sich dadurch NIE.
     *
     * Der naechste Entwurf DESSELBEN Projekts uebernimmt die Abweichung
     * (invoiceLogic: pickLastUsedCustomerOverrides) — dieselbe Mechanik wie
     * Kostenstelle, Projektname und Adress-Zusatz. Leer heisst: der Beleg
     * folgt dem Kundenstamm.
     */
    customerOverride?: CustomerOverride;
    bankAccount?: { iban?: string; bic?: string; holder?: string; bankName?: string };
    /** BT-10 Leitweg-ID — bei oeffentlichen Auftraggebern Pflicht in der XRechnung. */
    buyerReference?: string;
    /** BT-13 Bestellnummer des Kunden. */
    orderReference?: string;
    /** BT-12 Rahmenvertragsbezug. */
    contractReference?: string;

    /**
     * Der Leistungszeitraum, den der Beleg AUSWEIST — von/bis, jeweils
     * yyyy-mm-dd.
     *
     * Getrennt von deliveryDateFrom/deliveryDateTo, und das ist der Kern:
     * jene beiden steuern die AUSWAHL (welche Protokolleintraege der Aufbau
     * uebernimmt und in welcher Reihenfolge planAllocations sie zuordnet) und
     * duerfen deshalb nicht nach Anzeigewuenschen verstellt werden. Kumulativ
     * wird deliveryDateFrom sogar zwingend auf die erste Leistung des Projekts
     * gezogen (invoiceService: cumulativeFrom) — der Beleg rechnet alles bis
     * zum Stichtag ab und muss das abdecken. Genau daraus entstand der
     * Fehlerfall, den diese Felder aufloesen: eine Rechnung fuer Juni/Juli
     * wies "01.03.2026 – 31.07.2026" aus, weil im Maerz die erste Leistung
     * protokolliert war.
     *
     * Gefuellt beim Anlegen aus dem GEWAEHLTEN Abrechnungszeitraum (volle
     * Monatsgrenzen, invoiceLogic: servicePeriodFromSelection) und in der Maske
     * frei ueberschreibbar. Leer heisst "nichts gesagt" — dann faellt der
     * Andruck auf deliveryDateFrom/deliveryDateTo zurueck, so wie jeder Beleg
     * aus der Zeit vor diesen Feldern.
     *
     * Wirkt auf ALLES, was den Zeitraum ausweist: Andruck (Leistungszeitraum
     * bzw. Leistungsdatum), E-Rechnung (BT-72/73/74) und den angehaengten
     * Leistungsnachweis. Was der Beleg abrechnet, aendert sich dadurch nie.
     * Liegt in details und friert damit beim Ausstellen ein.
     */
    servicePeriodFrom?: string;
    servicePeriodTo?: string;

    /**
     * Der LEISTUNGSZEITRAUM: erster und letzter Tag, an dem fuer diesen Beleg
     * tatsaechlich geleistet wurde — jeweils yyyy-mm-dd.
     *
     * Aus dem Leistungsprotokoll des Projekts gebildet
     * (invoiceLogic: performedSpanFromLog), begrenzt auf den Stichtag des
     * Belegs, und beim Ausstellen eingefroren wie die Zuordnung.
     *
     * Warum gespeichert und nicht bei jeder Anzeige neu gerechnet: dem
     * fertigen Beleg liegt das Protokoll nicht mehr bei. Andruck, E-Rechnung,
     * DATEV-Stapel und GoBD-Archiv arbeiten allein mit der Rechnung — teils
     * ueber hunderte Belege aus verschiedenen Projekten hinweg. Und ein
     * archivierter Beleg muss seinen Leistungszeitpunkt behalten, auch wenn im
     * Projekt spaeter weitergeleistet wird.
     *
     * Bewusst NICHT aus der Zuordnung ableitbar: die verbraucht kumulativ die
     * offenen Protokolleintraege vom aeltesten her. Ist der Zuwachs einer
     * Rechnung schon durch Januar bis April gedeckt, endet ihre Zuordnung am
     * 22.04. — obwohl im Juli und August weiter geleistet wurde und der Beleg
     * diesen Stand mit abrechnet. Genau so entstand der Andruck
     * "27.01.–22.04.2026" auf einem Beleg, dessen letzte Begehungen am 27.07.
     * und 10.08. stattfanden.
     *
     * Fehlt die Angabe (Altbelege), faellt performancePeriod auf eine
     * Naeherung aus dem Beleg selbst zurueck.
     */
    performedFrom?: string;
    performedTo?: string;

    /**
     * Von Hand gesetzter LEISTUNGSBEGINN eines KUMULATIVEN Belegs, yyyy-mm-dd.
     *
     * Kumulativ ermittelt der Aufbau den Beginn selbst (erste abrechenbare
     * Leistung bis zum Stichtag, invoiceLogic: cumulativeDeliveryStart) und
     * schreibt ihn nach deliveryDateFrom und performedFrom. Diese Angabe
     * ersetzt den ermittelten Wert an BEIDEN Stellen und ueberlebt jeden
     * Neuaufbau und das Ausstellen. Leer heisst: automatisch, wie bisher.
     *
     * Bewusst ein eigenes Feld statt eines einmal ueberschriebenen
     * deliveryDateFrom: der Aufbau laeuft nach jeder Stichtagsaenderung von
     * selbst und holte den ermittelten Wert sonst still zurueck.
     */
    cumulativeFromManual?: string;

    /**
     * WERT der Kostenstellenzeile, im Beleg unter der Projektbezeichnung —
     * das, was rechts vom Doppelpunkt steht ("4711-02").
     *
     * Reine Anzeigeangabe des Empfaengers: er verbucht den Beleg intern auf
     * diese Stelle und will sie deshalb auf dem Papier wiederfinden. Bewusst
     * KEIN Bezug zu einer eigenen Kostenrechnung — der Wert wird nirgends
     * ausgewertet und darf frei geschrieben werden. In der E-Rechnung ist genau
     * dieser Wert BT-19 (cbc:AccountingCost bzw.
     * ram:ReceivableSpecifiedTradeAccountingAccount).
     *
     * Ohne Wert gibt es keine Zeile — auch dann nicht, wenn eine Bezeichnung
     * dasteht: "PSP-Element:" ohne PSP-Element ist keine Angabe.
     *
     * Liegt in details und damit im jsonb: kein eigenes Feld, und beim
     * Ausstellen friert die Angabe mit dem uebrigen Belegkopf ein.
     */
    costCenter?: string;

    /**
     * BEZEICHNUNG der Kostenstellenzeile — der Text links vom Doppelpunkt.
     *
     * Jeder Auftraggeber nennt dieselbe Angabe anders ("Kostenstelle",
     * "PSP-Element", "Innenauftrag", "Auftragsnummer intern"). Fehlt sie, druckt
     * der Beleg "Kostenstelle" (invoiceLogic.ts: DEFAULT_COST_CENTER_LABEL) —
     * der haeufigste Fall und der Stand aller Belege vor diesem Feld.
     *
     * Wiederkehrende Bezeichnungen liefert die Vorlagenliste (Tabelle
     * invoice_cost_centers); im XML taucht die Bezeichnung NICHT auf, EN 16931
     * kennt zu BT-19 kein Beschriftungsfeld.
     */
    costCenterLabel?: string;

    /**
     * Freier Zusatztext im Belegkopf, unter der Kostenstelle.
     *
     * Mehrzeilig. Gedacht fuer Angaben, die der Auftraggeber ueber der
     * Anrede sehen will (Bauteil, Vergabenummer, interner Vermerk) und fuer
     * die es kein eigenes Feld gibt. Ausdruecklich NICHT die Einleitung:
     * die steht als introText unter der Anrede und wird aus der
     * Konfiguration vorbelegt.
     *
     * Heisst subjectNote und NICHT headerNote: `headerText` daneben ist die
     * eigene UEBERSCHRIFT des Belegs. Zwei Felder, deren Namen sich um einen
     * Wortteil unterscheiden und die beide „irgendwo oben" landen, werden
     * beim Lesen und beim Schreiben verwechselt — der Bezugspunkt hier ist
     * der Betreff-Block (Projekt, Kostenstelle, Zusatztext).
     */
    subjectNote?: string;

    skonto?: { percent: number; days: number };
    /** Grund bei Storno, Korrektur und Gutschrift — im Beleg auszuweisen. */
    reasonText?: string;
    /** Nur SELF_BILLING_CREDIT_NOTE: die vom Kunden vergebene Belegnummer. */
    externalDocumentNumber?: string;

    /**
     * Projekte des Belegs mit Nummer und Name, festgehalten beim Anlegen.
     *
     * Am BELEG und nicht zur Anzeigezeit nachgeladen: der PDF-Generator kennt
     * nur die Rechnung, und beim Ausstellen friert details mit ein. Ein spaeter
     * umbenanntes oder umnummeriertes Projekt darf einen archivierten Beleg
     * nicht veraendern — der Andruck muss zeigen, was zum Zeitpunkt der
     * Rechnung galt.
     *
     * Bei Belegen aus der Zeit vor diesem Vermerk leer; dann bleibt die Angabe
     * aus, statt geraten zu werden.
     *
     * Beim AUSSTELLEN wird der Vermerk bewusst NICHT aufgefrischt. Es sieht nach
     * einer Verbesserung aus — der Entwurf koennte Wochen alt sein —, ist aber
     * genau die Aenderung, die Storno und Korrektur zerstoert: die erben ihren
     * Vermerk vom Ursprungsbeleg und muessen dessen Stand zeigen, denn sie
     * nehmen genau diesen Beleg zurueck. Wuerde beim Ausstellen aufgefrischt,
     * nennte ein Storno nach einer Projektumbenennung ein anderes Projekt als
     * die Rechnung, die es aufhebt.
     */
    projects?: InvoiceProjectRef[];

    /**
     * Leistungsnachweis als Anlage hinten an die Rechnung haengen.
     *
     * Gehoert an den BELEG und nicht in die globale Konfiguration: ob ein
     * Nachweis beiliegt, verlangt der Auftraggeber — bei dem einen immer, bei
     * dem anderen nie. Die Anlage entsteht beim Ausstellen und ist damit Teil
     * der archivierten Bytes und ihres Hashes.
     */
    attachPerformanceProof?: boolean;

    /**
     * Eigener, hochgeladener Leistungsnachweis (Storage-Pfad im Bucket
     * `invoices`, Prefix custom-proofs/). Gesetzt schlaegt er den generierten
     * Nachweis: gebraucht, wenn der Nachweis unterschrieben werden musste und
     * als Scan zurueckkommt. Wird wie die generierte Fassung beim Ausstellen
     * hinten angehaengt und friert mit den archivierten Bytes ein.
     */
    customProofPath?: string;
    /** Urspruenglicher Dateiname des Uploads — nur fuer die Anzeige. */
    customProofFileName?: string;

    /**
     * Zeitraum, den der ANHANG zeigt — falls er vom Auswahlfenster des Belegs
     * abweichen soll.
     *
     * Der Nachweis filtert sonst auf deliveryDateFrom/-To, also auf das
     * Fenster, aus dem die Mengen gezogen wurden. Das ist der Normalfall und
     * bleibt die Vorgabe. Es gibt aber den Beleg, dessen Fenster enger oder
     * weiter liegt als das, was der Auftraggeber belegt sehen will — ein
     * kumulativer Beleg etwa zieht Mengen ab dem Projektbeginn, der Nachweis
     * soll aber nur den abgerechneten Monat auflisten.
     *
     * Je Seite einzeln, dieselbe Regel wie servicePeriodFrom/-To: die fehlende
     * Seite faellt auf das Auswahlfenster zurueck, damit niemand beide Daten
     * nachtragen muss, um eines zu aendern.
     *
     * Aendert NUR die Anlage. Was der Beleg abrechnet, haengt am Auswahlfenster
     * und wird hiervon nicht angefasst — sonst waere das eine Mengenaenderung,
     * die wie eine Anzeigeeinstellung aussieht.
     */
    proofRangeFrom?: string;
    proofRangeTo?: string;

    /**
     * LV-Positionen, die im Nachweis NICHT erscheinen sollen (ihre Ids).
     *
     * Ausschluss- statt Einschlussliste, und das ist die entscheidende
     * Richtung: eine Position, die nach dem Speichern im Projekt dazukommt,
     * steht damit AUF dem Nachweis. Eine Einschlussliste liesse sie
     * stillschweigend fehlen — und ein Nachweis, dem etwas fehlt, faellt
     * niemandem auf, waehrend eine Position zu viel sofort auffaellt.
     *
     * Nur die Anlage, nie die Rechnung: ausgeblendet heisst „nicht belegt",
     * nicht „nicht abgerechnet".
     */
    proofExcludedServiceIds?: string[];

    /**
     * Letzte Zurueckweisung aus der Pruefung zurueck in die Bearbeitung.
     *
     * Nur der letzte Vorgang; die vollstaendige Kette steht im
     * invoice_audit_log, das jeden Statuswechsel mitschreibt.
     */
    reviewRejection?: { reason: string; at: string; by: string };

    /**
     * Nebenkostenzuschlag in Prozent auf die Honorarsumme (z.B. 2 fuer 2 %).
     *
     * Am BELEG und nicht nur in der Konfiguration: der Satz steht im Vertrag
     * und unterscheidet sich je Auftraggeber. Die Konfiguration liefert nur den
     * Vorschlag fuer neue Belege.
     */
    ancillaryPercent?: number;

    /**
     * Nachlass auf den GANZEN Beleg in Prozent (10 = 10 %).
     *
     * Der Wert ist nur die EINGABE. Auf dem Beleg entsteht daraus je Steuersatz
     * eine eigene Zeile der Art DISCOUNT (applyDocumentDiscount, invoiceLogic) —
     * das Modell kennt bewusst keine Dokumentebene fuer Nachlaesse (BT-107 wird
     * nie geschrieben, siehe en16931Rules), und eine einzige Zeile mit einem
     * gemittelten Satz waere weder nachrechenbar noch §14-konform.
     *
     * Bemessungsgrundlage ist die Honorarsumme OHNE den Nebenkostenzuschlag:
     * der Zuschlag wird anschliessend auf die bereits geminderte Summe neu
     * gebildet und traegt den Nachlass damit von selbst mit. Wuerde der Nachlass
     * auch auf den Zuschlag rechnen, waere er dort zweimal beruecksichtigt.
     *
     * Es gibt bewusst KEINE Vorbelegung aus dem Kundenstamm
     * (Customer.defaultDiscountPercent gilt nur im Angebot): ein stiller
     * Durchgriff auf die Rechnung veraenderte Betraege, ohne dass es jemand
     * eintraegt.
     */
    discountPercent?: number;
    /**
     * Fester Nachlass auf den GANZEN Beleg in Cent, positiv als Minderung.
     *
     * SCHLAEGT discountPercent: ist dieser Betrag gesetzt und ungleich 0,
     * entsteht der Nachlass ausschliesslich daraus. Verteilt wird er anteilig
     * auf die Steuergruppen (distributeDiscountAmount, invoiceLogic) — ein
     * Nachlass, der nur eine Gruppe minderte, verschoebe Bemessungsgrundlagen
     * zwischen den Saetzen und waere nach §14c UStG angreifbar.
     */
    discountAmountCents?: number;

    /**
     * Sicherheitseinbehalt in Prozent auf den LEISTUNGSSTAND brutto (5 = 5 %),
     * kumulativ: mit diesem Beleg wird round(pct x gross) minus das auf den
     * abgezogenen Vorbelegen bereits Einbehaltene einbehalten
     * (computeRetention in invoiceLogic).
     *
     * Vorbelegt aus ProjectGlobalData.retention, danach am Beleg massgeblich.
     * Kein Nachlass: der Einbehalt mindert nur den Zahlbetrag
     * (InvoiceTotals.retentionCents), nie die Bemessungsgrundlage.
     *
     * 0 % auf einem Abschlag heisst: bisher Einbehaltenes wird FREIGEGEBEN
     * (cumulative 0 < prior). Das ist die Konsequenz der Formel und gewollt —
     * wer den Einbehalt aufhebt, zahlt ihn aus.
     */
    retentionPercent?: number;
    /**
     * Schlussrechnung: bisher einbehaltene Betraege freigeben. Fehlt = true bei
     * FINAL_INVOICE, sonst ohne Wirkung (wantsRetentionRelease). Wirkt ueber
     * den effektiven Satz (effectiveRetentionPercent): Freigabe heisst Satz 0,
     * und die Formel gibt dann die Summe der Vorbelege frei. `false` laesst
     * den Satz stehen — der Gewaehrleistungseinbehalt bleibt einbehalten.
     */
    releaseRetention?: boolean;

    /**
     * Abrechnungsmodell des Belegs.
     *
     * 'CUMULATIVE' = der Beleg zeigt den GESAMTEN Leistungsstand und zieht alle
     * vorher ausgestellten Rechnungen des Projekts ab. Ohne Angabe gilt die
     * Fortschreibung: nur die neuen Mengen, kein Abzug.
     *
     * Der Unterschied ist beim Ausstellen tragend — bei 'CUMULATIVE' duerfen
     * die abgezogenen Belege NICHT abgeloest werden, denn die naechste Rechnung
     * muss sie erneut abziehen.
     */
    billingMode?: 'CUMULATIVE';

    /**
     * Leistungen des Projekt-LV, die dieser Beleg NICHT abrechnet (ServiceItem.id).
     *
     * Wer im Mai nur die Begehungen fordern will und den SiGe-Plan desselben
     * Zeitraums erst mit dem naechsten Beleg, nimmt die Position hier heraus.
     * Die Leistung bleibt offen: sie wird weder gebucht noch reserviert und
     * steht dem naechsten Beleg unveraendert zur Verfuegung.
     *
     * Am BELEG und nicht nur im Zustand der Maske: der Positionsaufbau laeuft
     * bei jeder Zeitraumaenderung erneut und ersetzt die LV-Zeilen vollstaendig.
     * Eine bloss geloeschte Zeile holte er sofort wieder herein — der Ausschluss
     * muss jeden Neuaufbau ueberleben, und dafuer muss er gespeichert sein.
     *
     * Friert mit details beim Ausstellen ein und dokumentiert damit, was der
     * Beleg bewusst offen gelassen hat.
     */
    /**
     * Gliederung des Belegs: frei benannte Abschnitte in Andruckreihenfolge.
     *
     * Am BELEG und nicht im Zustand der Maske — aus demselben Grund wie
     * excludedServiceIds: der Positionsaufbau ersetzt die LV-Zeilen bei jeder
     * Zeitraumaenderung vollstaendig, und die Gliederung muss das ueberleben.
     * Friert mit details beim Ausstellen ein.
     *
     * Leere Liste (oder fehlend) heisst: keine Gliederung, der Beleg bleibt
     * genau wie bisher. Ein Abschnitt ohne Positionen wird nicht gedruckt, in
     * der Maske aber weiter angezeigt — sonst liesse sich nichts hineinlegen.
     */
    sections?: InvoiceSection[];

    excludedServiceIds?: string[];

    /**
     * Wurden die Leistungszeilen MIT „ohne Datum mitnehmen" gebaut?
     *
     * Die Wahl, mit der buildProjectServiceLines (bzw. der Rechnungslauf) die
     * Zeilen zuletzt aus dem Protokoll aufgebaut hat — gesetzt bei jeder
     * Neuanlage und jedem Neuaufbau, ueberschrieben vom naechsten. Eine von
     * Hand geaenderte Menge laesst sie stehen: sie aendert die Zeile, nicht
     * die Grundlage, aus der sie entstand.
     *
     * Gespeichert fuer den Schatten am Tor (S5a): der vergleicht die Menge
     * ohne Datum nur dort mit Geldwirkung, wo sie in den Entwurf geflossen ist.
     * Ohne die Angabe am Beleg wusste er das nicht und musste jeden Entwurf so
     * pruefen, als waere er mit der Option gebaut (Betreiber-Entscheid
     * 18.10.2026).
     *
     * Fehlt das Feld (Entwurf aus der Zeit davor, Abschlag, Storno,
     * Gutschrift), ist die Wahl unbekannt — nicht „ohne".
     */
    includeUndated?: boolean;

    /**
     * Andruck der durch eine Monatspauschale gedeckten Positionen.
     *
     * 'ALL'       = jede Zeile wie sie auf dem Beleg steht (Bestand).
     * 'FLAT_ONLY' = die gedeckten Zeilen (InvoiceLine.flatCovered) werden im
     *               Andruck — PDF UND E-Rechnung — je Steuersatz zu EINER Zeile
     *               "Leistungen bis zur Umstellung auf die Monatspauschale"
     *               zusammengefasst. Der Kunde sieht die Pauschale und nicht
     *               mehr zwoelf alte Begehungen mit eingefrorenem Stand.
     *
     * Reine DARSTELLUNG: die Zeilen des Belegs, seine Summen, der Abzug der
     * Vorrechnungen, Rueckschreibung und Storno bleiben unveraendert. Die
     * gedeckten Zeilen WEGZULASSEN ginge nicht — kumulativ zieht der Beleg
     * je Vorrechnung deren Gesamtforderung ab; fehlte der eingefrorene Stand
     * im Gesamtstand, forderte der Beleg zu wenig. Die Summenzeile traegt
     * deshalb genau die Summe der zusammengefassten Zeilen.
     *
     * Vorbelegt mit 'FLAT_ONLY', sobald ein neuer Entwurf gedeckte Zeilen
     * traegt (createInvoiceDraft). Fehlt das Feld, gilt 'ALL' — Altbelege
     * drucken wie bisher. Friert mit details beim Ausstellen ein.
     */
    flatRateDisplay?: FlatRateDisplayMode;

    /** Ergebnis des Projektchecks samt dokumentierter Kenntnisnahme bei Gelb. */
    projectCheck?: {
        traffic: ProjectCheckTraffic;
        findings: ProjectCheckFinding[];
        checkedAt?: string;
        acknowledgedBy?: string;
        acknowledgedAt?: string;
        acknowledgeNote?: string;
    };

    /**
     * Protokoll der LV-Rueckschreibung je Projekt: Buchungsreferenz und der
     * Sync-Anker nach dem Speichern. Basis fuer die exakte Inversion beim Storno.
     */
    writeBack?: Array<{
        projectId: string;
        bookingReference: string;
        updatedAt: string;
        lineIds: string[];
        skipped?: boolean;
    }>;
}

export interface Invoice {
    id: string;
    /** Erst ab ISSUED gesetzt — vorher gibt es bewusst keine Nummer. */
    invoiceNumber?: string;
    documentType: DocumentType;
    series: InvoiceSeries;
    status: InvoiceStatus;

    customerId: string;
    /** Aus dem Join, nicht persistiert. */
    customerName?: string;
    /** Einzelrechnung: gesetzt. Sammelrechnung: leer, die Projekte stehen in den Zeilen. */
    primaryProjectId?: string;
    quoteId?: string;
    billingRunId?: string;
    /**
     * Teilrechnung: alle Belege EINER Aufteilung tragen dieselbe Gruppe.
     *
     * Zweck ist nicht die Anzeige, sondern die Reservierung: ohne die Gruppe
     * halten sich die Entwuerfe derselben Aufteilung gegenseitig fuer einen
     * fremden Zugriff auf dieselbe LV-Position und blockieren sich
     * ("Menge doppelt beansprucht").
     */
    splitGroupId?: string;
    /**
     * ABSCHNITTE: WESSEN Beleg das ist — der Teilnehmer des
     * Aufteilungsschluessels (project_splits.id).
     *
     * `splitGroupId` sagt, zu welcher Aufteilung der Beleg gehoert, dieses Feld
     * sagt, welcher Teil davon er ist. Bei mehreren Auftraggebern liess sich
     * das aus `customerId` ableiten; bei mehreren Abschnitten DESSELBEN
     * Auftraggebers (Gesamtprojekt mit mehreren Haeusern) nicht mehr — und
     * genau daran haengt der Abzug der Vorrechnungen. Ohne das Feld zoege die
     * Rechnung fuer Haus B die Abschlaege von Haus A mit ab und forderte fast
     * nichts, und der Beleg saehe dabei richtig aus.
     *
     * Wird bei der ANLAGE gesetzt und danach nie wieder. Leer an Altbelegen —
     * die werden ueber `customerId` aufgeloest, solange das eindeutig ist.
     */
    splitPartyId?: string;
    /** Anteil dieses Belegs an der Aufteilung (5000 = 50,00 %). */
    splitShareBp?: number;

    invoiceDate: string;
    /** BT-73 Beginn des Leistungszeitraums. */
    deliveryDateFrom?: string;
    /** BT-74 Ende des Leistungszeitraums. */
    deliveryDateTo?: string;
    dueDate: string;
    paymentTermsDays: number;

    lines: InvoiceLine[];
    advanceDeductions: AdvanceDeduction[];
    advanceDeductionMode: AdvanceDeductionMode;
    /**
     * Einbehalte der Vorbelege, die dieser Beleg abzieht — je abgezogenem
     * Vorbeleg ein Eintrag (priorRetentionsFromDeductions), wie
     * advanceDeductions beim Ausstellen eingefroren. Ihre Summe ist
     * totals.priorRetentionCents; auf der Schlussrechnung (Satz 0) ist das die
     * Freigabeliste.
     *
     * Datenbankspalte heisst weiterhin `retention_releases` (Nr. 104) — der
     * Name stammt aus dem ersten Bau, in dem nur die Schlussrechnung eine Liste
     * trug. Das Mapping in invoiceService uebersetzt.
     */
    priorRetentions?: PriorRetention[];
    totals: InvoiceTotals;

    /** Aggregate aus invoice_payments bzw. den Gutschriften. */
    paidCents: number;
    creditedCents: number;
    /** payable - paid - credited. In der Datenbank eine generierte Spalte. */
    openCents: number;

    /**
     * Hoechste TATSAECHLICH versandte Mahnstufe (0 = noch nicht gemahnt).
     *
     * Anders als die Ueberfaelligkeit ist das keine Ableitung aus dem Datum,
     * sondern ein Ereignis — deshalb gespeichert.
     */
    dunningLevel: number;
    dunningLastAt?: string;

    /** Beim Ausstellen eingefroren — eine spaetere Adressaenderung darf alte Belege nicht veraendern. */
    customerSnapshot?: Customer;
    companySnapshot?: InvoiceCompanySnapshot;

    pdfUrl?: string;
    snapshotId?: string;
    sentAt?: string;
    sentTo?: string;
    issuedAt?: string;
    issuedBy?: string;
    approvedAt?: string;
    approvedBy?: string;
    exportedAt?: string;

    details: InvoiceDetails;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export type PaymentDirection = 'INCOMING' | 'OUTGOING';
export type PaymentMethod =
    | 'BANK_TRANSFER'
    | 'CASH'
    | 'CARD'
    | 'DIRECT_DEBIT'
    | 'OFFSET'
    | 'OTHER'
    /**
     * Skonto: der Abzug des Kunden bei Zahlung in der Skontofrist. Kein Geld,
     * aber ein Ausgleich des offenen Rests (SQL Nr. 121, src/services/cashDiscount.ts).
     */
    | 'CASH_DISCOUNT'
    /**
     * Sicherheitseinbehalt des Kunden: beim Zahlen abgezogen, stand nicht auf
     * der Rechnung. Gleicht den Rest aus und wird per Trigger als Alt-Einbehalt
     * des Projekts gefuehrt (SQL Nr. 125/126, src/services/paymentRemainder.ts).
     */
    | 'CUSTOMER_RETENTION';

export interface InvoicePayment {
    id: string;
    invoiceId: string;
    /** OUTGOING = Erstattung an den Kunden. */
    direction: PaymentDirection;
    valueDate: string;
    amountCents: number;
    /** Aufteilung des Zahlbetrags — Pflicht fuer Umsatzsteuer und DATEV. */
    allocatedNetCents: number;
    allocatedTaxCents: number;
    method: PaymentMethod;
    /** Verwendungszweck aus dem Kontoauszug. */
    reference?: string;
    /** Idempotenzschluessel des Bankimports — verhindert Doppelbuchung desselben Umsatzes. */
    bankTxId?: string;
    note?: string;
    createdBy: string;
    createdAt: string;
}

export type InvoiceRelationType =
    | 'CANCELS'
    | 'CORRECTS'
    | 'DEDUCTS_ADVANCE'
    | 'CREDITS'
    | 'SUPERSEDES'
    | 'BASED_ON_QUOTE';

export interface InvoiceRelation {
    id: string;
    fromInvoiceId: string;
    toInvoiceId: string;
    type: InvoiceRelationType;
    createdAt: string;
}

/**
 * Unveraenderliches Beweisstueck einer ausgestellten Rechnung.
 *
 * Der Snapshot traegt den vollstaendigen Rendering-Input UND den Hash der
 * archivierten PDF-Bytes. Reproduziert wird aus den Bytes, nicht aus payload:
 * ein Neu-Rendern haengt an jsPDF-Version, Schriftmetriken und dem jederzeit
 * austauschbaren Briefpapierbild. payload dient der XML-Erzeugung, dem
 * DATEV-Export und der Pruefungsfrage, was das System damals angenommen hat.
 */
export interface IssuedInvoiceSnapshot {
    id: string;
    invoiceId: string;
    invoiceNumber: string;
    issuedAt: string;
    issuedBy: string;
    payload: Record<string, unknown>;
    pdfPath: string;
    /** sha256 als Kleinbuchstaben-Hex. */
    pdfSha256: string;
    pdfBytes: number;
    xmlPath?: string;
    xmlSha256?: string;
    xmlFlavour?: 'XRECHNUNG_UBL' | 'XRECHNUNG_CII' | 'ZUGFERD';
    /** Anwendungs- und jsPDF-Version, mit der gerendert wurde. */
    generatorVersion: string;
}

export interface CustomerCredit {
    id: string;
    customerId: string;
    sourceInvoiceId?: string;
    sourcePaymentId?: string;
    amountCents: number;
    /** Bereits erstattet oder verrechnet. Verfuegbar ist amount - used. */
    usedCents: number;
    reason: 'OVERPAYMENT' | 'CREDIT_NOTE' | 'MANUAL';
    note?: string;
    createdAt: string;
    createdBy: string;
}

/** Verrechnung eines Guthabens mit einer anderen Rechnung. */
export interface CreditAllocation {
    id: string;
    creditId: string;
    targetInvoiceId: string;
    allocatedCents: number;
    allocatedAt: string;
    allocatedBy: string;
}

/**
 * Nummernkreis-Konfiguration.
 *
 * 'shared' = ein durchlaufender Kreis ueber alle Belegarten (Standard).
 * 'per_type' = eigener Zaehler je Belegart (RE/AR/SR/...).
 * Der Wechsel ist reine Konfiguration und braucht keine Datenmigration; nach
 * der ersten ausgestellten Rechnung warnt die Oberflaeche davor.
 */
export interface InvoiceNumberingConfig {
    mode: 'shared' | 'per_type';
    /** Kreis im Modus 'shared'. */
    sharedSeries: InvoiceSeries;
    /** 'dash' -> RE-2026-0001 · 'housestyle' -> RE 1-2026 (wie die Angebote). */
    format: 'dash' | 'housestyle';
    padWidth: number;
    withYear: boolean;
}

export interface InvoiceDatevConfig {
    consultantNumber: string;
    clientNumber: string;
    /** yyyy-mm-dd, Beginn des Wirtschaftsjahres. */
    fiscalYearStart: string;
    accountLength: number;
    skr: 'SKR03' | 'SKR04';
    revenueAccount19: string;
    revenueAccount7: string;
    revenueAccount0: string;
    /** Basis der Debitorenkonten; Customer.customerNumber wird angehaengt bzw. genutzt. */
    debtorAccountBase?: string;

    /**
     * Aufwandskonto der Freelancer-Gutschriften (Phase D5).
     *
     * Gegenkonto der KREDITORISCHEN Buchung: Konto = Kreditor des Freelancers
     * (freelancers.datev_kreditor_nr), Gegenkonto = dieses Aufwandskonto.
     * Bewusst EIN Konto und nicht je Steuersatz eines wie bei den Erloesen —
     * die Leistung ist immer dieselbe (Fremdleistung Baustellenbegehung), und
     * der Steuersatz haengt am Status des Freelancers (19 % oder §19), nicht
     * an der Art der Leistung.
     *
     * Ohne Wert scheitert der Gutschriften-Export mit Klartext, statt ein
     * Konto zu raten (src/services/datev/creditNoteDatev.ts).
     */
    kreditorAufwandKonto?: string;

    /**
     * Konten der erhaltenen Anzahlungen, JE STEUERSATZ eines — genau wie die
     * Erloeskonten darueber, damit in diesem Objekt nicht zwei Denkweisen
     * nebeneinander stehen. Die Kanzlei fuehrt SKR04 3272 fuer 19 % und
     * SKR04 3271 fuer 7 %.
     *
     * Eine Abschlagsrechnung ist kein Erloes, sondern eine erhaltene
     * Anzahlung und damit eine Verbindlichkeit; die Schlussrechnung loest sie
     * gegen dieses Konto wieder auf. Ohne das Konto laesst sich das nicht
     * abbilden — der Abschlag muesste dann als Erloes gebucht und in der
     * Schlussrechnung ein zweites Mal als Erloes gebucht werden.
     *
     * Warum je Satz ein eigenes und kein Sammelkonto: bei Automatikkonten
     * steuert das KONTO den Steuerschluessel. Liefen 19 % und 7 % ueber
     * dasselbe Konto, zoege die Kanzlei fuer beide dieselbe Umsatzsteuer —
     * ein Fehler, der erst in der Voranmeldung auffaellt. Aus demselben Grund
     * weicht der Export bei einem fehlenden Konto NICHT auf ein anderes aus,
     * sondern scheitert mit Klartext
     * (advanceAccountForTaxRate in src/services/datev/datevExport.ts).
     *
     * Die Konsequenz des Umschaltens gehoert vor die Eingabe: sobald hier ein
     * Konto gepflegt ist, buchen Abschlagsrechnungen NICHT mehr auf Erloese.
     * Wer es mitten im Wirtschaftsjahr eintraegt, hat zwei Buchungslogiken in
     * einem Jahr — der Wechsel gehoert deshalb auf einen Jahresanfang und in
     * die Abstimmung mit der Kanzlei.
     *
     * Leer ist deshalb ein gueltiger Zustand und folgenlos: wer TEILLEISTUNGEN
     * abrechnet (§ 13 Abs. 1 Nr. 1a UStG — erbrachte Leistung, echter Erloes),
     * braucht kein Anzahlungskonto und laesst die drei Felder leer. Gebraucht
     * werden sie erst, wenn echte Abschlagsrechnungen gestellt werden; nur
     * dann verlangt der Buchungsstapel sie und scheitert sonst mit Klartext
     * (src/services/datev/datevExport.ts).
     */
    advancePaymentAccount19?: string;
    /** Anzahlungskonto fuer 7 % (SKR04 3271). Siehe advancePaymentAccount19. */
    advancePaymentAccount7?: string;
    /** Anzahlungskonto fuer 0 % / §13b. Siehe advancePaymentAccount19. */
    advancePaymentAccount0?: string;

    /**
     * WANN das Anzahlungskonto eingetragen wurde (ISO-Zeitstempel). Die
     * Verwaltung stempelt ihn automatisch, sobald von „gar kein
     * Anzahlungskonto“ auf „mindestens eines“ gewechselt wird — er ist keine
     * Eingabe.
     *
     * Bewusst EIN Zeitstempel fuer alle Steuersaetze und keiner je Konto: die
     * Umstellung auf das Anzahlungskonto ist ein Ereignis in der Buchhaltung,
     * kein Wert je Steuersatz. Eine spaetere Korrektur einer Kontonummer
     * setzt ihn deshalb nicht neu.
     *
     * Er ist die Grenze zwischen zwei Buchungslogiken: Abschlagsrechnungen,
     * die VOR diesem Zeitpunkt exportiert wurden, hat der Export auf das
     * Erloeskonto gebucht (Bestandsverhalten ohne Konto); ihr spaeterer Abzug
     * muss die Erloeszeile mindern. Abschlaege danach liegen auf dem
     * Anzahlungskonto und werden dort aufgeloest. Ohne den Zeitstempel ist
     * ein bereits exportierter Abschlag nicht zuordenbar, und der Export
     * bricht bei seinem Abzug ab, statt zu raten
     * (deductedIsAdvance in src/services/datev/datevExport.ts).
     */
    advancePaymentAccountSince?: string;

    /**
     * Wie das VORSYSTEM die externen Alt-Abschlaege gebucht hat.
     *
     * Beschreibt ausdruecklich die Vergangenheit und nicht, wie wir buchen
     * wollen. Externe Alt-Abschlaege (public.external_advances, in der
     * Abzugsliste am Praefix 'ext:' erkennbar) tragen keine Belegart — die
     * Information existiert im System nicht und laesst sich nur beim
     * Vorsystem bzw. der Kanzlei erfragen. Beide Annahmen waeren falsch:
     *  'PRIOR_INVOICE' auf eine echte Anzahlung angewandt weist den Umsatz um
     *  deren Betrag zu NIEDRIG aus; 'ADVANCE' auf eine gewoehnliche
     *  Vorrechnung angewandt loest einen Anzahlungsbestand auf, den dieser
     *  Export nie aufgebaut hat — das Konto liefe ins Minus.
     *
     * Alle Altbelege stammen aus EINEM Vorsystem mit EINER Buchungspraxis;
     * die Frage ist also einmalig zu beantworten und danach historisch fix.
     * Ein spaeterer Wechsel traefe denselben Altbestand mit zwei
     * Buchungslogiken und ist deshalb keine Einstellung, an der man dreht.
     *
     * Ohne Wert scheitert ein Stapel mit externen Alt-Abschlaegen mit
     * Klartext, statt zu raten (src/services/datev/datevExport.ts).
     */
    externalAdvanceBooking?: 'ADVANCE' | 'PRIOR_INVOICE';

    /**
     * Optionale Basis der Kreditorenkonten — wird `datev_kreditor_nr`
     * VORANGESTELLT, gleiche Lesart wie debtorAccountBase. Leer lassen, wenn
     * im Stammsatz bereits die vollstaendige Kontonummer steht (Normalfall).
     */
    creditorAccountBase?: string;
}

export interface InvoiceConfig {
    defaultTaxRateBp: number;
    defaultPaymentTermsDays: number;
    defaultPaymentTerms?: string;
    defaultIntroText?: string;
    defaultOutroText?: string;
    /**
     * Ueberschrift fuer alle Belege ohne eigene (details.headerText).
     *
     * Wirkt zur Renderzeit, nicht beim Anlegen: sie wird nicht in den Beleg
     * kopiert, damit eine Aenderung hier auch bestehende Entwuerfe erreicht.
     * Leer heisst nicht "keine Ueberschrift" — dann bleibt der Standardtitel
     * der Belegart ('<Belegart> Nr. <Nummer>') der Rueckfall.
     */
    defaultHeaderText?: string;

    /**
     * Grussformel unter dem Schlusstext der Rechnung.
     *
     * undefined heisst "nicht gepflegt" — dann gilt der bisherige Standard
     * "Mit freundlichem Gruß". Leer (auch nur Leerzeichen) heisst dagegen
     * BEWUSST ohne Grussformel: dasselbe Muster wie ownLetterhead — was auf
     * der Rechnung nicht stehen soll, muss sich entfernen lassen.
     */
    closingText?: string;

    /**
     * Freier Zusatztext unter Zahlungsbedingungen und Bankverbindung
     * (z. B. Verwendungszweck-Hinweis, Skonto). Mehrzeilig, nur gedruckt,
     * wenn gesetzt. Entfaellt wie die Bankverbindung auf Erstattungsbelegen —
     * ein Zahlungshinweis liest sich dort als Aufforderung an den Falschen.
     */
    paymentNoteText?: string;

    /** §19 UStG. Setzt den Steuersatz auf 0 und erzwingt den Hinweistext. */
    smallBusiness?: boolean;
    smallBusinessNote?: string;

    vatId?: string;
    taxNumber?: string;
    registerCourt?: string;
    registerNumber?: string;
    managingDirectors?: string[];
    iban?: string;
    bic?: string;
    bankName?: string;
    /**
     * Kontoinhaber/Zahlungsempfaenger (BT-85), wie er bei der Bank hinterlegt
     * ist — weicht oft in der Schreibweise vom Firmennamen ab. Rueckfallkette:
     * Beleg (details.bankAccount.holder) → Snapshot → dieses Feld → Firmenname.
     */
    accountHolder?: string;

    /**
     * Fusszeile manuell bearbeiten (Muster ownLetterhead): AN = die vier
     * Texte in footerColumnsText definieren die Spalten vollstaendig, Zeile
     * fuer Zeile; leere Spalten entfallen. AUS = Automatik aus den
     * strukturierten Feldern, gespeicherte Texte bleiben wirkungslos liegen.
     */
    footerCustom?: boolean;
    /** Je Spalte ein mehrzeiliger Text; Zeilenumbruch = Fusszeilen-Zeile. */
    footerColumnsText?: string[];

    /**
     * Ansprechpartner des Rechnungsstellers — BT-41, BT-42, BT-43.
     *
     * In der XRechnung PFLICHT (BR-DE-5, BR-DE-6, BR-DE-7). Auf dem Papier
     * stehen Telefon und Mail zwar im Briefkopf-Text, aber das ist eine freie
     * Textspalte: fuer den Empfaenger einer E-Rechnung ist sie nicht
     * auswertbar, und die Regeln pruefen strukturierte Felder.
     *
     * Ohne contactName faellt der Serialisierer auf den ersten
     * Geschaeftsfuehrer zurueck.
     */
    contactName?: string;
    phone?: string;
    email?: string;
    /**
     * Nur fuers Druckbild (Spalten-Fusszeile, "Web www…"). Kein XRechnung-Feld
     * — die Kontakt-Pflichtfelder BR-DE-5..7 sind Name/Telefon/E-Mail.
     */
    website?: string;

    // ── Briefkopf der Rechnungen ────────────────────────────────────────────
    // Standard ist ein gemeinsamer Briefkopf mit den Angeboten: dieselben
    // Felder in QuoteConfig, nicht gepflegte Kopien. Rechnungen sollen aber
    // weniger Angaben tragen als Angebote, deshalb dieser Schalter.
    //
    // Bewusst ein Schalter und KEIN Rueckfall Feld fuer Feld: ein absichtlich
    // geleertes Rechnungsfeld muss leer bleiben duerfen. Mit Feld-Rueckfall
    // liesse sich eine Angabe des Angebots auf der Rechnung nie loswerden —
    // leer waere von "nicht gepflegt" nicht unterscheidbar.
    /**
     * false/undefined (Standard): Rechnungen nutzen den Briefkopf der Angebote,
     * die Felder darunter sind wirkungslos.
     * true: AUSSCHLIESSLICH die Felder darunter gelten — auch die leeren.
     */
    ownLetterhead?: boolean;
    /** Rechte Briefkopf-Spalte, eine Angabe pro Zeile. Nur bei ownLetterhead. */
    letterheadText?: string;
    /** Kleine linke Kopfzeile auf Seite 1. Nur bei ownLetterhead. */
    letterheadLeftLine?: string;
    /** dataURL — Ganzseiten-Briefpapier Seite 1. Ohne dieses Feld kein Briefpapier. */
    stationeryPage1?: string;
    /** dataURL — Briefpapier der Folgeseiten. Leer: Seite 1 gilt fuer alle Seiten. */
    stationeryFollowing?: string;
    /** mm, Inhaltsbeginn Seite 1 (default 50). */
    stationeryTopMarginPage1?: number;
    /** mm, Inhaltsbeginn Folgeseiten (default 25). */
    stationeryTopMarginFollowing?: number;
    /** mm, unterer Rand auf allen Seiten (default 25). */
    stationeryBottomMargin?: number;
    // footerFontSize fehlt hier absichtlich: reine Typografie der Fusszeile,
    // die in beiden Belegarten gleich aussehen soll. Sie bleibt in QuoteConfig.

    numbering: InvoiceNumberingConfig;
    advanceDeductionMode: AdvanceDeductionMode;
    /**
     * Vorschlag fuer den Nebenkostenzuschlag neuer Belege, in Prozent.
     * Der massgebliche Satz steht am Beleg (details.ancillaryPercent).
     */
    defaultAncillaryPercent?: number;
    /**
     * Abrechnungsmodell neuer Rechnungen. 'CUMULATIVE' haakt "Kumulativ
     * abrechnen" im Anlagedialog vor; massgeblich bleibt der Beleg selbst.
     */
    defaultBillingMode?: 'CUMULATIVE';
    dunningLevels?: Array<{ level: 1 | 2 | 3; daysAfterDue: number; feeCents: number }>;
    projectCheck: ProjectCheckConfig;
    /** §147 AO — nur eine Aufbewahrungswarnung, es wird nie automatisch geloescht. */
    archiveYears: number;
    datev?: InvoiceDatevConfig;
}

/** Regelkennung des Projektchecks. Siehe services/projectCheckLogic.ts. */
export type ProjectCheckRule =
    | 'report_count_mismatch'
    | 'inspection_counter_mismatch'
    | 'contract_overrun'
    | 'vorank_history_mismatch'
    | 'unlogged_quantity'
    /** Gegenstueck zu unlogged_quantity: das Protokoll fuehrt MEHR als der Leistungsstand. */
    | 'unbilled_logged_quantity'
    | 'billed_exceeds_used'
    /**
     * Der Abrechnungszaehler liegt UNTER der Rueckschreibungssumme des
     * Protokolls (Rechnung/Storno/manuelle Verbuchung, mit Vorzeichen): eine
     * Rechnung hat die Menge gefordert, der Zaehler weiss es nicht mehr — die
     * naechste Rechnung forderte sie ein zweites Mal (26051026, 19.09.2026).
     */
    | 'billed_below_postings'
    | 'reserved_exceeds_open'
    | 'zero_unit_price'
    | 'missing_start_docs'
    | 'project_not_finished'
    | 'child_project_direct'
    | 'customer_missing'
    | 'overlapping_invoice_period'
    | 'stale_open_position'
    | 'advance_exceeds_total'
    | 'no_open_positions'
    /** Hinweiszeile (gruen): auf dem Projekt stehen noch Sicherheitseinbehalte offen. */
    | 'open_retention'
    /** Schlussrechnung ohne Auszahlung, obwohl Einbehalte offen sind — das Geld bliebe liegen. */
    | 'retention_not_released'
    /**
     * Paketposition: alle Bestandteile erledigt, Position aber nicht gebucht —
     * die automatische Buchung ist nicht gelaufen (Ablauf abgebrochen, der
     * Nachtlauf holt es nach). Bleibt der Befund stehen, liegt Geld herum.
     */
    | 'bestandteile_vollstaendig_nicht_gebucht'
    /** Paketposition gebucht, aber Bestandteile offen — sie ist zurueckgehalten. */
    | 'position_gebucht_bestandteile_offen'
    /** Eine Unterlage ist Bestandteil einer Paketposition UND eigene LV-Position (E9). */
    | 'bestandteil_doppelt_als_position';

/**
 * Ein Befund des Projektchecks.
 *
 * Bewusst formkompatibel zu AdminAlertsView.globalAlerts
 * ({ id, type, title, severity }), damit dieselben Regeln dort mit angezeigt
 * werden koennen, ohne eine zweite Darstellung zu bauen.
 */
export interface ProjectCheckFinding {
    id: string;
    rule: ProjectCheckRule;
    /** Alias von rule — haelt die Alerts-Form kompatibel. */
    type: ProjectCheckRule;
    title: string;
    /** Konkrete Zahlen im Klartext, z.B. "Archiv: 2 Berichte, Leistungsstand: 3". */
    detail: string;
    severity: 'high' | 'medium' | 'low';
    traffic: ProjectCheckTraffic;
    projectId: string;
    projectName: string;
    serviceId?: string;
    lvPosition?: string;
    expected?: number;
    actual?: number;
    /** Bei Rot false — Rot laesst sich nicht wegquittieren. */
    acknowledgeable: boolean;
}

export interface ProjectCheckConfig {
    enableReportCount: boolean;
    enableVorankHistory: boolean;
    enableContractOverrun: boolean;
    enableUnloggedQuantity: boolean;
    enableStartDocs: boolean;
    enableStalePositions: boolean;
    stalePositionMonths: number;
    /** Ab dieser Abweichung wird aus Gelb Rot (Default 2). */
    reportCountRedDelta: number;
    /** Notausstieg fuer Altdaten: false stuft Rot auf Gelb herunter. */
    blockOnRed: boolean;
}

export interface ProjectCheckResult {
    traffic: ProjectCheckTraffic;
    findings: ProjectCheckFinding[];
    /** Immer true — Sichtbarkeit vor Blockade; der Entwurf darf entstehen. */
    canCreateDraft: boolean;
    /** Bei Rot false. */
    canIssue: boolean;
    /** Bei Gelb true: Ausstellen erfordert eine dokumentierte Kenntnisnahme. */
    requiresAcknowledgement: boolean;
    /**
     * Fuer dieses Projekt sind die berichtsbezogenen Pruefungen abgeschaltet.
     *
     * Gehoert ins Ergebnis, damit die Oberflaeche es anzeigen kann: ein
     * schweigender Check sieht sonst genauso aus wie ein bestandener, und
     * niemand koennte unterscheiden, ob alles stimmt oder ob jemand die
     * Pruefung stillgelegt hat.
     */
    reportExempt?: boolean;
    /**
     * Das Projekt rechnet nach Stunden ab und fuehrt keine Stueck-Begehungs-
     * position.
     *
     * Aus demselben Grund im Ergebnis wie reportExempt: der Berichtsabgleich
     * schweigt dann, und ein schweigender Check sieht sonst aus wie ein
     * bestandener. Die Oberflaeche sagt damit, WARUM sie zu den Berichten nichts
     * meldet, statt "Berichte passen zusammen" zu behaupten.
     */
    hoursOnly?: boolean;
}

export interface BillingRunParams {
    projectIds?: string[];
    customerIds?: string[];
    periodFrom?: string;
    periodTo?: string;
    onlyProjectStatus?: ProjectStatus[];
    /** Bagatellgrenze in Cent. */
    minNetCents?: number;
    /** 'customer' = echte Sammelrechnung ueber mehrere Projekte. */
    groupBy: 'customer' | 'project';
    documentType: 'INVOICE' | 'FINAL_INVOICE';
    invoiceDate: string;
    /** Gelbe Projekte aufnehmen? Rote NIE — das ist keine Option. */
    includeYellow: boolean;
    /**
     * Kumulativ abrechnen — dasselbe Modell wie bei der einzelnen Rechnung.
     *
     * Ohne dieses Kennzeichen erzeugte der Lauf fortschreibende Belege: nur die
     * neuen Mengen, ohne Abzug der Vorrechnungen. In einem Betrieb, der
     * kumulativ abrechnet, waeren das Belege einer voellig anderen Bauart als
     * alles uebrige — und ohne jeden Abzug.
     *
     * Kumulativ entsteht IMMER ein Beleg je Projekt: ein kumulativer Beleg
     * schreibt den Gesamtstand EINES Projekts fort und zieht dessen
     * Vorrechnungen ab. Ueber mehrere Projekte hinweg gibt es diesen Stand
     * nicht.
     */
    cumulative?: boolean;
    /**
     * Mengen ohne Protokolleintrag mitnehmen.
     *
     * Wer den Leistungsstand von Hand korrigiert, erzeugt keinen Eintrag im
     * Leistungsprotokoll — solche Mengen gehoeren zu keinem Tag und fallen
     * durch jeden Zeitraumfilter. Ohne diese Option waeren sie ueber den Lauf
     * gar nicht abrechenbar, und das Projekt erschiene dauerhaft als "nichts
     * offen".
     *
     * Wie bei der einzelnen Rechnung eine bewusste Entscheidung, keine
     * Voreinstellung: sie schreibt die Menge dem gewaehlten Zeitraum zu, und
     * niemand ausser dem Nutzer kann wissen, ob das stimmt.
     */
    includeUndated?: boolean;
}

export interface BillingRun {
    id: string;
    status: 'PREVIEW' | 'CREATED' | 'CANCELLED';
    params: BillingRunParams;
    createdInvoiceIds: string[];
    /**
     * Bericht des Laufs: was ausgeschlossen wurde und warum, die Summen, und
     * welche Gruppen gescheitert sind.
     *
     * Der Ausschlussbericht ist der eigentliche Wert eines Laufs — wer nur die
     * erzeugten Entwuerfe sieht, erfaehrt nie, welche Projekte NICHT
     * abgerechnet wurden.
     */
    report?: {
        excluded?: Array<{
            projectId: string;
            projectName: string;
            reason: string;
            detail: string;
        }>;
        totals?: { groups: number; projects: number; netCents: number; grossCents: number };
        failures?: Array<{ customerId: string; customerName?: string; message: string }>;
    };
    createdBy: string;
    createdAt: string;
}
